import React, { useState, useEffect, useRef } from 'react';
const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function Incorporation({ onNavigate }) {
  const INCORPORATION_TABS = [
    { key: 'all', label: 'All' },
    { key: 'Private Limited Company', label: 'Private Limited' },
    { key: 'Public Limited Company', label: 'Public Limited' },
    { key: 'One Person Company', label: 'OPC' },
    { key: 'Section 8 Company', label: 'Section 8' },
    { key: 'LLP Incorporation', label: 'LLP' },
    { key: 'Producer Company', label: 'Producer Company' },
    { key: 'Nidhi Company', label: 'Nidhi Company' },
  ];

  const EMPTY_CASE = {
    client_name: '',
    proposed_name: '',
    incorporation_type: 'Private Limited Company',
    assigned_to: '',
    current_stage: 'Lead Received',
    status: 'Active',
    priority: 'Normal',
    expected_completion: '',
    notes: '',
  };

  const [activeTab, setActiveTab] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [cases, setCases] = useState([]);
  const [form, setForm] = useState(EMPTY_CASE);
  const [users, setUsers] = useState([]);
  const [openActionId, setOpenActionId] = useState(null);
  const [editingCaseId, setEditingCaseId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alertMsg, setAlertMsg] = useState(null);
  const [confirmDeleteItem, setConfirmDeleteItem] = useState(null);
  const token = localStorage.getItem('token');

  const fetchCases = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/api/mca/incorporations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setCases(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('MCA incorporation fetch error:', e);
      setCases([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${BASE}/api/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setUsers(Array.isArray(data) ? data : []);
    } catch (e) {
      setUsers([]);
    }
  };

  useEffect(() => {
    fetchCases();
    fetchUsers();
  }, []);
  const ic = "w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-blue-500 bg-white";

  const STAGE_COLORS = {
    'Lead Received': 'bg-gray-100 text-gray-700',
    'Name Approval': 'bg-blue-100 text-blue-700',
    'Drafting': 'bg-purple-100 text-purple-700',
    'Filed': 'bg-yellow-100 text-yellow-700',
    'COI Received': 'bg-green-100 text-green-700',
    'Post Incorporation': 'bg-emerald-100 text-emerald-700',
  };

  const WORKFLOW_STAGES = [
    'Lead Received',
    'Name Approval',
    'Drafting',
    'Filed',
    'COI Received',
    'Post Incorporation',
  ];

  const filteredCases = activeTab === 'all'
    ? cases
    : cases.filter(c => c.incorporation_type === activeTab);

  const stats = {
    total: cases.length,
    privateLimited: cases.filter(c => c.incorporation_type === 'Private Limited Company').length,
    llp: cases.filter(c => c.incorporation_type === 'LLP Incorporation').length,
    completed: cases.filter(c => c.current_stage === 'COI Received').length,
  };

  const resetCaseForm = () => {
    setForm(EMPTY_CASE);
    setEditingCaseId(null);
    setShowForm(false);
  };

  const updateCaseRecord = async (item, patch) => {
    const payload = { ...item, ...patch };
    const res = await fetch(`${BASE}/api/mca/incorporations/${item.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Could not update incorporation case');

    setCases(prev => prev.map(row => row.id === item.id ? data : row));
    setSelectedCase(prev => prev && prev.id === item.id ? data : prev);
    return data;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const url = editingCaseId
        ? `${BASE}/api/mca/incorporations/${editingCaseId}`
        : `${BASE}/api/mca/incorporations`;

      const res = await fetch(url, {
        method: editingCaseId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(form)
      });

      const data = await res.json();

      if (!res.ok) {
        setAlertMsg(data.message || 'Could not save incorporation case');
        return;
      }

      setCases(prev => editingCaseId
        ? prev.map(row => row.id === editingCaseId ? data : row)
        : [data, ...prev]
      );
      setActiveTab(data.incorporation_type);
      resetCaseForm();
    } catch (e) {
      console.error('MCA incorporation save error:', e);
      setAlertMsg('Server error while saving incorporation case');
    } finally {
      setSaving(false);
    }
  };

  const handleEditCase = (item) => {
    setForm({
      client_name: item.client_name || '',
      proposed_name: item.proposed_name || '',
      incorporation_type: item.incorporation_type || activeTab,
      assigned_to: item.assigned_to || '',
      current_stage: item.current_stage || 'Lead Received',
      status: item.status || 'Active',
      priority: item.priority || 'Normal',
      expected_completion: item.expected_completion ? String(item.expected_completion).slice(0, 10) : '',
      notes: item.notes || '',
    });
    setEditingCaseId(item.id);
    setOpenActionId(null);
    setShowForm(true);
  };

  const handleDeleteCase = (item) => {
    setOpenActionId(null);
    setConfirmDeleteItem(item);
  };

  const confirmDelete = async () => {
    const item = confirmDeleteItem;
    if (!item) return;
    setConfirmDeleteItem(null);
    try {
      const res = await fetch(`${BASE}/api/mca/incorporations/${item.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Could not delete case');
      setCases(prev => prev.filter(row => row.id !== item.id));
    } catch (e) {
      setAlertMsg(e.message || 'Server error while deleting case');
    }
  };

  const handleAssignUser = async (item, assignedTo) => {
    try {
      await updateCaseRecord(item, { assigned_to: assignedTo });
      setOpenActionId(null);
    } catch (e) {
      setAlertMsg(e.message || 'Could not assign user');
    }
  };

  if (selectedCase) {
    return (
      <IncorporationCaseDetail
        item={selectedCase}
        onBack={() => setSelectedCase(null)}
        workflowStages={WORKFLOW_STAGES}
        stageColors={STAGE_COLORS}
        onStageChange={(id, stage) => {
          setCases(prev => prev.map(c => c.id === id ? { ...c, current_stage: stage } : c));
        }}
        onNavigate={onNavigate}
      />
    );
  }

  return (
    <div className="space-y-5">
      <style>{`
        .dark [class~="bg-white"] { background-color: #1e293b !important; }
        .dark [class~="bg-gray-50"] { background-color: #0f172a !important; }
        .dark [class~="bg-gray-100"] { background-color: #334155 !important; }
        .dark [class~="bg-blue-50"] { background-color: rgba(30,58,138,0.3) !important; }
        .dark [class~="bg-blue-100"] { background-color: rgba(30,58,138,0.35) !important; }
        .dark [class~="bg-red-50"] { background-color: rgba(127,29,29,0.3) !important; }
        .dark [class~="bg-red-100"] { background-color: rgba(127,29,29,0.35) !important; }
        .dark [class~="bg-green-50"] { background-color: rgba(6,78,59,0.3) !important; }
        .dark [class~="bg-green-100"] { background-color: rgba(6,78,59,0.35) !important; }
        .dark [class~="bg-green-600"] { background-color: #16a34a !important; }
        .dark [class~="bg-orange-50"] { background-color: rgba(124,45,18,0.3) !important; }
        .dark [class~="bg-orange-100"] { background-color: rgba(124,45,18,0.35) !important; }
        .dark [class~="bg-purple-100"] { background-color: rgba(88,28,135,0.35) !important; }
        .dark [class~="bg-indigo-100"] { background-color: rgba(49,46,129,0.35) !important; }
        .dark [class~="bg-yellow-100"] { background-color: rgba(113,63,18,0.35) !important; }

        .dark [class~="hover:bg-gray-50"]:hover { background-color: #334155 !important; }
        .dark [class~="hover:bg-gray-100"]:hover { background-color: #334155 !important; }
        .dark [class~="hover:bg-blue-50"]:hover { background-color: #334155 !important; }
        .dark [class~="hover:bg-white"]:hover { background-color: #334155 !important; }

        .dark [class~="text-gray-300"] { color: #475569 !important; }
        .dark [class~="text-gray-400"] { color: #64748b !important; }
        .dark [class~="text-gray-500"] { color: #94a3b8 !important; }
        .dark [class~="text-gray-600"] { color: #cbd5e1 !important; }
        .dark [class~="text-gray-700"] { color: #cbd5e1 !important; }
        .dark [class~="text-gray-800"] { color: #f1f5f9 !important; }
        .dark [class*="text-[#0f1f45]"] { color: #f1f5f9 !important; }
        .dark [class*="text-[#00206E]"] { color: #93c5fd !important; }
        .dark [class~="text-blue-100"] { color: #bfdbfe !important; }
        .dark [class~="text-blue-600"] { color: #60a5fa !important; }
        .dark [class~="text-blue-700"] { color: #60a5fa !important; }
        .dark [class~="text-red-600"] { color: #f87171 !important; }
        .dark [class~="text-red-700"] { color: #f87171 !important; }
        .dark [class~="text-green-600"] { color: #34d399 !important; }
        .dark [class~="text-green-700"] { color: #34d399 !important; }
        .dark [class~="text-emerald-600"] { color: #34d399 !important; }
        .dark [class~="text-orange-500"] { color: #fb923c !important; }
        .dark [class~="text-orange-700"] { color: #fb923c !important; }
        .dark [class~="text-purple-600"] { color: #c084fc !important; }
        .dark [class~="text-purple-700"] { color: #c084fc !important; }
        .dark [class~="text-indigo-700"] { color: #a5b4fc !important; }
        .dark [class~="text-yellow-600"] { color: #facc15 !important; }
        .dark [class~="text-yellow-700"] { color: #facc15 !important; }
        .dark [class~="hover:text-gray-600"]:hover { color: #e2e8f0 !important; }

        .dark [class~="border-gray-50"] { border-color: #334155 !important; }
        .dark [class~="border-gray-100"] { border-color: #334155 !important; }
        .dark [class~="border-gray-200"] { border-color: #475569 !important; }
        .dark [class*="border-[#0f1f45]"] { border-color: #93c5fd !important; }
        .dark [class~="border-blue-200"] { border-color: rgba(96,165,250,0.4) !important; }
        .dark [class~="border-blue-500"] { border-color: #60a5fa !important; }
        .dark [class~="border-green-200"] { border-color: rgba(52,211,153,0.4) !important; }
        .dark [class~="border-orange-100"] { border-color: rgba(251,146,60,0.4) !important; }
        .dark [class~="hover:border-[#0f1f45]"]:hover { border-color: #93c5fd !important; }

        .dark input, .dark select, .dark textarea {
          background-color: #1e293b !important;
          color: #f1f5f9 !important;
          border-color: #475569 !important;
        }
        .dark input::placeholder, .dark textarea::placeholder { color: #64748b !important; }
      `}</style>

      <div className="relative overflow-hidden rounded-2xl border border-gray-100 shadow-sm bg-white">
        <div className="absolute inset-x-0 top-0 h-1" style={{ background: 'linear-gradient(90deg,#0f1f45,#00206E 55%,#FF8400)' }} />
        <div className="p-5 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0" style={{ background: 'linear-gradient(135deg,#0f1f45,#00206E)' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.9"><path d="M3 21h18M6 21V7l6-4 6 4v14M10 9h.01M14 9h.01M10 13h.01M14 13h.01M10 17h.01M14 17h.01"/></svg>
            </div>
            <div>
              <h3 className="text-xl font-bold text-[#0f1f45]">Incorporation</h3>
              <p className="text-sm text-gray-400 mt-1">
                Track company and LLP incorporation cases business-type wise.
              </p>
            </div>
          </div>

          {activeTab === 'all' ? (
            <div className="px-4 py-2.5 rounded-xl bg-gray-50 border border-gray-200 text-gray-500 text-sm flex-shrink-0">
              Select a business-type tab to create a case
            </div>
          ) : (
            <button
              onClick={() => {
                setForm({ ...EMPTY_CASE, incorporation_type: activeTab });
                setShowForm(true);
              }}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-semibold bg-[#0f1f45] hover:bg-[#162d5e] transition shadow-sm flex-shrink-0"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              New {INCORPORATION_TABS.find(t => t.key === activeTab)?.label}
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Cases', value: stats.total, tone: 'blue',
            icon: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></> },
          { label: 'Private Limited', value: stats.privateLimited, tone: 'purple',
            icon: <><rect x="4" y="2" width="16" height="20" rx="2"/><path d="M9 22v-4h6v4M9 6h.01M15 6h.01M9 10h.01M15 10h.01M9 14h.01M15 14h.01"/></> },
          { label: 'LLP', value: stats.llp, tone: 'amber',
            icon: <><path d="M17 21v-2a4 4 0 0 0-3-3.87M9 21v-2a4 4 0 0 1 3-3.87M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z"/></> },
          { label: 'COI Received', value: stats.completed, tone: 'emerald',
            icon: <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></> },
        ].map(card => {
          const tones = {
            blue:    { bg: 'bg-blue-50 border-blue-200', val: 'text-[#00206E]', ic: '#2563eb' },
            purple:  { bg: 'bg-purple-100 border-gray-100', val: 'text-purple-700', ic: '#7c3aed' },
            amber:   { bg: 'bg-orange-50 border-orange-100', val: 'text-orange-700', ic: '#d97706' },
            emerald: { bg: 'bg-green-50 border-green-200', val: 'text-green-600', ic: '#059669' },
          }[card.tone];
          return (
            <div key={card.label} className={`rounded-2xl border shadow-sm p-4 ${tones.bg}`}>
              <div className="flex items-start justify-between">
                <p className="text-xs font-medium text-gray-500">{card.label}</p>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={tones.ic} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">{card.icon}</svg>
              </div>
              <p className={`text-2xl font-bold mt-1.5 ${tones.val}`}>{card.value}</p>
            </div>
          );
        })}
      </div>

      <div className="relative bg-white rounded-2xl border border-gray-100 shadow-sm overflow-visible">
        <div className="absolute inset-x-0 top-0 h-1 rounded-t-2xl" style={{ background: 'linear-gradient(90deg,#0f1f45,#00206E 55%,#FF8400)' }} />
        <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
          <div className="flex flex-wrap gap-2">
            {INCORPORATION_TABS.map(tab => {
              const active = activeTab === tab.key;
              const count = tab.key === 'all' ? cases.length : cases.filter(c => c.incorporation_type === tab.key).length;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-semibold border transition ${
                    active
                      ? 'bg-[#0f1f45] text-white border-[#0f1f45] shadow-sm'
                      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {tab.label}
                  {active ? (
                    <span className="min-w-[18px] text-center text-[11px] font-bold px-1.5 py-0.5 rounded-full text-white" style={{ background: 'rgba(255,255,255,0.22)' }}>
                      {count}
                    </span>
                  ) : (
                    <span className="min-w-[18px] text-center text-[11px] font-bold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {showForm && (
          <form onSubmit={handleSave} className="border-b border-gray-100 bg-gray-50 p-5">
            <div className="grid grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Client / Promoter *</label>
                <input
                  required
                  value={form.client_name}
                  onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))}
                  placeholder="Existing individual client"
                  className={ic}
                />
              </div>

                <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Incorporation Type</label>
                <input
                    value={INCORPORATION_TABS.find(t => t.key === form.incorporation_type)?.label || form.incorporation_type}
                    readOnly
                    className={`${ic} bg-gray-100 text-gray-600 cursor-not-allowed`}
                />
                </div>

              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Proposed Name *</label>
                <input
                  required
                  value={form.proposed_name}
                  onChange={e => setForm(f => ({ ...f, proposed_name: e.target.value }))}
                  placeholder="Proposed company / LLP name"
                  className={ic}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Assigned To</label>
                <select
                  value={form.assigned_to}
                  onChange={e => setForm(f => ({ ...f, assigned_to: e.target.value }))}
                  className={ic}
                >
                  <option value="">Select team member</option>
                  {users.map(user => (
                    <option key={user.id} value={user.full_name || user.email}>
                      {user.full_name || user.email}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Current Stage</label>
                <select
                  value={form.current_stage}
                  onChange={e => setForm(f => ({ ...f, current_stage: e.target.value }))}
                  className={ic}
                >
                  {WORKFLOW_STAGES.map(stage => (
                    <option key={stage} value={stage}>{stage}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Priority</label>
                <select
                  value={form.priority}
                  onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
                  className={ic}
                >
                  <option>Low</option>
                  <option>Normal</option>
                  <option>High</option>
                  <option>Urgent</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Expected Completion</label>
                <input
                  type="date"
                  value={form.expected_completion}
                  onChange={e => setForm(f => ({ ...f, expected_completion: e.target.value }))}
                  className={ic}
                />
              </div>

              <div className="col-span-4">
                <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                <input
                  value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Internal notes"
                  className={ic}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-4">
              <button
                type="button"
                onClick={resetCaseForm}
                className="px-5 py-2.5 rounded-lg border border-gray-200 text-gray-600 text-sm bg-white"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2.5 rounded-lg text-white text-sm font-medium bg-[#0f1f45] disabled:opacity-50"
              >
                {saving ? 'Saving...' : editingCaseId ? 'Update Case' : 'Save Case'}
              </button>
            </div>
          </form>
        )}

        {loading ? (
          <div className="py-16 text-center text-gray-400 text-sm">
            Loading incorporation cases...
          </div>
        ) : filteredCases.length === 0 ? (
          <div className="py-16 text-center text-gray-400 text-sm">
            No incorporation cases found under this type.
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr style={{ background: 'linear-gradient(90deg,#0f1f45,#13294f)' }}>
                {['Case', 'Type', 'Client / Promoter', 'Stage', 'Assigned To', 'Expected', 'Priority', 'Actions'].map(h => (
                  <th key={h} className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-[0.06em]" style={{ color: 'rgba(219,234,254,0.92)' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {filteredCases.map(item => (
                <tr key={item.id} className="border-b border-gray-50 hover:bg-blue-50 transition-colors">
                  <td className="px-5 py-3">
                    <button
                      onClick={async () => {
                        try {
                          const res = await fetch(`${BASE}/api/mca/incorporations/${item.id}`, {
                            headers: { Authorization: `Bearer ${token}` },
                          });
                          const fresh = res.ok ? await res.json() : item;
                          setSelectedCase(fresh);
                        } catch (e) {
                          console.error('Failed to fetch fresh case data, opening with cached copy:', e);
                          setSelectedCase(item);
                        }
                      }}
                      className="text-sm font-semibold text-[#00206E] hover:underline text-left"
                    >
                      {item.proposed_name}
                    </button>
                    <p className="text-xs text-gray-400 mt-0.5">Created {new Date(item.created_at || item.created_on).toLocaleDateString('en-IN')}</p>
                  </td>

                  <td className="px-5 py-3 text-sm text-gray-600">{item.incorporation_type}</td>
                  <td className="px-5 py-3 text-sm text-gray-600">{item.client_name}</td>

                  <td className="px-5 py-3">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${STAGE_COLORS[item.current_stage] || 'bg-gray-100 text-gray-700'}`}>
                      {item.current_stage}
                    </span>
                  </td>

                  <td className="px-5 py-3 text-sm text-gray-600">{item.assigned_to || '-'}</td>

                  <td className="px-5 py-3 text-sm text-gray-600">
                    {item.expected_completion ? new Date(item.expected_completion).toLocaleDateString('en-IN') : '-'}
                  </td>

                  <td className="px-5 py-3">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                      item.priority === 'Urgent' ? 'bg-red-100 text-red-700' :
                      item.priority === 'High' ? 'bg-orange-100 text-orange-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {item.priority}
                    </span>
                  </td>

                  <td className="px-5 py-3 relative">
                    <button
                      type="button"
                      onClick={() => setOpenActionId(openActionId === item.id ? null : item.id)}
                      className="w-8 h-8 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 hover:border-[#0f1f45] flex items-center justify-center text-lg font-bold"
                      aria-label="Case actions"
                    >
                      ⋮
                    </button>

                    {openActionId === item.id && (
                      <div
                        onMouseLeave={() => setOpenActionId(null)}
                        className="absolute right-5 top-11 z-30 w-56 bg-white rounded-xl border border-gray-100 shadow-lg py-2"
                      >
                        <button
                          type="button"
                          onClick={() => handleEditCase(item)}
                          className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          Edit
                        </button>

                        <div className="px-4 py-2">
                          <label className="block text-xs font-medium text-gray-400 mb-1">Assign User</label>
                          <select
                            value={item.assigned_to || ''}
                            onChange={e => handleAssignUser(item, e.target.value)}
                            className="w-full border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:border-blue-500"
                          >
                            <option value="">Unassigned</option>
                            {users.map(user => (
                              <option key={user.id} value={user.full_name || user.email}>
                                {user.full_name || user.email}
                              </option>
                            ))}
                          </select>
                        </div>

                        <button
                          type="button"
                          onClick={() => handleDeleteCase(item)}
                          className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {alertMsg && (
        <AlertModal message={alertMsg} onClose={() => setAlertMsg(null)} />
      )}

      {confirmDeleteItem && (
        <ConfirmModal
          title="Delete incorporation case"
          message={`Delete incorporation case "${confirmDeleteItem.proposed_name}"? This cannot be undone.`}
          confirmLabel="Delete"
          onConfirm={confirmDelete}
          onCancel={() => setConfirmDeleteItem(null)}
        />
      )}
    </div>
  );
}

function IncorporationCaseDetail({ item, onBack, workflowStages, stageColors, onStageChange, onNavigate }) {
  const [activeTab, setActiveTab] = useState('overview');
    const [nameApproval, setNameApproval] = useState(() => ({
    proposed_name_1: item.proposed_name || '',
    proposed_name_2: '',
    approved_name: '',
    main_object: '',
    nic_code: '',
    srn_filed: false,
    srn: '',
    application_date: '',
    approval_date: '',
    extensions_taken: 0,
    status: 'Drafting',
    remarks: '',
    ...(item.name_approval_data || {}),
    }));
    const [nameApprovalRequired, setNameApprovalRequired] = useState(true);
    const [peopleList, setPeopleList] = useState([]);
    const [casePeople, setCasePeople] = useState(() => item.case_people || []);
    const [capitalStructure, setCapitalStructure] = useState(() => ({
        has_share_capital: true,
        aoa_entrenched: false,
        entrenchment_articles: [],
        total_authorized_capital: '',
        equity_classes: [
          { class_name: 'Equity', authorized_shares: '', authorized_nominal_value: '10', subscribed_shares: '', subscribed_nominal_value: '10' },
        ],
        preference_classes: [],
        max_members: '',
        max_members_excl_employees: '',
        number_of_members: '',
        number_of_members_excl_employees: '',
        main_object: '',
        ...(item.capital_structure || {}),
    }));
    const [registeredOfficeData, setRegisteredOfficeData] = useState(() => ({
        correspondence_address: {
          address: '', pincode: '', district: '', state: '', mobile: '', company_email: '',
        },
        same_as_registered_office: true,
        longitude: '', latitude: '',
        roc_office: '',
        noc_rent_agreement_file: null, noc_rent_agreement_name: '',
        utility_bill_file: null, utility_bill_name: '', utility_bill_date: '',
        ...(item.registered_office_data || {}),
    }));
    const [opcNomineeData, setOpcNomineeData] = useState(() => ({
        subscriber_name: '',
        nominee_first_name: '', nominee_middle_name: '', nominee_surname: '',
        father_first_name: '', father_middle_name: '', father_surname: '',
        gender: '', date_of_birth: '', nationality: 'India', pan: '',
        place_of_birth_district: '', place_of_birth_state: '',
        occupation_type: '', area_of_occupation: '', education_qualification: '',
        mobile: '', email: '',
        address: '', pincode: '', district: '', state: '',
        identity_proof_type: '', identity_proof_number: '', identity_proof_file: null, identity_proof_file_name: '',
        residential_proof_type: '', residential_proof_number: '', residential_proof_file: null, residential_proof_file_name: '',
        declaration_accepted: false,
        ...(item.opc_nominee_data || {}),
    }));
    const [stampDutyPanTanData, setStampDutyPanTanData] = useState(() => ({
        stamp_duty_state: '',
        pay_electronically_mca21: 'yes',
        duty_form_amount: '', duty_moa_amount: '', duty_aoa_amount: '',
        already_paid: {
          mode_of_payment: '', vendor_name: '', serial_number: '',
          vendor_registration_number: '', purchase_date: '', purchase_place: '', total_amount: '',
        },
        pan_area_code: '', pan_ao_type: '', pan_range_code: '', pan_ao_number: '',
        tan_area_code: '', tan_ao_type: '', tan_range_code: '', tan_ao_number: '',
        source_of_income: [],
        ...(item.stamp_duty_pan_tan_data || {}),
    }));
    const [peopleForm, setPeopleForm] = useState({
        person_id: '',
        role: '',
        shareholding_percent: '',
        shares_or_contribution: '',
        preference_shares: '',
        contribution_type: 'Cash',
        contribution_type_other: '',
        remarks: '',
        });
    const [documents, setDocuments] = useState(() => item.documents_checklist || []);
    const [drafts, setDrafts] = useState(() => item.drafts_data || []);
    const [selectedDraft, setSelectedDraft] = useState(null);
    const [mcaForms, setMcaForms] = useState(() => item.forms_data || []);
    const [filingData, setFilingData] = useState(() => ({
        master_srn: '',
        filing_date: '',
        roc_fees: '',
        additional_fees: '',
        challan_status: 'Pending',
        challan_file: '',
        filing_status: 'Not Filed',
        resubmission_remarks: '',
        approval_date: '',
        remarks: '',
        ...(item.filing_data || {}),
        }));
    const [postIncorpData, setPostIncorpData] = useState(() => ({
        coi_received: false,
        cin_llpin: '',
        incorporation_date: '',
        first_board_meeting_date: '',
        coi_file: '',
        pan: '',
        tan: '',
        gstin: '',
        bank_account_opened: false,
        company_client_created: false,
        company_client_id: null,
        moved_to_company_master: false,
        company_master_id: null,
        director_id_map: {},
        notes: '',
        checklist: [],
        ...(item.post_incorp_data || {}),
        }));

    // ── Persistence — save all tab data to the backend ────────────────
    const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | saved | error
    const [saveErrorDetail, setSaveErrorDetail] = useState('');
    const hasMountedRef = useRef(false);

    // ── Stage tracking — auto-advances as each stage's real work completes ──
    const [currentStage, setCurrentStage] = useState(item.current_stage || 'Lead Received');

    useEffect(() => {
      const idxOf = (label) => workflowStages.indexOf(label);

      // Rule 1: Name Approval → Drafting
      // Condition: SRN filed and MCA approval date entered
      const nameIsApproved = !!(nameApproval.srn_filed && nameApproval.approval_date);

      // Rule 2: Drafting → Filed
      // Condition: incorporation form marked filed in the merged Filing &
      // Post Incorporation tab (SRN + filing status recorded there)
      const formIsFiled = filingData.filing_status === 'Filed' || filingData.filing_status === 'Approved';

      // Rule 3: Filed → COI Received
      // Condition: COI Received checkbox ticked in Post Incorporation
      const coiReceived = !!postIncorpData.coi_received;

      // Rule 4: COI Received → Post Incorporation
      // Condition: EVERY item in both the Board Governance box and the
      // MCA/ROC Filings box is marked Completed or Not Applicable — this
      // includes custom items added manually, not just template ones.
      // The whole point is to catch delays, so this only completes when
      // there's genuinely nothing left outstanding in either box.
      const checklist = postIncorpData.checklist || [];
      const governanceItems = checklist.filter(c => c.group === 'governance');
      const filingItems = checklist.filter(c => c.group === 'filing');
      const isDone = (row) => row.status === 'Completed' || row.status === 'Not Applicable';
      const allGovernanceDone = governanceItems.length > 0 && governanceItems.every(isDone);
      const allFilingDone = filingItems.length > 0 && filingItems.every(isDone);
      const postIncorpComplete = allGovernanceDone && allFilingDone;

      // Stage is recomputed live from the underlying data every time it
      // changes — ticking a box advances it, and un-ticking the same box
      // that drove the advance reverses it back down, cascading through
      // whichever earlier rules still hold. 'Lead Received' and 'Name
      // Approval' have no auto-condition of their own (they're set manually
      // when the case is created/edited), so they're the floor this effect
      // will neither drop below nor auto-advance into on its own.
      let target = null;
      if (postIncorpComplete) target = 'Post Incorporation';
      else if (coiReceived) target = 'COI Received';
      else if (formIsFiled) target = 'Filed';
      else if (nameIsApproved) target = 'Drafting';

      if (target && target !== currentStage) {
        setCurrentStage(target);
      } else if (!target && idxOf(currentStage) > idxOf('Name Approval')) {
        setCurrentStage('Name Approval');
      }
    }, [
      nameApproval.srn_filed, nameApproval.approval_date,
      filingData.filing_status, postIncorpData.coi_received, postIncorpData.checklist,
    ]);

    const saveCaseDetails = async () => {
      if (!item?.id) {
        console.error('Save aborted — item.id is missing:', item);
        setSaveErrorDetail('Case ID is missing — cannot save.');
        setSaveStatus('error');
        return;
      }

      setSaveStatus('saving');
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${BASE}/api/mca/incorporations/${item.id}/details`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            name_approval_data: nameApproval,
            case_people: casePeople,
            documents_checklist: documents,
            capital_structure: capitalStructure,
            drafts_data: drafts,
            forms_data: mcaForms,
            filing_data: filingData,
            post_incorp_data: postIncorpData,
            current_stage: currentStage,
            registered_office_data: registeredOfficeData,
            opc_nominee_data: opcNomineeData,
            stamp_duty_pan_tan_data: stampDutyPanTanData,
          }),
        });

        if (!res.ok) {
          let detail = `HTTP ${res.status}`;
          try {
            const errBody = await res.json();
            detail = errBody.message || errBody.error || detail;
          } catch (_) { /* response wasn't JSON */ }
          console.error('Save case details failed:', detail);
          setSaveErrorDetail(detail);
          setSaveStatus('error');
          return;
        }

        setSaveErrorDetail('');
        setSaveStatus('saved');
        if (onStageChange) onStageChange(item.id, currentStage);
        setTimeout(() => setSaveStatus('idle'), 2000);
      } catch (e) {
        console.error('Save case details network error:', e);
        setSaveErrorDetail(e.message || 'Network error');
        setSaveStatus('error');
      }
    };

    // Debounced auto-save — fires 1.5s after the last change on any tab.
    // Skips the very first run on mount (that's just data loading in, not a real edit).
    useEffect(() => {
      if (!hasMountedRef.current) {
        hasMountedRef.current = true;
        return;
      }
      const timer = setTimeout(() => {
        saveCaseDetails();
      }, 1500);
      return () => clearTimeout(timer);
    }, [nameApproval, casePeople, documents, capitalStructure, drafts, mcaForms, filingData, postIncorpData, currentStage, registeredOfficeData, opcNomineeData, stampDutyPanTanData]);

    const handleBack = async () => {
      await saveCaseDetails();
      onBack();
    };

    useEffect(() => {
    const token = localStorage.getItem('token');

  fetch(`${BASE}/api/persons`, {
    headers: { 'Authorization': `Bearer ${token}` }
  })
    .then(r => r.json())
    .then(d => setPeopleList(Array.isArray(d) ? d : []))
    .catch(() => setPeopleList([]));
    }, []);
  const isOPC = item.incorporation_type === 'One Person Company';
  const isLLPCase = item.incorporation_type === 'LLP Incorporation';

  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'name', label: 'Name Approval' },
    { key: 'structure', label: isLLPCase ? 'Capital Contribution' : 'Structure & Capital' },
    { key: 'office', label: 'Registered Office' },
    ...(isOPC ? [{ key: 'opc', label: 'OPC Nominee' }] : []),
    { key: 'people', label: 'Promoters / Partners' },
    { key: 'stamp', label: 'PAN & TAN' },
    { key: 'drafting', label: 'Drafting' },
    { key: 'post', label: 'Filing & Post Incorporation' },
  ];

  return (
    <div className="space-y-5">
      <style>{`
        .dark [class~="bg-white"] { background-color: #1e293b !important; }
        .dark [class~="bg-gray-50"] { background-color: #0f172a !important; }
        .dark [class~="bg-gray-100"] { background-color: #334155 !important; }
        .dark [class~="bg-blue-50"] { background-color: rgba(30,58,138,0.3) !important; }
        .dark [class~="bg-blue-100"] { background-color: rgba(30,58,138,0.35) !important; }
        .dark [class~="bg-red-50"] { background-color: rgba(127,29,29,0.3) !important; }
        .dark [class~="bg-red-100"] { background-color: rgba(127,29,29,0.35) !important; }
        .dark [class~="bg-green-50"] { background-color: rgba(6,78,59,0.3) !important; }
        .dark [class~="bg-green-100"] { background-color: rgba(6,78,59,0.35) !important; }
        .dark [class~="bg-green-600"] { background-color: #16a34a !important; }
        .dark [class~="bg-orange-50"] { background-color: rgba(124,45,18,0.3) !important; }
        .dark [class~="bg-orange-100"] { background-color: rgba(124,45,18,0.35) !important; }
        .dark [class~="bg-purple-100"] { background-color: rgba(88,28,135,0.35) !important; }
        .dark [class~="bg-indigo-100"] { background-color: rgba(49,46,129,0.35) !important; }
        .dark [class~="bg-yellow-100"] { background-color: rgba(113,63,18,0.35) !important; }

        .dark [class~="hover:bg-gray-50"]:hover { background-color: #334155 !important; }
        .dark [class~="hover:bg-gray-100"]:hover { background-color: #334155 !important; }
        .dark [class~="hover:bg-blue-50"]:hover { background-color: #334155 !important; }
        .dark [class~="hover:bg-white"]:hover { background-color: #334155 !important; }

        .dark [class~="text-gray-300"] { color: #475569 !important; }
        .dark [class~="text-gray-400"] { color: #64748b !important; }
        .dark [class~="text-gray-500"] { color: #94a3b8 !important; }
        .dark [class~="text-gray-600"] { color: #cbd5e1 !important; }
        .dark [class~="text-gray-700"] { color: #cbd5e1 !important; }
        .dark [class~="text-gray-800"] { color: #f1f5f9 !important; }
        .dark [class*="text-[#0f1f45]"] { color: #f1f5f9 !important; }
        .dark [class*="text-[#00206E]"] { color: #93c5fd !important; }
        .dark [class~="text-blue-100"] { color: #bfdbfe !important; }
        .dark [class~="text-blue-600"] { color: #60a5fa !important; }
        .dark [class~="text-blue-700"] { color: #60a5fa !important; }
        .dark [class~="text-red-600"] { color: #f87171 !important; }
        .dark [class~="text-red-700"] { color: #f87171 !important; }
        .dark [class~="text-green-600"] { color: #34d399 !important; }
        .dark [class~="text-green-700"] { color: #34d399 !important; }
        .dark [class~="text-emerald-600"] { color: #34d399 !important; }
        .dark [class~="text-orange-500"] { color: #fb923c !important; }
        .dark [class~="text-orange-700"] { color: #fb923c !important; }
        .dark [class~="text-purple-600"] { color: #c084fc !important; }
        .dark [class~="text-purple-700"] { color: #c084fc !important; }
        .dark [class~="text-indigo-700"] { color: #a5b4fc !important; }
        .dark [class~="text-yellow-600"] { color: #facc15 !important; }
        .dark [class~="text-yellow-700"] { color: #facc15 !important; }
        .dark [class~="hover:text-gray-600"]:hover { color: #e2e8f0 !important; }

        .dark [class~="border-gray-50"] { border-color: #334155 !important; }
        .dark [class~="border-gray-100"] { border-color: #334155 !important; }
        .dark [class~="border-gray-200"] { border-color: #475569 !important; }
        .dark [class*="border-[#0f1f45]"] { border-color: #93c5fd !important; }
        .dark [class~="border-blue-200"] { border-color: rgba(96,165,250,0.4) !important; }
        .dark [class~="border-blue-500"] { border-color: #60a5fa !important; }
        .dark [class~="border-green-200"] { border-color: rgba(52,211,153,0.4) !important; }
        .dark [class~="border-orange-100"] { border-color: rgba(251,146,60,0.4) !important; }
        .dark [class~="hover:border-[#0f1f45]"]:hover { border-color: #93c5fd !important; }

        .dark input, .dark select, .dark textarea {
          background-color: #1e293b !important;
          color: #f1f5f9 !important;
          border-color: #475569 !important;
        }
        .dark input::placeholder, .dark textarea::placeholder { color: #64748b !important; }
      `}</style>

      {/* ── Save status toast — fixed, doesn't disturb any other alert/UI ── */}
      {saveStatus !== 'idle' && (
        <div className="fixed bottom-6 right-6 z-[9999]" style={{ animation: 'slideUp 0.2s ease-out' }}>
          <style>{`@keyframes slideUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }`}</style>

          {saveStatus === 'saving' && (
            <div className="flex items-center gap-3 bg-white border border-gray-200 shadow-lg rounded-xl px-4 py-3 min-w-[220px]">
              <svg className="animate-spin" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0f1f45" strokeWidth="2.5">
                <circle cx="12" cy="12" r="9" strokeOpacity="0.25" />
                <path d="M21 12a9 9 0 0 0-9-9" strokeLinecap="round" />
              </svg>
              <span className="text-sm font-medium text-gray-700">Saving changes…</span>
            </div>
          )}

          {saveStatus === 'saved' && (
            <div className="flex items-center gap-3 bg-white border border-green-200 shadow-lg rounded-xl px-4 py-3 min-w-[220px]">
              <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="3">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
              <span className="text-sm font-medium text-green-700">All changes saved</span>
            </div>
          )}

          {saveStatus === 'error' && (
            <div className="bg-white border border-red-200 shadow-lg rounded-xl px-4 py-3 max-w-[340px]">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.5">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-red-700">Save failed</p>
                  {saveErrorDetail && (
                    <p className="text-xs text-gray-500 mt-0.5 break-words">{saveErrorDetail}</p>
                  )}
                  <div className="flex items-center gap-3 mt-2">
                    <button onClick={saveCaseDetails} className="text-xs font-semibold text-red-600 hover:underline">
                      Retry now
                    </button>
                    <button onClick={() => setSaveStatus('idle')} className="text-xs text-gray-400 hover:text-gray-600">
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-200 bg-gray-50 text-gray-600 text-xs font-semibold hover:bg-[#0f1f45] hover:text-white hover:border-[#0f1f45] transition-colors mb-3"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
            <span>Back to Incorporation</span>
          </button>
          <h3 className="text-xl font-bold text-[#0f1f45]">{item.proposed_name}</h3>
          <p className="text-sm text-gray-400 mt-1">
            {item.incorporation_type} • {item.client_name}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <span className={`px-3 py-1.5 rounded-full text-xs font-medium ${stageColors[currentStage] || 'bg-gray-100 text-gray-700'}`}>
            {currentStage}
          </span>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
        <div className="flex flex-wrap gap-2">
{tabs.map(tab => {
  const disabled = tab.key === 'name' && !nameApprovalRequired;

  return (
    <button
      key={tab.key}
      disabled={disabled}
      onClick={() => !disabled && setActiveTab(tab.key)}
      className={`px-4 py-2 rounded-lg text-sm font-medium border ${
        disabled
          ? 'bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed'
          : activeTab === tab.key
            ? 'bg-[#0f1f45] text-white border-[#0f1f45]'
            : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
      }`}
    >
      {tab.label}{disabled ? ' (Skipped)' : ''}
    </button>
  );
})}
        </div>
      </div>

      {activeTab === 'overview' && (
        <div className="grid grid-cols-3 gap-5">
        {(() => {
          const isLLPCase = item.incorporation_type === 'LLP Incorporation';
          const nameOk = !!(nameApproval.approved_name || (nameApproval.srn_filed && nameApproval.approval_date));
          const equityClasses = capitalStructure?.equity_classes || [];
          const totalAuthCap = equityClasses.reduce((s, c) => s + (Number(c.authorized_shares) || 0) * (Number(c.authorized_nominal_value) || 0), 0);
          const totalSubShares = equityClasses.reduce((s, c) => s + (Number(c.subscribed_shares) || 0), 0);
          const totalAllottedShares = casePeople.reduce((s, p) => s + (Number(p.shares_or_contribution) || 0), 0);
          const totalContributionDeclared = Number(capitalStructure?.total_authorized_capital) || 0;
          const capitalOk = isLLPCase
            ? totalContributionDeclared > 0 && totalAllottedShares >= totalContributionDeclared
            : totalAuthCap > 0 && totalSubShares > 0 && totalAllottedShares >= totalSubShares;

          const addr = registeredOfficeData?.correspondence_address;
          const addrOk = !!(addr?.address && addr?.pincode && addr?.state);
          const needsOfficeDocs = registeredOfficeData?.same_as_registered_office !== false;
          const officeDocsOk = !needsOfficeDocs || !!(registeredOfficeData?.noc_rent_agreement_file && registeredOfficeData?.utility_bill_file);

          const peopleDocsComplete = casePeople.filter(p => p.entity_type === 'corporate' || (p.pan_number && p.aadhaar_number && p.photo && p.signature)).length;
          const peopleTotal = casePeople.length;

          const draftsNeeded = isLLPCase ? ['LLP Agreement'] : ['MOA', 'AOA'];
          const draftsDone = draftsNeeded.filter(name => drafts.some(d => d.key === name || d.name?.includes(name))).length;

          const checklist = postIncorpData.checklist || [];
          const checklistDone = checklist.filter(c => c.status === 'Completed' || c.status === 'Not Applicable').length;
          const filingOk = filingData.filing_status === 'Filed' || filingData.filing_status === 'Approved';

          const rows = [
            { label: 'Name Approval', ok: nameOk, detail: nameOk ? 'Approved' : 'Pending', tab: 'name' },
            { label: isLLPCase ? 'Capital Contribution' : 'Structure & Capital', ok: capitalOk, detail: capitalOk ? 'Capital set & allotted' : 'Incomplete', tab: 'structure' },
            { label: 'Registered Office', ok: addrOk && officeDocsOk, detail: !addrOk ? 'Address incomplete' : !officeDocsOk ? 'NOC / Utility bill pending' : 'Complete', tab: 'office' },
            { label: 'Promoters & Partners', ok: peopleTotal > 0 && peopleDocsComplete === peopleTotal, detail: `${peopleDocsComplete} of ${peopleTotal} fully documented`, tab: 'people' },
            { label: 'Drafting', ok: draftsDone === draftsNeeded.length, detail: `${draftsDone} of ${draftsNeeded.length} generated`, tab: 'drafting' },
            { label: 'Filing', ok: filingOk, detail: filingOk ? 'Filed' : 'Not filed yet', tab: 'post' },
            { label: 'Post Incorporation Checklist', ok: checklist.length > 0 && checklistDone === checklist.length, detail: checklist.length ? `${checklistDone} of ${checklist.length} done` : 'Not generated yet', tab: 'post' },
          ];
          const completeCount = rows.filter(r => r.ok).length;

          return (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden col-span-3">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <div>
                  <h4 className="text-base font-bold text-[#0f1f45]">Status Panel</h4>
                  <p className="text-xs text-gray-400 mt-0.5">One-glance readiness across every tab — click any row to jump straight there.</p>
                </div>
                <span className={`px-3 py-1.5 rounded-full text-xs font-semibold ${completeCount === rows.length ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                  {completeCount} / {rows.length} complete
                </span>
              </div>
              <div className="divide-y divide-gray-50">
                {rows.map(row => (
                  <button
                    key={row.label}
                    onClick={() => setActiveTab(row.tab)}
                    className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-50 text-left"
                  >
                    <div className="flex items-center gap-3">
                      <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${row.ok ? 'bg-green-500' : 'bg-amber-400'}`} />
                      <span className="text-sm font-medium text-gray-700">{row.label}</span>
                    </div>
                    <span className={`text-xs ${row.ok ? 'text-green-600' : 'text-amber-600'}`}>{row.detail}</span>
                  </button>
                ))}
              </div>
            </div>
          );
        })()}

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 col-span-3">
  <div className="flex items-center justify-between">
    <div>
      <h4 className="text-base font-bold text-[#0f1f45]">Incorporation Route</h4>
      <p className="text-sm text-gray-400 mt-1">
        Choose whether separate name approval is required or case will proceed directly through SPICe+ / incorporation forms.
      </p>
    </div>

    <div className="flex rounded-lg border border-gray-200 overflow-hidden">
      <button
        type="button"
        onClick={() => setNameApprovalRequired(true)}
        className={`px-4 py-2 text-sm font-medium ${
          nameApprovalRequired ? 'bg-[#0f1f45] text-white' : 'bg-white text-gray-600'
        }`}
      >
        Name Approval Required
      </button>

      <button
        type="button"
        onClick={() => {
          setNameApprovalRequired(false);
          if (activeTab === 'name') setActiveTab('people');
        }}
        className={`px-4 py-2 text-sm font-medium border-l border-gray-200 ${
          !nameApprovalRequired ? 'bg-[#0f1f45] text-white' : 'bg-white text-gray-600'
        }`}
      >
        Proceed Directly
      </button>
    </div>
  </div>
</div>
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 col-span-2">
            <h4 className="text-base font-bold text-[#0f1f45] mb-4">Workflow Progress</h4>

            <div className="space-y-3">
              {workflowStages.map((stage, index) => {
                const currentIndex = workflowStages.indexOf(currentStage);
                const done = index < currentIndex;
                const current = index === currentIndex;

                return (
                  <div key={stage} className="flex items-center gap-3">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                      done ? 'bg-green-100 text-green-700' :
                      current ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-400'
                    }`}>
                      {done ? '✓' : index + 1}
                    </div>
                    <p className={`text-sm ${current ? 'font-semibold text-gray-800' : 'text-gray-500'}`}>
                      {stage}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <h4 className="text-base font-bold text-[#0f1f45] mb-4">Case Summary</h4>

            <div className="space-y-3 text-sm">
              <InfoRow label="Client" value={item.client_name} />
              <InfoRow label="Type" value={item.incorporation_type} />
              <InfoRow label="Assigned To" value={item.assigned_to || '-'} />
              <InfoRow label="Priority" value={item.priority} />
              <InfoRow label="Expected" value={item.expected_completion ? new Date(item.expected_completion).toLocaleDateString('en-IN') : '-'} />
            </div>
          </div>
        </div>
      )}

{activeTab === 'name' && (
  <NameApprovalTab
    data={nameApproval}
    setData={setNameApproval}
    isLLP={isLLPCase}
  />
)}

{activeTab === 'structure' && (
  <CompanyStructureCapitalTab
    item={item}
    data={capitalStructure}
    setData={setCapitalStructure}
    nicCode={nameApproval.nic_code}
    casePeople={casePeople}
  />
)}

{activeTab === 'office' && (
  <RegisteredOfficeTab
    data={registeredOfficeData}
    setData={setRegisteredOfficeData}
  />
)}

{activeTab === 'opc' && isOPC && (
  <OpcNomineeTab
    data={opcNomineeData}
    setData={setOpcNomineeData}
  />
)}

    {activeTab === 'people' && (
    <PromotersPartnersTab
        item={item}
        peopleList={peopleList}
        casePeople={casePeople}
        setCasePeople={setCasePeople}
        peopleForm={peopleForm}
        setPeopleForm={setPeopleForm}
        capitalStructure={capitalStructure}
        setCapitalStructure={setCapitalStructure}
    />
    )}

{activeTab === 'stamp' && (
  <StampDutyPanTanTab
    item={item}
    data={stampDutyPanTanData}
    setData={setStampDutyPanTanData}
  />
)}

    {activeTab === 'drafting' && (
  <DraftingTab
    item={item}
    casePeople={casePeople}
    nameApproval={nameApproval}
    capitalStructure={capitalStructure}
    registeredOfficeData={registeredOfficeData}
    opcNomineeData={opcNomineeData}
    drafts={drafts}
    setDrafts={setDrafts}
    selectedDraft={selectedDraft}
    setSelectedDraft={setSelectedDraft}
  />
)}

{activeTab === 'post' && (
  <PostIncorporationTab
    item={item}
    postIncorpData={postIncorpData}
    setPostIncorpData={setPostIncorpData}
    checklist={postIncorpData.checklist || []}
    setChecklist={(updater) => {
      setPostIncorpData(prev => ({
        ...prev,
        checklist: typeof updater === 'function' ? updater(prev.checklist || []) : updater,
      }));
    }}
    registeredOfficeData={registeredOfficeData}
    filingData={filingData}
    setFilingData={setFilingData}
    nameApproval={nameApproval}
    capitalStructure={capitalStructure}
    casePeople={casePeople}
    setCasePeople={setCasePeople}
    onNavigate={onNavigate}
  />
)}
    {activeTab !== 'overview' && activeTab !== 'name' && activeTab !== 'structure' && activeTab !== 'office' && activeTab !== 'opc' && activeTab !== 'people' && activeTab !== 'stamp' && activeTab !== 'drafting' && activeTab !== 'post' && (
  <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8 min-h-[360px]">
    <h4 className="text-lg font-bold text-[#0f1f45]">
      {tabs.find(t => t.key === activeTab)?.label}
    </h4>
    <p className="text-sm text-gray-400 mt-2">
      This workspace will hold all work for this incorporation case.
    </p>

    <div className="mt-10 border border-dashed border-gray-200 rounded-xl p-10 text-center text-gray-400 text-sm">
      {tabs.find(t => t.key === activeTab)?.label} section ready for next build.
    </div>
  </div>
)}
    </div>
  );
}

// ── Export helpers — Word (.doc via HTML blob) and PDF (via print dialog) ──
function textToHtml(text) {
  return (text || '')
    .split('\n\n')
    .map(para => `<p>${para.split('\n').map(l => l.replace(/&/g,'&amp;').replace(/</g,'&lt;')).join('<br/>')}</p>`)
    .join('');
}

function buildWordDoc(title, bodyHtml) {
  return `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="utf-8"><title>${title}</title>
<style>
  @page { size: A4; margin: 1in; }
  body { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 12pt; line-height: 1.15; text-align: justify; color: #000; }
  p { margin: 0 0 8pt 0; line-height: 1.15; text-align: justify; }

  /* ── Legal-deed schema (LLP Agreement) ── */
  .doc-title { text-align: center; font-weight: bold; text-transform: uppercase; text-decoration: underline; font-size: 14pt; margin-bottom: 5px; }
  .doc-subtitle { text-align: center; font-style: italic; font-size: 12pt; margin-bottom: 20px; }
  .recital-text { margin-bottom: 15px; text-align: justify; }
  .party-name { font-weight: bold; text-transform: uppercase; }
  .section-header { text-align: center; font-weight: bold; text-transform: uppercase; font-size: 12pt; margin-top: 20px; margin-bottom: 10px; }
  .clause-heading { font-weight: bold; text-decoration: underline; font-size: 12pt; margin: 15px 0 5px 0; text-align: left; }
  .clause-num { margin: 0 0 6pt 0; padding-left: 24pt; text-indent: -24pt; text-align: justify; }
  .clause-num .cn { font-weight: normal; }
  .clause-cont { margin: 0 0 6pt 0; padding-left: 24pt; text-align: justify; }
  .def-list { margin: 0 0 12pt 0; padding-left: 34pt; list-style-type: lower-roman; }
  .def-list li { margin-bottom: 5px; text-align: justify; }
  .sub-list { margin: 3pt 0 10pt 0; padding-left: 46pt; }
  .sub-list li { margin-bottom: 4px; text-align: justify; }
  ol.sub-list[type="a"] { list-style-type: lower-alpha; }
  ol.sub-list[type="1"] { list-style-type: decimal; }
  .sig-block, .sig-block td { border: none !important; }
  .sig-block td { padding: 1pt 0; font-size: 12pt; }

  /* ── Charter documents (MOA / AOA / declarations) ── */
  h1 { font-size: 14pt; font-weight: bold; text-transform: uppercase; text-align: center; margin: 0 0 4pt 0; }
  h2 { font-size: 12pt; font-weight: bold; text-align: center; margin: 0 0 12pt 0; }
  h3 { font-size: 12pt; font-style: italic; text-align: center; margin: 0 0 12pt 0; }
  .sub-heading { font-size: 11pt; font-weight: bold; text-decoration: underline; font-style: italic; margin: 8pt 0 2pt 0; }
  ol { padding-left: 22pt; }
  ol li { margin-bottom: 8pt; text-align: justify; line-height: 1.35; }
  table { width: 100%; border-collapse: collapse; margin-top: 12pt; }
  td, th { border: 1pt solid #000; padding: 6pt 8pt; font-size: 10pt; vertical-align: top; }
  th { font-weight: bold; text-align: center; }
  tr.sig-row { height: 45pt; }
</style></head>
<body>${bodyHtml}</body></html>`;
}

// \u2500\u2500 HTML \u2192 RTF \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
// The old exporter wrote an HTML string with a .doc extension. Word opens
// that as a "web page", and the moment it saves it drops a
// "<name>_files" sidecar folder (themedata.thmx, filelist.xml,
// colorschememapping.xml) next to the document \u2014 Word's own theme cache,
// harmless but confusing. RTF is a single self-contained text format that
// Word/LibreOffice/Google Docs all open as a native, fully-editable
// document with no sidecar and no "format doesn't match extension" warning.
function rtfEscape(s) {
  let out = '';
  for (const ch of String(s)) {
    const c = ch.codePointAt(0);
    if (ch === '\\') out += '\\\\';
    else if (ch === '{') out += '\\{';
    else if (ch === '}') out += '\\}';
    else if (c === 9) out += '\\tab ';
    else if (c === 10 || c === 13) out += ' ';
    else if (c === 160) out += '\\~';
    else if (c < 128) out += ch;
    else if (c > 0xffff) out += '?';
    else out += `\\u${c > 32767 ? c - 65536 : c}?`;
  }
  return out;
}

function htmlToRtf(bodyHtml) {
  const doc = new DOMParser().parseFromString(`<div id="__root">${bodyHtml || ''}</div>`, 'text/html');
  const root = doc.getElementById('__root');
  const kids = (el, tag) => Array.from(el.children).filter(c => c.tagName === tag);
  const toRoman = (num) => {
    const map = [[10, 'x'], [9, 'ix'], [5, 'v'], [4, 'iv'], [1, 'i']];
    let r = '', x = num;
    for (const [v, s] of map) while (x >= v) { r += s; x -= v; }
    return r;
  };

  const styleOf = (n) => (n.getAttribute && n.getAttribute('style') || '').replace(/\s+/g, '').toLowerCase();
  const alignFromStyle = (st) => st.includes('text-align:center') ? '\\qc' : st.includes('text-align:left') ? '\\ql' : '\\qj';

  const inline = (node) => {
    let s = '';
    node.childNodes.forEach(n => {
      if (n.nodeType === 3) { s += rtfEscape(n.nodeValue.replace(/[ \t\r\n]+/g, ' ')); return; }
      if (n.nodeType !== 1) return;
      const tag = n.tagName.toLowerCase();
      const cls = n.getAttribute('class') || '';
      if (tag === 'br') s += '\\line ';
      else if (tag === 'b' || tag === 'strong') s += `{\\b ${inline(n)}}`;
      else if (tag === 'i' || tag === 'em') s += `{\\i ${inline(n)}}`;
      else if (tag === 'u') s += `{\\ul ${inline(n)}\\ulnone}`;
      else if (tag === 'span' && cls.includes('party-name')) s += `{\\b ${inline(n)}}`;
      else if (tag === 'span' && cls.includes('cn')) s += `${inline(n)}\\tab `;
      else if (tag === 'div' && cls.includes('sub-heading')) s += `{\\b\\i\\ul ${inline(n)}\\ulnone}\\line `;
      else s += inline(n);
    });
    return s;
  };

  let body = '';
  const tableRtf = (table) => {
    let out = '';
    const borderless = (table.getAttribute('class') || '').includes('sig-block') || styleOf(table).includes('border:none');
    table.querySelectorAll('tr').forEach(tr => {
      const cells = Array.from(tr.children);
      const n = cells.length || 1;
      const w = Math.floor(9360 / n);
      let defs = '';
      for (let i = 1; i <= n; i++) defs += `${borderless ? '' : '\\clbrdrt\\brdrs\\clbrdrl\\brdrs\\clbrdrb\\brdrs\\clbrdrr\\brdrs'}\\cellx${w * i}`;
      const hMatch = cells.map(c => styleOf(c)).join('').match(/height:(\d+)px/);
      const rh = hMatch ? `\\trrh-${Math.round(Number(hMatch[1]) * 15)}` : '';
      out += `\\trowd\\trgaph108\\trleft0${rh}${defs}\n`;
      cells.forEach(c => {
        const isHead = c.tagName.toLowerCase() === 'th';
        const align = isHead ? '\\qc' : (borderless ? '\\ql' : '\\qc');
        out += `\\pard\\intbl${align}\\f0\\fs${borderless ? 24 : 20} ${isHead ? '\\b ' : ''}${inline(c)}\\cell\n`;
      });
      out += '\\row\n';
    });
    return out + '\\pard\\par\n';
  };

  const walk = (parent) => {
    parent.childNodes.forEach(n => {
      if (n.nodeType === 3) {
        const t = n.nodeValue.trim();
        if (t) body += `\\pard\\qj\\f0\\fs24 ${rtfEscape(t)}\\par\n`;
        return;
      }
      if (n.nodeType !== 1) return;
      const tag = n.tagName.toLowerCase();
      const cls = n.getAttribute('class') || '';
      const st = styleOf(n);
      if (tag === 'div' && cls.includes('doc-title')) body += `\\pard\\qc\\sb0\\sa60\\b\\ul\\f0\\fs28 ${inline(n)}\\ulnone\\b0\\par\n`;
      else if (tag === 'div' && cls.includes('doc-subtitle')) body += `\\pard\\qc\\sa280\\i\\f0\\fs24 ${inline(n)}\\i0\\par\n`;
      else if (tag === 'div' && cls.includes('section-header')) body += `\\pard\\qc\\sb280\\sa160\\b\\f0\\fs24 ${inline(n)}\\b0\\par\n`;
      else if (tag === 'h1') body += `\\pard\\qc\\sb0\\sa120\\b\\f0\\fs28 ${inline(n)}\\b0\\par\n`;
      else if (tag === 'h2') body += `\\pard\\qc\\sa120\\b\\f0\\fs24 ${inline(n)}\\b0\\par\n`;
      else if (tag === 'h3') body += `\\pard\\qc\\sa120\\i\\f0\\fs24 ${inline(n)}\\i0\\par\n`;
      else if (tag === 'p' && cls.includes('clause-heading')) body += `\\pard\\ql\\sb240\\sa60\\b\\ul\\f0\\fs24 ${inline(n)}\\ulnone\\b0\\par\n`;
      else if (tag === 'p' && cls.includes('clause-num')) body += `\\pard\\qj\\fi-460\\li460\\sa120\\tx460\\f0\\fs24 ${inline(n)}\\par\n`;
      else if (tag === 'p' && cls.includes('clause-cont')) body += `\\pard\\qj\\li460\\sa120\\f0\\fs24 ${inline(n)}\\par\n`;
      else if (tag === 'p' && cls.includes('recital-text')) body += `\\pard\\qj\\sa240${st.includes('font-weight:bold') ? '\\b' : ''}\\f0\\fs24 ${inline(n)}${st.includes('font-weight:bold') ? '\\b0' : ''}\\par\n`;
      else if (tag === 'p') { const bold = st.includes('font-weight:bold'); body += `\\pard${alignFromStyle(st)}\\sa160${bold ? '\\b' : ''}\\f0\\fs24 ${inline(n)}${bold ? '\\b0' : ''}\\par\n`; }
      else if (tag === 'div' && cls.includes('sub-heading')) body += `\\pard\\qj\\sb120\\b\\i\\ul\\f0\\fs24 ${inline(n)}\\ulnone\\i0\\b0\\par\n`;
      else if (tag === 'ol' || tag === 'ul') {
        const type = n.getAttribute('type');
        const roman = cls.includes('def-list') || type === 'i';
        const letters = type === 'a';
        const indent = cls.includes('def-list') ? 720 : cls.includes('sub-list') ? 1000 : 820;
        let i = 0;
        kids(n, 'LI').forEach(li => {
          const mark = tag === 'ul' ? '\\bullet'
            : roman ? `(${toRoman(i + 1)})`
            : letters ? `(${String.fromCharCode(97 + i)})`
            : `${i + 1}.`;
          body += `\\pard\\qj\\fi-460\\li${indent}\\tx${indent}\\sa100\\f0\\fs24 ${mark}\\tab ${inline(li)}\\par\n`;
          i++;
        });
      }
      else if (tag === 'table') body += tableRtf(n);
      else walk(n);
    });
  };
  walk(root);

  return `{\\rtf1\\ansi\\ansicpg1252\\uc1\\deff0\\deflang1033
{\\fonttbl{\\f0\\fswiss\\fcharset0 Plus Jakarta Sans;}}
\\paperw11906\\paperh16838\\margl1800\\margr1440\\margt1440\\margb1440
${body}}`;
}

function downloadWord(filename, bodyHtml) {
  const rtf = htmlToRtf(bodyHtml);
  const blob = new Blob([rtf], { type: 'application/rtf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename.replace(/\s+/g, '_')}.rtf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function printAsPdf(filename, bodyHtml) {
  const win = window.open('', '_blank');
  if (!win) return false;
  win.document.write(buildWordDoc(filename, bodyHtml));
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 300);
  return true;
}

// ── Shared modal chrome — every document popup uses this frame ─────────────
// ── Alert Modal — replaces every native alert() with an in-app popup ────────
function AlertModal({ message, title, variant = 'error', onClose }) {
  const styles = {
    error: { bg: 'bg-red-50', border: 'border-red-100', icon: '#dc2626', title: title || 'Something needs attention' },
    warning: { bg: 'bg-amber-50', border: 'border-amber-100', icon: '#d97706', title: title || 'Please check' },
    info: { bg: 'bg-blue-50', border: 'border-blue-100', icon: '#2563eb', title: title || 'Notice' },
  };
  const s = styles[variant] || styles.error;

  return (
    <div className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className={`px-6 py-5 flex items-start gap-3 ${s.bg} rounded-t-2xl border-b ${s.border}`}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={s.icon} strokeWidth="2" className="flex-shrink-0 mt-0.5">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          <div>
            <p className="text-sm font-bold text-gray-800">{s.title}</p>
            <p className="text-sm text-gray-600 mt-1">{message}</p>
          </div>
        </div>
        <div className="px-6 py-4 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-white text-sm font-medium bg-[#0f1f45]">
            OK
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Confirm Modal — replaces window.confirm() with an in-app popup ─────────
function ConfirmModal({ message, title, confirmLabel = 'Confirm', onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-5 flex items-start gap-3 bg-red-50 rounded-t-2xl border-b border-red-100">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" className="flex-shrink-0 mt-0.5">
            <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/>
          </svg>
          <div>
            <p className="text-sm font-bold text-gray-800">{title || 'Confirm'}</p>
            <p className="text-sm text-gray-600 mt-1">{message}</p>
          </div>
        </div>
        <div className="px-6 py-4 flex justify-end gap-2">
          <button onClick={onCancel} className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-gray-600 text-sm font-medium">
            Cancel
          </button>
          <button onClick={onConfirm} className="px-4 py-2 rounded-lg text-white text-sm font-medium bg-red-600 hover:bg-red-700">
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function DraftModal({ title, subtitle, colorClass, onClose, onSave, wordHtml, pdfHtml, filename, children }) {
  const [popupBlocked, setPopupBlocked] = useState(false);

  const handlePdfClick = () => {
    const ok = printAsPdf(filename, pdfHtml);
    if (!ok) setPopupBlocked(true);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="no-scrollbar bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        onClick={e => e.stopPropagation()}
      >
        <style>{`.no-scrollbar::-webkit-scrollbar { display: none; }`}</style>
        <div className={`px-6 py-4 border-b border-gray-100 flex items-center justify-between rounded-t-2xl ${colorClass}`}>
          <div>
            <h3 className="text-base font-bold text-white">{title}</h3>
            <p className="text-xs text-white/80 mt-0.5">{subtitle}</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white text-xl leading-none">×</button>
        </div>

        <div className="p-6 space-y-5">
          {children}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-2 rounded-b-2xl sticky bottom-0">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-gray-600 text-sm font-medium">
            Close
          </button>
          <button
            onClick={() => downloadWord(filename, wordHtml)}
            className="px-4 py-2 rounded-lg border border-blue-200 bg-white text-blue-600 text-sm font-medium hover:bg-blue-50"
          >
            Word
          </button>
          <button
            onClick={handlePdfClick}
            className="px-4 py-2 rounded-lg border border-red-200 bg-white text-red-600 text-sm font-medium hover:bg-red-50"
          >
            PDF
          </button>
          <button
            onClick={onSave}
            className="px-4 py-2 rounded-lg text-white text-sm font-medium bg-[#0f1f45]"
          >
            Save
          </button>
        </div>
      </div>

      {popupBlocked && (
        <AlertModal
          variant="warning"
          title="Pop-up blocked"
          message="Your browser blocked the PDF export window. Please allow pop-ups for this site and try again."
          onClose={() => setPopupBlocked(false)}
        />
      )}
    </div>
  );
}

// ── Document type selector card ─────────────────────────────────────────────
function DraftCard({ icon, title, description, colorClass, status, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`text-left rounded-xl border-2 p-4 transition-all hover:shadow-md ${colorClass}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="w-9 h-9 rounded-lg bg-white/70 flex items-center justify-center text-lg">{icon}</div>
        {status && (
          <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-white/80">{status}</span>
        )}
      </div>
      <p className="text-sm font-bold text-gray-800">{title}</p>
      <p className="text-xs text-gray-600 mt-1">{description}</p>
    </button>
  );
}

// ── MOA Modal — 6/7/8 clause structure, branches for Section 8 and OPC ─────
function MoaModal({ item, nameApproval, capitalStructure, registeredOfficeData, opcNomineeData, casePeople, existing, onClose, onSave }) {
  const isOPC = item.incorporation_type === 'One Person Company';
  const isSection8 = item.incorporation_type === 'Section 8 Company';
  const ic = "w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-blue-500 bg-white";

  const approvedName = nameApproval.approved_name || nameApproval.proposed_name_1 || '';
  const stateVal = registeredOfficeData?.correspondence_address?.state || '';

  const [companyName, setCompanyName] = useState(existing?.companyName || approvedName);
  const [stateName, setStateName] = useState(existing?.stateName || stateVal);
  // Clause 3(a) — main objects, numbered list, one clause per item
  const [objectsMain, setObjectsMain] = useState(existing?.objectsMain || [{ id: 1, text: nameApproval.main_object || '' }]);
  // Clause 3(b) — matters for furtherance, numbered list with a bold heading per item
  const [objectsFurtherance, setObjectsFurtherance] = useState(existing?.objectsFurtherance || [{ id: 1, heading: '', text: '' }]);
  const [objectScope, setObjectScope] = useState(existing?.objectScope || 'the whole of India');

  const addMainObject = () => setObjectsMain(list => [...list, { id: Date.now(), text: '' }]);
  const updateMainObject = (id, value) => setObjectsMain(list => list.map(o => o.id === id ? { ...o, text: value } : o));
  const removeMainObject = (id) => setObjectsMain(list => list.filter(o => o.id !== id));

  const addFurtheranceItem = () => setObjectsFurtherance(list => [...list, { id: Date.now(), heading: '', text: '' }]);
  const updateFurtheranceItem = (id, field, value) => setObjectsFurtherance(list => list.map(o => o.id === id ? { ...o, [field]: value } : o));
  const removeFurtheranceItem = (id) => setObjectsFurtherance(list => list.filter(o => o.id !== id));

  const equityClasses = capitalStructure?.equity_classes || [];
  const totalAuthCapital = equityClasses.reduce((s, c) => s + (Number(c.authorized_shares) || 0) * (Number(c.authorized_nominal_value) || 0), 0)
    + (capitalStructure?.preference_classes || []).reduce((s, c) => s + (Number(c.authorized_shares) || 0) * (Number(c.authorized_nominal_value) || 0), 0);
  const totalShares = equityClasses.reduce((s, c) => s + (Number(c.authorized_shares) || 0), 0);
  const faceValue = Number(equityClasses[0]?.authorized_nominal_value) || 10;

  const subscribers = casePeople || [];
  const nomineeName = [opcNomineeData?.nominee_first_name, opcNomineeData?.nominee_middle_name, opcNomineeData?.nominee_surname].filter(Boolean).join(' ');

  const section8FixedB = `The doing of all such other lawful things as considered necessary for the furtherance of the above objects: Provided that the company shall not support with its funds, or endeavour to impose on, or procure to be observed by its members or others, any regulation or restriction which, as an object of the company, would make it a trade union.`;

  const liabilityText = `The liability of the member(s) is limited, and this liability is limited to the amount unpaid, if any, on the shares held by them.`;
  const section8ProfitClause = `The income and property of the company, whencesoever derived, shall be applied solely for the promotion of its objects as set forth in this Memorandum. No portion thereof shall be paid or transferred, directly or indirectly, by way of dividend, bonus, or otherwise by way of profit, to the members of the company.`;

  const buildSubscriberTable = (forWord) => {
    if (subscribers.length === 0) {
      return forWord ? '<p><em>[No subscribers added yet — add them in Promoters &amp; Partners tab]</em></p>' : '[No subscribers added yet — add them in Promoters & Partners tab]';
    }
    if (forWord) {
      const rows = subscribers.map((p, i) => `<tr class="sig-row">
        <td>${i + 1}</td>
        <td>${p.full_name || ''}<br/>${[p.father_first_name, p.father_surname].filter(Boolean).join(' ')}<br/>${(p.present_address?.address) || p.address || ''}</td>
        <td>${p.occupation_type || ''}</td>
        <td>${p.shares_or_contribution || 0}</td>
        <td></td>
      </tr>`).join('');
      return `<table><tr><th>No.</th><th>Name, Address &amp; Father's Name</th><th>Occupation</th><th>Shares Taken</th><th>Signature</th></tr>${rows}</table>`;
    }
    return subscribers.map((p, i) => `${i + 1}. ${p.full_name} — Shares: ${p.shares_or_contribution || 0} — Signature: __________`).join('\n');
  };

  const mainObjectsText = () => objectsMain.filter(o => o.text.trim()).map((o, i) => `${i + 1}. ${o.text}`).join('\n');
  const furtheranceText = () => objectsFurtherance.filter(o => o.text.trim()).map((o, i) => `${o.heading ? `[${o.heading}]\n` : ''}${i + 1}. ${o.text}`).join('\n');

  const mainObjectsHtml = () => {
    const items = objectsMain.filter(o => o.text.trim());
    if (items.length === 0) return '<p><em>[Main objects — not yet entered]</em></p>';
    return `<ol>${items.map(o => `<li>${o.text}</li>`).join('')}</ol>`;
  };
  const furtheranceHtml = () => {
    const items = objectsFurtherance.filter(o => o.text.trim());
    if (items.length === 0) return '<p><em>[Matters for furtherance — not yet entered]</em></p>';
    return `<ol>${items.map(o => `<li>${o.heading ? `<div class="sub-heading">${o.heading}</div>` : ''}${o.text}</li>`).join('')}</ol>`;
  };

  const compileText = () => {
    let out = `THE COMPANIES ACT, 2013\nMEMORANDUM OF ASSOCIATION\nOF\n${(companyName || '[COMPANY NAME]').toUpperCase()}\n\n`;
    out += `1. The name of the Company is "${companyName || '[Company Name]'}".\n\n`;
    out += `2. The Registered Office of the Company will be situated in the State of ${stateName || '[State]'}.\n\n`;
    out += `3. (a) The main objects to be pursued by the Company on its incorporation are:\n${mainObjectsText() || '[Main objects]'}\n\n`;
    out += `(b) Matters necessary for furtherance of the objects specified in clause 3(a):\n${isSection8 ? section8FixedB : (furtheranceText() || '[Matters necessary for furtherance of the above objects]')}\n\n`;
    if (isSection8) {
      out += `4. The objects of the Company extend to ${objectScope}.\n\n`;
      out += `5. ${liabilityText}\n\n`;
      out += `6. ${section8ProfitClause}\n\n`;
      out += `7. ${totalAuthCapital > 0 ? `The Authorised Share Capital of the Company is ₹${totalAuthCapital.toLocaleString('en-IN')} divided into ${totalShares} shares of ₹${faceValue} each.` : 'The Company does not have a share capital and is limited by guarantee.'}\n\n`;
      out += `SUBSCRIBER SHEET\n${buildSubscriberTable(false)}`;
    } else {
      out += `4. ${liabilityText}\n\n`;
      out += `5. The Authorised Share Capital of the Company is ₹${totalAuthCapital.toLocaleString('en-IN')} divided into ${totalShares} shares of ₹${faceValue} each.\n\n`;
      out += `6. We, the several persons whose names, addresses and descriptions are subscribed below, are desirous of being formed into a Company in pursuance of this Memorandum of Association, and we respectively agree to take the number of shares set opposite our names.\n\n${buildSubscriberTable(false)}`;
      if (isOPC) {
        out += `\n\n8. NOMINEE CLAUSE:\nIn the event of death or incapacity to contract of the sole subscriber, ${nomineeName || '[Nominee Name]'} shall become the sole member of the Company.`;
      }
    }
    return out;
  };

  const compileHtml = () => {
    let out = `<h1>THE COMPANIES ACT, 2013</h1><h2>MEMORANDUM OF ASSOCIATION OF ${(companyName || '[COMPANY NAME]').toUpperCase()}</h2>`;
    out += `<p class="clause-heading">1. The name of the Company is "${companyName || '[Company Name]'}".</p>`;
    out += `<p class="clause-heading">2. The Registered Office of the Company will be situated in the State of ${stateName || '[State]'}.</p>`;
    out += `<p class="clause-heading">3. (a) The main objects to be pursued by the Company on its incorporation are:</p>${mainObjectsHtml()}`;
    out += `<p class="clause-heading">(b) Matters necessary for furtherance of the objects specified in clause 3(a):</p>${isSection8 ? `<p>${section8FixedB}</p>` : furtheranceHtml()}`;
    if (isSection8) {
      out += `<p class="clause-heading">4. The objects of the Company extend to ${objectScope}.</p>`;
      out += `<p class="clause-heading">5. ${liabilityText}</p>`;
      out += `<p class="clause-heading">6. ${section8ProfitClause}</p>`;
      out += `<p class="clause-heading">7. ${totalAuthCapital > 0 ? `The Authorised Share Capital of the Company is ₹${totalAuthCapital.toLocaleString('en-IN')} divided into ${totalShares} shares of ₹${faceValue} each.` : 'The Company does not have a share capital and is limited by guarantee.'}</p>`;
      out += `<h2>SUBSCRIBER SHEET</h2>${buildSubscriberTable(true)}`;
    } else {
      out += `<p class="clause-heading">4. ${liabilityText}</p>`;
      out += `<p class="clause-heading">5. The Authorised Share Capital of the Company is ₹${totalAuthCapital.toLocaleString('en-IN')} divided into ${totalShares} shares of ₹${faceValue} each.</p>`;
      out += `<p class="clause-heading">6. We, the several persons whose names are subscribed, agree to take the shares set against our names.</p>${buildSubscriberTable(true)}`;
      if (isOPC) out += `<p class="clause-heading">8. Nominee Clause: In the event of death or incapacity of the sole subscriber, ${nomineeName || '[Nominee Name]'} shall become the sole member.</p>`;
    }
    return out;
  };

  const handleSave = () => {
    onSave({
      key: 'MOA', name: 'Memorandum of Association (MOA)', type: 'Charter', status: 'Generated',
      companyName, stateName, objectsMain, objectsFurtherance, objectScope,
      content: compileText(),
    });
  };

  return (
    <DraftModal
      title="Memorandum of Association"
      subtitle={isSection8 ? 'Section 8 format' : isOPC ? 'Table A — OPC' : 'Table A — Schedule I'}
      colorClass="bg-gradient-to-r from-blue-600 to-blue-700"
      onClose={onClose}
      onSave={handleSave}
      filename={`MOA_${item.proposed_name || 'Draft'}`}
      wordHtml={compileHtml()}
      pdfHtml={compileHtml()}
    >
      <div>
        <p className="text-xs font-bold text-blue-700 mb-2">Clause 1 — Name</p>
        {approvedName ? (
          <input readOnly value={companyName} className={`${ic} bg-blue-50 text-gray-700`} />
        ) : (
          <input value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="Company name" className={ic} />
        )}
      </div>

      <div>
        <p className="text-xs font-bold text-blue-700 mb-2">Clause 2 — Registered Office (State)</p>
        {stateVal ? (
          <input readOnly value={stateName} className={`${ic} bg-blue-50 text-gray-700`} />
        ) : (
          <input value={stateName} onChange={e => setStateName(e.target.value)} placeholder="State" className={ic} />
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-bold text-blue-700">Clause 3(a) — Main Objects</p>
          <button type="button" onClick={addMainObject} className="text-xs text-blue-600 hover:underline">+ Add clause</button>
        </div>
        <div className="space-y-2">
          {objectsMain.map((o, i) => (
            <div key={o.id} className="flex items-start gap-2">
              <span className="text-xs font-bold text-gray-400 mt-3 w-5">{i + 1}.</span>
              <textarea
                rows="2"
                value={o.text}
                onChange={e => updateMainObject(o.id, e.target.value)}
                placeholder={`Object clause ${i + 1}`}
                className={ic}
              />
              {objectsMain.length > 1 && (
                <button type="button" onClick={() => removeMainObject(o.id)} className="text-xs text-red-500 hover:underline mt-3">Remove</button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-bold text-blue-700">Clause 3(b) — Matters for Furtherance</p>
          {!isSection8 && (
            <button type="button" onClick={addFurtheranceItem} className="text-xs text-blue-600 hover:underline">+ Add clause</button>
          )}
        </div>
        {isSection8 ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-500">
            {section8FixedB}
            <p className="text-[11px] text-gray-400 mt-1 italic">Fixed statutory wording per Section 8 — not editable.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {objectsFurtherance.map((o, i) => (
              <div key={o.id} className="border border-gray-100 rounded-lg p-2 space-y-1.5">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-gray-400 w-5">{i + 1}.</span>
                  <input
                    value={o.heading}
                    onChange={e => updateFurtheranceItem(o.id, 'heading', e.target.value)}
                    placeholder="Short heading (e.g. To open bank account)"
                    className="flex-1 text-xs italic font-semibold border-0 border-b border-dashed border-gray-300 focus:outline-none focus:border-blue-400 bg-transparent"
                  />
                  {objectsFurtherance.length > 1 && (
                    <button type="button" onClick={() => removeFurtheranceItem(o.id)} className="text-xs text-red-500 hover:underline">Remove</button>
                  )}
                </div>
                <textarea
                  rows="2"
                  value={o.text}
                  onChange={e => updateFurtheranceItem(o.id, 'text', e.target.value)}
                  placeholder="Clause text"
                  className={ic}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {isSection8 && (
        <div>
          <p className="text-xs font-bold text-blue-700 mb-2">Clause 4 — Territorial Scope</p>
          <select value={objectScope} onChange={e => setObjectScope(e.target.value)} className={ic}>
            <option>the whole of India</option>
            <option>the State of {stateName || '[State]'}</option>
          </select>
        </div>
      )}

      <div>
        <p className="text-xs font-bold text-blue-700 mb-2">Clause {isSection8 ? '5' : '4'} — Liability</p>
        <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-500">
          {liabilityText}
          <p className="text-[11px] text-gray-400 mt-1 italic">Same as Table A — not editable.</p>
        </div>
      </div>

      <div>
        <p className="text-xs font-bold text-blue-700 mb-2">Clause {isSection8 ? '7' : '5'} — Capital</p>
        <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2.5 text-sm text-blue-800">
          {totalAuthCapital > 0
            ? `Authorised Capital ₹${totalAuthCapital.toLocaleString('en-IN')} — ${totalShares} shares of ₹${faceValue} each`
            : 'Set Authorised Capital in Structure & Capital tab'}
        </div>
      </div>

      <div>
        <p className="text-xs font-bold text-blue-700 mb-2">Clause {isSection8 ? 'Subscriber Sheet' : '6'} — Subscribers</p>
        <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-xs text-gray-600 whitespace-pre-line max-h-40 overflow-y-auto">
          {buildSubscriberTable(false)}
        </div>
      </div>

      {isOPC && !isSection8 && (
        <div>
          <p className="text-xs font-bold text-blue-700 mb-2">Clause 8 — Nominee</p>
          <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2.5 text-sm text-blue-800">
            {nomineeName || 'Set nominee details in OPC Nominee tab'}
          </div>
        </div>
      )}
    </DraftModal>
  );
}

// ── LLP Agreement Modal — Sec 23(4)/Rule 21(1). Every clause is its own
//    editable box, pre-filled with the firm's standard deed wording (the
//    dynamic bits — name, address, partners, contribution — are filled in
//    from case data). Any clause's wording can be freely rewritten per case;
//    "Refill" on the data-driven clauses re-generates just that clause from
//    the latest case data without touching hand edits made elsewhere. ──────
function LlpAgreementModal({ item, nameApproval, capitalStructure, registeredOfficeData, casePeople, existing, onClose, onSave }) {
  const ic = "w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-purple-500 bg-white";
  const ta = `${ic} font-mono text-xs leading-relaxed`;

  const approvedName = nameApproval.approved_name || nameApproval.proposed_name_1 || item.proposed_name || '';
  const addr = registeredOfficeData?.correspondence_address || {};
  const defaultAddress = [addr.address, addr.district, addr.state].filter(Boolean).join(', ') + (addr.pincode ? ` – ${addr.pincode}` : '');
  const N = approvedName || item.proposed_name || '[LLP NAME]';

  const [agreementDate, setAgreementDate] = useState(existing?.agreementDate || new Date().toISOString().slice(0, 10));

  const numberToWords = (num) => {
    num = Math.floor(Number(num) || 0);
    if (num === 0) return 'Zero';
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
      'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const twoDigits = (n) => n < 20 ? ones[n] : tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
    const threeDigits = (n) => n < 100 ? twoDigits(n) : ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + twoDigits(n % 100) : '');
    const crore = Math.floor(num / 10000000);
    const lakh = Math.floor((num % 10000000) / 100000);
    const thousand = Math.floor((num % 100000) / 1000);
    const rest = num % 1000;
    let words = '';
    if (crore) words += threeDigits(crore) + ' Crore ';
    if (lakh) words += twoDigits(lakh) + ' Lakh ';
    if (thousand) words += twoDigits(thousand) + ' Thousand ';
    if (rest) words += threeDigits(rest);
    return words.trim();
  };

  const ORDINALS = ['FIRST', 'SECOND', 'THIRD', 'FOURTH', 'FIFTH', 'SIXTH', 'SEVENTH', 'EIGHTH', 'NINTH', 'TENTH'];
  const ordinalOf = (i) => ORDINALS[i] || `${i + 1}TH`;

  const partners = casePeople || [];
  const designatedPartners = partners.filter(p => p.person_role === 'director_only' || p.person_role === 'subscriber_director');
  const dpList = designatedPartners.length > 0 ? designatedPartners : partners;
  const nameOf = (p) => p.full_name || '[Partner Name]';
  const fatherOf = (p) => [p.father_first_name, p.father_middle_name, p.father_surname].filter(Boolean).join(' ') || "[Father's/Spouse's Name]";
  const addressOf = (p) => (p.present_address?.address) || p.address || '[Address]';
  const dpNames = () => dpList.length > 0 ? dpList.map(nameOf).join(' and ') : '[Designated Partners — add them in Promoters / Partners tab]';
  const totalContribution = Number(capitalStructure?.total_authorized_capital) || partners.reduce((s, p) => s + (Number(p.shares_or_contribution) || 0), 0);

  // ── Data-driven default text — regenerated on demand via each clause's Refill link ──
  const buildPreambleText = () => {
    if (partners.length === 0) {
      return `THIS Agreement of Limited Liability Partnership made on the [Date].\n\nBETWEEN\n\n[No partners added yet — add them in Promoters / Partners tab]\n\nNOW the Partners are interested in forming a Limited Liability Partnership under the Limited Liability Partnership Act, 2008 and that they intend to record the terms and conditions of the said formation.`;
    }
    const dateStr = agreementDate ? new Date(agreementDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '[Date]';
    const partyLines = partners.map((p, i) =>
      `MR./MS. ${nameOf(p).toUpperCase()}, ${p.entity_type === 'corporate' ? 'a body corporate' : `son/daughter of ${fatherOf(p)}`}, residing at ${addressOf(p)}, which expression shall, unless it be repugnant to the subject or context thereof, include his/her legal heirs, successors, nominees and permitted assignees and hereinafter called the ${ordinalOf(i)} PARTY,`
    ).join('\n\n');
    const partyWord = partners.length > 1 ? 'PARTIES' : 'PARTY';
    const ordinalsUsed = partners.length > 1 ? partners.map((_, i) => ordinalOf(i)).join(' AND ') : ordinalOf(0);
    return `THIS Agreement of Limited Liability Partnership made on ${dateStr}.\n\nBETWEEN\n\n${partyLines}\n\n(THE ${ordinalsUsed} ${partyWord} SHALL BE INDIVIDUALLY REFERRED TO AS A PARTNER AND COLLECTIVELY AS PARTNERS)\n\nNOW the Partners are interested in forming a Limited Liability Partnership under the Limited Liability Partnership Act, 2008 and intend to record the terms and conditions of the said formation.\n\nIT IS HEREBY AGREED BY AND BETWEEN THE PARTIES HERETO AS FOLLOWS:`;
  };

  const buildContributionText = () => {
    const rows = partners.length > 0 ? partners.map((p, i) => {
      const amt = Number(p.shares_or_contribution) || 0;
      const pct = totalContribution > 0 ? ((amt / totalContribution) * 100).toFixed(0) : '0';
      return `- ${ordinalOf(i)} Party (${nameOf(p)}): ${pct}% i.e. Rs. ${amt.toLocaleString('en-IN')}/- (Rupees ${numberToWords(amt)} only)`;
    }).join('\n') : '- [No partners added yet]';
    return `The initial contribution of ${N} shall be Rs. ${totalContribution.toLocaleString('en-IN')}/- (Rupees ${numberToWords(totalContribution)} only), which shall be contributed by the Partners in the following proportions:\n\n${rows}\n\nAny further contributions, if required by the LLP, shall be brought in by the Partners in such ratio as may be decided with the consent of all the Partners from time to time.\n\nThe contribution of a Partner may be tangible, intangible, moveable or immoveable property.\n\nSubject to the approval of all the Partners, ${N} may call upon the existing Partners to bring in additional contribution in their profit-sharing ratio for meeting operational needs, working-capital requirements and any other requirements.\n\nSuch increase in contribution shall be effected by amending the LLP Agreement with the approval of all Partners and on compliance with all the requirements under the Act and the Rules.`;
  };

  const buildAppointmentDpText = () => `${dpNames()} shall act as the Designated Partners of ${N} in terms of the requirements of the Limited Liability Partnership Act, 2008 and the Limited Liability Partnership Rules, 2009.\n\nA person shall not be appointed as a Designated Partner of the LLP if he:\n\n- has at any time within the preceding five years been adjudged insolvent; or\n- suspends, or has at any time within the preceding five years suspended, payment to his creditors and has not made a composition with them; or\n- has been convicted by a court for any offence involving moral turpitude and sentenced in respect thereof to imprisonment for not less than six months; or\n- has been convicted by a court for an offence involving section 30 of the Limited Liability Partnership Act, 2008.\n\nThe majority of the Partners of ${N} may, from time to time, appoint any one or more Partners to be the Designated Partners of ${N}, such that the minimum number of Designated Partners does not at any time fall below two.\n\nThe Designated Partners need not compulsorily be Partners of ${N}.\n\n${N} may, from time to time, increase or reduce the number of Designated Partners with the approval of all Partners, within the limits fixed by this Agreement and the LLP Act, 2008.`;

  const buildSignatureText = () => {
    if (partners.length === 0) {
      return `Signed and delivered by\n\nFor and on behalf of\n\n'${N}'\n\n[No partners added yet]\n\n\nWitness:\n\nName: _______________________\n\nAddress: ______________________\n\nSignature: _____________________`;
    }
    const sigLines = partners.map(p => `${nameOf(p)}\t\t\t(${dpList.includes(p) ? 'Designated Partner' : 'Partner'})`).join('\n\n\n');
    return `Signed and delivered by\n\nFor and on behalf of\n\n'${N}'\n\n\n${sigLines}\n\n\nWitness:\t\t\t       Witness:\n\nName: _______________________\t        2. Name : _______________________\n\nAddress: ______________________\t       Address:   ________________________\n\nSignature: _____________________\t      Signature: _________________________`;
  };

  // ── Fixed clause id list, in the order the real deed uses. `dynamic: true`
  //    marks the four clauses generated from case data (Preamble, Contribution,
  //    Appointment of Designated Partners, Signatures) — everything else is
  //    the firm's standard wording, editable like any other clause. ──────────
  const CLAUSE_DEFS = [
    { id: 'preamble', heading: 'Preamble & Parties', dynamic: true, build: buildPreambleText },
    { id: 'definitions', heading: 'Definitions', build: () =>
      `The Act and the said Act shall mean the Limited Liability Partnership Act, 2008\n\nThe Rules and the said Rules shall mean the Limited Liability Partnership Rules, 2009\n\nThe provisions of the first schedule of the Act will not be applicable to the LLP so far as they are not inconsistent with any of the clauses of the agreement.\n\nDesignated Partners - Designated Partner is Partner as defined u/s 7 of Limited Liability Partnership Act, 2008\n\nPartners - Partners are those who are admitted to ${N} with the mutual consent of the Designated Partners and include the Designated Partner.\n\nContribution – Contribution means the amount brought in by the partners of ${N}.\n\nProfit Sharing Ratio – Profit Sharing Ratio means the ratio in which the profits of the LLP shall be distributed among the partners of the LLP, and is more specifically described in the Profit Sharing Ratio clause of this Agreement.` },
    { id: 'name', heading: 'Name of the LLP', build: () =>
      `A Limited Liability Partnership shall be carried on in the name and style of '${N}' [hereinafter referred to as '${N}' or 'the LLP'].` },
    { id: 'office', heading: 'Registered Office', build: () =>
      `${N} shall have its Registered Office at ${defaultAddress || '[Registered Office Address]'}, and / or at such other place or places, as shall be agreed to by the majority of the Partners from time to time.` },
    { id: 'voting', heading: 'Voting Rights', build: () =>
      `The voting rights of all the Partners shall be in the ratio of their respective contribution in the LLP.` },
    { id: 'business', heading: 'Business', build: () =>
      `The objects to be pursued by the LLP on its incorporation are:\n\n${
        nameApproval.main_object
          ? nameApproval.main_object.split(/\r?\n/).map(l => l.trim()).filter(Boolean).map(l => `- ${l.replace(/^[-•(]?\s*[a-z0-9]+[).]\s*/i, '')}`).join('\n')
          : '- [First object — e.g. To carry on the business of trading, importing and exporting of …]\n- [Second object]'
      }` },
    { id: 'contribution', heading: 'Contribution', dynamic: true, build: buildContributionText },
    { id: 'profit_sharing', heading: 'Profit Sharing Ratio', build: () =>
      `The profit sharing ratio of the partners of ${N} will be in proportion to his contribution of ${N}.\n\nThe Profit sharing ratio of the incoming partner will be decided by the consent of all the existing Partners of ${N}.\n\nThe profit sharing ratio of the Partners may be altered subject to approval of all Partners, and alteration of the LLP Agreement to this effect and necessary compliance with all the requirements under the Act and Rules.` },
    { id: 'hdr_partners', heading: 'PARTNERS', isSectionHeader: true },
    { id: 'eligibility', heading: 'Eligibility', build: () =>
      `Any individual or body corporate may be a partner, provided that an individual shall not be capable of becoming a partner, if:\n\n- He has been found to be of unsound mind by a court of competent jurisdiction and the finding is in force;\n- He is an undischarged insolvent; or\n- He has applied to be adjudicated as an insolvent and his application is pending.` },
    { id: 'admission', heading: 'Admission of New Partner', build: () =>
      `No Person or Body Corporate may be introduced as a new Partner without the consent of all the existing Partners, and such incoming Partner shall give his prior consent to act as Partner of ${N}.` },
    { id: 'resignation_partner', heading: 'Resignation/ Retirement of Partner', build: () =>
      `Any Partner may cease to be Partner of the LLP by giving a notice in writing of not less than Sixty (60) days to the other Partners of his intention to resign/ retire as Partner.\n\nThe accounts of the LLP shall be taken as closed on the date of resignation/ retirement/ transfer of partners' rights to other persons/ person other than existing partners, and the amount found due from or payable to the Resigning/ Retiring Partner/ Partner whose rights are being transferred, shall be paid or recovered within three months of the date of resignation/ retirement. The business of the LLP with all its assets and liabilities shall be continued by the continuing Partners.` },
    { id: 'death_partner', heading: 'Death of Partner', build: () =>
      `Upon the death of any of the Partners herein the heirs, executors and administrators of such deceased Partners shall be entitled to and shall be paid the full payment in respect of the right, title and interest of such deceased Partner.` },
    { id: 'removal_partner', heading: 'Removal of Partner', build: () =>
      `No Partner can be expelled by the other Partners or a majority of Partners except in where he / she have been found guilty of carrying on any activity / business of ${N} for fraudulent purposes.\n\nA Partner may be expelled by giving a notice of thirty (30) days from the date of decision taken by the majority of Partners, after giving an opportunity to such partner, to be heard.` },
    { id: 'rights_partner', heading: 'Rights of Partner', build: () =>
      `All the Partners hereto shall have the rights, title and interest in all the assets and properties in the LLP in the proportion of their contribution to the capital.\n\nEach of the parties hereto shall be entitled to carry on their own, separate and independent business as hitherto they might be doing or they may hereafter do as they deem fit and proper and other Partners and the LLP shall have no objection thereto provided that the said Partner has intimated the said fact to the LLP before the start of the independent business and moreover he shall not use the name of the LLP to carry on the said business.\n\nEvery Partner has a right to have access to and to inspect and copy any books of account / documents, etc. of the LLP.\n\nThe LLP shall have perpetual succession, and the death, retirement or insolvency of any Partner shall not dissolve the LLP.\n\nUpon insolvency of a Partner his or her rights, title and interest in the LLP shall come to an end.` },
    { id: 'duties_partner', heading: 'Duties of Partners', build: () =>
      `Every Partner shall account to the Limited Liability Partnership for any benefit derived by him without the consent of the Limited Liability Partnership from any transaction concerning the Limited Liability Partnership, or from any use by him of the property, name or any business connection of the Limited Liability Partnership.\n\nEvery Partner shall indemnify the Limited Liability Partnership and the other existing Partners for any loss caused to it by his fraud in the conduct of the business of the Limited Liability Partnership.\n\nEach Partner shall render true accounts and full information of all things affecting the Limited Liability Partnership to any Partner or his legal representatives.\n\nIf at any time the numbers of partners of LLP is reduced below two and LLP carries on business for more than six months while the number is so reduced, the person, who is the only partner of the LLP during the time that it so carries on business after those six months and has the knowledge of the fact that it is carrying on business with him alone, shall be liable personally for the obligations of the LLP incurred during that period.\n\nNo Partner shall, without the written consent of all the other Partners of ${N}:\n\n- Employ any money, goods or effects of ${N} or pledge the credit thereof except in the ordinary course of business and upon the account or for the benefit of ${N};\n- Lend money or give credit on behalf of ${N} or have any dealings with any person, company or firm whom the other Partners have previously forbidden in writing to trust or deal with (any loss incurred through any breach hereof shall be made good to ${N} by the Partner incurring the same);\n- Enter into any bond or become surety or security with or for any person, or knowingly cause or suffer anything whereby ${N}'s property or any part thereof may be seized;\n- Assign, mortgage or charge his or her share in ${N} or any asset or property thereof, or make any other person a Partner therein;\n- Compromise, compound or (except upon payment in full) release or discharge any debt due to ${N}, except upon the written consent of all the other Partners.` },
    { id: 'registration_changes', heading: "Registration of Changes in Particulars", build: () =>
      `As per section 25 of the Limited Liability Partnership Act, 2008:\n\n- Every partner shall inform the LLP of any change in his name or address within fifteen days of such change;\n- Where a person becomes or ceases to be a partner, the LLP shall file a notice with the Registrar within thirty days from the date he becomes or ceases to be a partner; and\n- Where there is any change in the name or address of a partner, the LLP shall file a notice with the Registrar within thirty days of such change.` },
    { id: 'extent_liability', heading: 'Extent of Liability of the LLP', build: () =>
      `The LLP is not bound by anything done by a Partner in dealing with a person if:\n\n- the Partner in fact has no authority to act for the LLP in doing that particular act; and\n- the person dealing with him knows that he has no authority, or does not know or believe him to be a Partner of the LLP.` },
    { id: 'hdr_meetings', heading: 'MEETINGS', isSectionHeader: true },
    { id: 'notice_meeting', heading: 'Notice of Meeting', build: () =>
      `One or more meetings of the partners of the LLP may be held at any such time and at any such intervals as may be deemed fit by the majority of Designated partners of the LLP.\n\nThe meeting of the Partners may be called by giving 21 days prior notice to all the Partners at their residential address or by mail or by Email provided by the individual Partners in writing to the LLP. In case any Partner is a foreign resident the meeting may be conducted by serving 21 days prior notice through email. Provided that the meeting may be called at shorter notice, if majority of the Partners agree in writing to the same either before or after the meeting.` },
    { id: 'place_manner', heading: 'Place and Manner of holding Meetings', build: () =>
      `With the written Consent of all the Partners, a meeting of the Partners may be conducted through Teleconferencing.\n\nThe meeting of Partners shall ordinarily be held at the registered office of ${N} or at any other place as per the convenience and agreement of Partners.\n\nAt any time, any of a partner of ${N}, being a body corporate, it shall be deemed to be present, if it is represented by any individual holding a valid letter of authority/ power of attorney/ resolution allowing such individual to represent the body corporate in the meetings of Partners of ${N}.` },
    { id: 'quorum', heading: 'Quorum for Meetings', build: () =>
      `Two or One third whichever is higher, partners present in person shall be a quorum for a Meeting of partners of ${N}.\n\nIf, at the expiration of half an hour from the time appointed for holding a meeting of ${N} a quorum shall not be present, the meeting if convened by or upon the requisition of Partners, shall stand dissolved, but in any other case the meeting shall stand adjourned to the same day in the next week at the same time and place or to such other day at such other time and place as the majority of Designated Partners may determine, and if at such adjourned meeting a quorum is not present at the expiration of half an hour from the time appointed for holding the meeting, the Partners present shall be a quorum, and may transact, the business for which the meeting was called.` },
    { id: 'chairman', heading: 'Chairman of the meeting', build: () =>
      `The majority of the partners may, at any time, elect any person amongst them as a Chairman to chair all or any of the meetings of the Partners of ${N} held after his appointment as a Chairman.\n\nIf the majority of the partners of ${N} have not appointed any Chairman or if at any meeting, such Chairman shall not be present within fifteen minutes of the time appointed for holding such meeting then the Partners present shall elect one of their Partners to be the Chairman.` },
    { id: 'voting_meetings', heading: 'Voting at meetings of Partners', build: () =>
      `The voting for matters discussed at the meetings of Partners shall be by show of hands, unless voting by poll is demanded by the Partners.\n\nEvery Partner shall be entitled to be present and to speak and vote at such meeting, and on a show of hands every Partner present in person shall have one vote and upon a poll the voting rights of every Partner, shall be in proportion to his share of the paid-up contribution of the LLP.\n\nThe Chairman of any meeting shall be the sole judge of the validity of every vote tendered at such meeting. The Chairman present at the taking of a poll shall be the sole judge of the validity of every vote tendered at such poll.` },
    { id: 'casting_vote', heading: "Chairman's Casting Vote", build: () =>
      `In the case of any equality of votes, the Chairman shall both on a show of hands and at a poll (if any) have a casting vote in addition to the votes to which he may be entitled as a Partner.` },
    { id: 'circular_resolution', heading: 'Circular resolution', build: () =>
      `The Partners may pass the necessary resolutions in writing by way of circulating the resolution in draft together with the necessary documents and such resolutions shall be binding on all the Partners of ${N}.\n\nOn a poll taken at a meeting of Partners of ${N}, a Partner entitled to more than one vote, or other person entitled to vote for him as the case may be, need not, if he votes, use all his votes or cast in the same way all the votes he uses.\n\nThe Partners shall ensure that all decisions taken by them in meetings are recorded in the minutes within thirty (30) days of taking such decisions and are kept and maintained at the Registered Office of ${N}.` },
    { id: 'hdr_dp', heading: 'DESIGNATED PARTNERS', isSectionHeader: true },
    { id: 'appointment_dp', heading: 'Appointment of Designated Partners', dynamic: true, build: buildAppointmentDpText },
    { id: 'resignation_dp', heading: 'Resignation of Designated Partner', build: () =>
      `Any Designated Partner may resign from ${N} by giving a notice of 90 days to the LLP.\n\nThe vacant position caused due to resignation of such Designated Partner shall be filled in with the approval of majority of the partners of ${N} within a period of 3 months from the date of resignation.` },
    { id: 'removal_dp', heading: 'Removal of Designated Partner', build: () =>
      `Any Designated Partner can be expelled by the other Partners or a majority of Partners by assigning the reason for removal of Designated Partner.\n\nA Designated Partner may be expelled by giving a notice of thirty (30) days with the approval of majority of Partners, after giving an opportunity to such Designated Partner, to be heard.\n\nThe vacant position caused due to removal of such Designated Partner shall be filled in with the approval of all partners of ${N} within a period of 3 months from the date of removal.` },
    { id: 'powers_dp', heading: 'Powers of Designated Partner', build: () =>
      `The business of ${N} shall be managed jointly by the Designated Partners, who may exercise all such powers of the LLP and do all such acts and things as are not, by the Act, or this Agreement, required to be exercised only by the Partners of ${N}.\n\nThe Designated Partners shall be responsible for the doing of all acts, matters and things as are required to be done by ${N} in respect of compliance of the provisions of this Act including filing of any document, return, statement and the like report pursuant to the provisions of Limited Liability Partnership Act, 2008.\n\nThe Designated Partners shall be responsible for the doing of all acts arising out of this agreement.` },
    { id: 'remuneration_dp', heading: 'Remuneration of Designated Partners', build: () =>
      `${N} shall pay such remuneration to each of the Designated Partner as may be decided by the majority of the Partners, for rendering his services as such.\n\n${N} shall indemnify and defend its Partners and other officers from and against any and all liability in connection with claims, actions and proceedings (regardless of the outcome), judgment, loss or settlement thereof, whether civil or criminal, arising out of or resulting from their respective performances as Partners and officers of ${N}, except for the gross negligence or willful misconduct of the Partner or officer seeking indemnification.` },
    { id: 'transfer_rights', heading: 'Transfer or Assignment of rights', build: () =>
      `In case any of the Partners of ${N} desires to transfer or assign his interest in LLP, he has to offer the same to the remaining Partners by giving 15 days' notice.\n\nIn the absence of any communication by the remaining Partners the concerned Partner can transfer or assign his share in the profits of the LLP to any other person in the market by execution of an agreement/ deed in writing.\n\nSuch transfer or assignment of share in the profits of the LLP shall be communicated to all the Partners within 30 days from the date of execution of the agreement/ deed, or the effective date of transfer/ assignment, whichever is earlier.` },
    { id: 'borrowing_powers', heading: 'Borrowing Powers', build: () =>
      `${N} may, from time to time at its discretion, borrow funds from its Partners or public, banks or any financial institutions or any other individual/ body corporate, for meeting its working capital requirements, and any other administrative and strategic requirements, with the approval of all the Designated Partners, to the extent of 70 % (Seventy per cent) of the contribution available in the account of ${N} as on the date of borrowing.\n\nIn respect of the funds borrowed, ${N} may provide as a security, all or any part of the property of the Company (both present and future) including its unpaid contribution for the time being, and other securities may be made assignable free from any equities between the LLP and the person to whom the same may be issued.\n\n${N} shall maintain a register of all mortgages, hypothecation of the property of the LLP made by the LLP against the borrowings of the LLP.\n\nOn behalf of the LLP, whenever Designated Partners enter into a contract with any Government, Central, State or Local, any bank or financial institution or any person or persons (hereinafter referred to as "the appointer") for borrowing any money or for providing any guarantee or security or for technical collaboration or assistance or entering into any other arrangement whatsoever, shall have the right to appoint or nominate by a notice in writing, from the appointer, addressed to the LLP one or more Representative Officers for the LLP, for such period and upon such conditions as may be mentioned in the agreement.` },
    { id: 'auditors', heading: 'Auditors', build: () =>
      `The auditors can be appointed with the consent of majority of the Designated Partners of the LLP.\n\nIf the auditors are not appointed by the Designated Partners of the LLP, then the Auditors can be appointed with the consent of all the Partners of the LLP.\n\nThe auditors can be removed before the expiry of the term of auditors with the consent of all the Partners of the LLP.` },
    { id: 'severability', heading: 'Severability', build: () =>
      `If any provision of this agreement found invalid or illegal, the remainder of this agreement shall be binding on the partners, and shall construed as if the invalid and illegal provision in this agreement has been deleted. The partners shall use all reasonable efforts to agree any substitute provisions for invalid and illegal provision having as close as practicable, the same commercial effect.` },
    { id: 'indemnity', heading: 'Indemnity Clause', build: () =>
      `Each Partner shall:\n\n- Punctually pay and discharge his separate debts and engagements and indemnify the other Partners and the assets of ${N} against the same and all proceedings, costs, claims and demands in respect thereof;\n- Be faithful to the other Partners and at all times give and render to them true and correct accounts and information of the business and affairs of ${N} and of all transactions, matters and things relating thereto;\n- Punctually and forthwith account for and pay to ${N} all moneys, cheques and other negotiable instruments received by him for and on behalf of ${N}, and punctually credit or cause to be credited the same in the books of account of ${N};\n- Give such time and attention as may be required for the fulfilment of the objectives of ${N}'s business, all Partners being Working Partners.` },
    { id: 'misc', heading: 'Miscellaneous Provisions', dynamic: true, build: () =>
      `The books of accounts of ${N} shall be kept for the reference of all the Partners at the Registered Office of ${N} or such other place as agreed by all the Partners.\n\nThe accounting year of ${N} shall be from 1st April of the year to 31st March of subsequent year. The first accounting year shall be from the date of commencement of ${N} till 31st March of the subsequent year.\n\nThe Statement of Account & Solvency of ${N} containing the Statement of Income and Expenditure and Statement of Assets and Liabilities shall be made within six (6) months after the 31st March of the relevant accounting year and each Partner, if he agrees in writing by signing the Statement of Accounts so prepared, shall be bound by it and will not be entitled to re-open the accounts except with the consent of all the Partners.\n\nIt is expressly agreed that the bank account of ${N} shall be opened with any Scheduled Bank / s as the Partners may mutually decide and shall be operated jointly by ${dpNames()}, or as otherwise agreed by all the Partners.` },
    { id: 'waivers', heading: 'Waivers and Amendments', build: () =>
      `Notwithstanding anything said or provided herein, the Partners shall have full discretion to modify, alter, or vary the terms and conditions of this LLP Agreement, subject to the provisions of the Limited Liability Partnership Act, 2008, in any manner whatsoever as they may deem fit by mutual agreement which shall be reduced to writing and be signed by all the Partners and thereupon and the said writing shall become part of this LLP Agreement.` },
    { id: 'disputes', heading: 'Settlements of Disputes', build: () =>
      `All disputes between the Partners or between the Partner and the ${N} arising out of the Limited Liability Partnership agreement which cannot be resolved in terms of this agreement shall be referred for arbitration as per the provisions of the Arbitration and Conciliation Act, 1996 (26 of 1996).\n\nAll disputes with third parties will be resolved in Delhi Jurisdiction.` },
    { id: 'winding_up', heading: 'Winding up', build: () =>
      `${N} can be wound up with the consent of all the Partners subject to the provisions of Limited Liability Partnership Act, 2008.` },
    { id: 'whole_agreement', heading: 'Whole Agreement', build: () =>
      `This Agreement constitutes the whole agreement and understanding of the partners with respect to the subject matter of this Agreement. None of the partners has entered in to this agreement upon any representation, warranty or undertaking by or on behalf of any partner which is not expressly set out in it provided that this clause shall not exclude any liability for (or remedy in respect of) fraudulent misrepresentation.` },
    { id: 'governing_law', heading: 'Governing Law', build: () =>
      `This Agreement and any non-contractual obligations arising out of or in connection with this Agreement governed and interpreted in accordance with Indian Law.` },
    { id: 'signatures', heading: 'IN WITNESS WHEREOF', dynamic: true, build: buildSignatureText },
  ];

  const buildDefaultClauses = () => CLAUSE_DEFS.filter(c => !c.isSectionHeader).map(c => ({ id: c.id, heading: c.heading, text: c.build() }));

  const [clauses, setClauses] = useState(() => {
    if (existing?.clauses?.length) {
      // Merge saved text onto the current clause list, so a firm-wide wording
      // update to this list of headings still reaches old drafts.
      const saved = new Map(existing.clauses.map(c => [c.id, c.text]));
      return CLAUSE_DEFS.filter(c => !c.isSectionHeader).map(c => ({ id: c.id, heading: c.heading, text: saved.has(c.id) ? saved.get(c.id) : c.build() }));
    }
    return buildDefaultClauses();
  });

  const updateClause = (id, text) => setClauses(list => list.map(c => c.id === id ? { ...c, text } : c));
  const refillClause = (id) => {
    const def = CLAUSE_DEFS.find(c => c.id === id);
    if (def) setClauses(list => list.map(c => c.id === id ? { ...c, text: def.build() } : c));
  };
  const clauseText = (id) => clauses.find(c => c.id === id)?.text || '';

  const compileText = () => {
    let out = `LIMITED LIABILITY PARTNERSHIP AGREEMENT\n(As per Section 23(4) of LLP Act, 2008)\n\n\n`;
    CLAUSE_DEFS.forEach(def => {
      if (def.isSectionHeader) { out += `${def.heading}\n\n`; return; }
      const text = clauseText(def.id);
      out += def.id === 'preamble' || def.id === 'signatures' ? `${text}\n\n\n` : `${def.heading}:\n\n${text}\n\n\n`;
    });
    return out.trim() + '\n';
  };

  // ── HTML compilers — emit the legal-deed schema (doc-title / section-header
  //    / clause-heading / recital-text / party-name / sub-list / sig table)
  //    that buildWordDoc + htmlToRtf both understand. ─────────────────────
  const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  // A numbered clause: the clause carries a running number (1., 2., 3. …)
  // that hangs to the left of the operative text (matches how the deed is
  // manually drafted). The first paragraph gets the number; later
  // paragraphs are aligned continuation text; any run of "- " lines
  // becomes a lettered / numbered sub-list.
  const renderNumberedClause = (num, text, listType) => {
    let out = '';
    let firstPara = true;
    String(text || '').split(/\n\n+/).forEach(block => {
      let para = [], list = [];
      const flushPara = () => {
        if (!para.length) return;
        const inner = para.map(esc).join('<br/>');
        out += firstPara ? `<p class="clause-num"><span class="cn">${num}.</span>&nbsp;${inner}</p>` : `<p class="clause-cont">${inner}</p>`;
        firstPara = false;
        para = [];
      };
      const flushList = () => {
        if (!list.length) return;
        out += `<ol class="sub-list" type="${listType}">${list.map(l => `<li>${esc(l)}</li>`).join('')}</ol>`;
        list = [];
      };
      block.split('\n').forEach(line => {
        const m = line.match(/^\s*[-•]\s+(.*)$/);
        if (m) { flushPara(); list.push(m[1]); }
        else { flushList(); if (line.trim()) para.push(line); }
      });
      flushPara(); flushList();
    });
    if (firstPara) out = `<p class="clause-num"><span class="cn">${num}.</span></p>` + out; // clause opened with a list
    return out;
  };

  const preambleHtml = (text) => {
    let html = '';
    String(text || '').split(/\n\n+/).forEach(block => {
      const t = block.trim();
      if (!t) return;
      if (/^BETWEEN$/i.test(t)) { html += '<p style="text-align:center;font-weight:bold;margin:12px 0;">BETWEEN</p>'; return; }
      if (/^IT IS HEREBY AGREED/i.test(t)) { html += `<p class="recital-text" style="font-weight:bold;">${esc(t)}</p>`; return; }
      if (/hereinafter called the/i.test(t)) {
        let h = esc(t.replace(/\n/g, ' '));
        h = h.replace(/hereinafter called the ([A-Z][A-Z ]*PART(?:Y|IES))/, (m, p1) => `hereinafter called the <span class="party-name">${p1.trim()}</span>`);
        h = h.replace(/^((?:MR\.?\/MS\.?|MR\.?|MS\.?|M\/S\.?)\s+)?([A-Z][A-Za-z .'’-]+?)(,\s)/, (m, pre, name, tail) => `${pre || ''}<span class="party-name">${name.trim()}</span>${tail}`);
        html += `<p class="recital-text">${h}</p>`;
        return;
      }
      html += `<p class="recital-text">${esc(t).replace(/\n/g, '<br/>')}</p>`;
    });
    return html;
  };

  const signatureBlockHtml = () => {
    const roleLabel = (p) => dpList.includes(p) ? 'Designated Partner' : 'Partner';
    const witnessCell = (n) => `<td style="width:50%;text-align:left;vertical-align:top;"><strong>Witness:</strong><br>${n}. Name: _______________________<br>Address: _______________________<br>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;_______________________<br>Signature: _____________________</td>`;
    const nameCell = (p) => `<td style="width:50%;text-align:left;vertical-align:top;padding-bottom:5px;"><strong>${p ? esc(nameOf(p)) : '[Partner Name]'}</strong><br>(${p ? roleLabel(p) : 'Designated Partner'})</td>`;
    const list = partners.length ? partners : [null, null];
    let rows = '';
    for (let i = 0; i < list.length; i += 2) {
      rows += `<tr>${nameCell(list[i])}${i + 1 < list.length ? nameCell(list[i + 1]) : '<td style="width:50%;"></td>'}</tr>`;
    }
    return `<div style="page-break-inside:avoid;width:100%;margin-top:40px;">
<p style="text-align:left;margin-bottom:20px;">IN WITNESS WHEREOF the parties have put their respective hands the day and year first hereinabove written.</p>
<p style="text-align:left;margin-bottom:30px;">Signed and delivered by <br>For and on behalf of <br><strong>'${esc(N)}'</strong></p>
<table class="sig-block" style="width:100%;border-collapse:collapse;border:none;">
${rows}
<tr><td style="height:60px;"></td><td style="height:60px;"></td></tr>
<tr>${witnessCell(1)}${witnessCell(2)}</tr>
</table>
</div>`;
  };

  const compileHtml = () => {
    let out = `<div class="doc-title">LIMITED LIABILITY PARTNERSHIP AGREEMENT</div>`;
    out += `<div class="doc-subtitle">(As per Section 23(4) of LLP Act, 2008)</div>`;
    let no = 0;
    CLAUSE_DEFS.forEach(def => {
      if (def.isSectionHeader) { out += `<div class="section-header">${esc(def.heading)}</div>`; return; }
      if (def.id === 'preamble') { out += preambleHtml(clauseText('preamble')); return; }
      if (def.id === 'signatures') { out += signatureBlockHtml(); return; }
      if (def.id === 'definitions') {
        out += `<p class="clause-heading">Definitions:</p>`;
        const items = clauseText('definitions').split(/\n\n+/).map(s => s.trim()).filter(Boolean);
        out += `<ol class="def-list" type="i">${items.map(t => `<li>${esc(t).replace(/\n/g, '<br/>')}</li>`).join('')}</ol>`;
        return;
      }
      no += 1;
      out += `<p class="clause-heading">${esc(def.heading)}:</p>`;
      out += renderNumberedClause(no, clauseText(def.id), def.id === 'business' ? '1' : 'a');
    });
    return out;
  };

  const handleSave = () => {
    onSave({
      key: 'LLP_AGREEMENT', name: 'LLP Agreement', type: 'Charter', status: 'Generated',
      agreementDate, clauses,
      content: compileText(),
    });
  };

  return (
    <DraftModal
      title="LLP Agreement"
      subtitle="Sec 23(4) &amp; Rule 21(1) — every clause editable"
      colorClass="bg-gradient-to-r from-purple-600 to-purple-700"
      onClose={onClose}
      onSave={handleSave}
      filename={`LLP_Agreement_${item.proposed_name || 'Draft'}`}
      wordHtml={compileHtml()}
      pdfHtml={compileHtml()}
    >
      <div className="flex items-end justify-between gap-3">
        <div className="flex-1">
          <p className="text-xs font-bold text-purple-700 mb-2">Agreement Date</p>
          <input type="date" value={agreementDate} onChange={e => setAgreementDate(e.target.value)} className={ic} />
        </div>
        <button
          type="button"
          onClick={() => { if (window.confirm('Replace every clause with the current standard wording? Any edits you have made will be lost.')) setClauses(buildDefaultClauses()); }}
          title="Discard all edits and reload every clause from the firm's standard wording (also re-applies the latest formatting)"
          className="flex items-center gap-1.5 pl-2.5 pr-3 py-2 rounded-lg border border-purple-200 bg-purple-50 text-purple-700 text-xs font-semibold hover:bg-purple-100 hover:border-purple-300 active:scale-95 transition-all whitespace-nowrap"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M23 4v6h-6" /><path d="M1 20v-6h6" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
          Reset all clauses to standard
        </button>
      </div>

      {CLAUSE_DEFS.map(def => {
        if (def.isSectionHeader) {
          return (
            <div key={def.id} className="pt-2 pb-1 border-t border-purple-100">
              <p className="text-xs font-bold text-purple-900 tracking-wide">{def.heading}</p>
            </div>
          );
        }
        const rows = def.id === 'preamble' || def.id === 'contribution' || def.id === 'appointment_dp' || def.id === 'signatures' ? 8 : 5;
        return (
          <div key={def.id}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold text-purple-700">{def.heading}</p>
              {def.dynamic && (
                <button
                  type="button"
                  onClick={() => refillClause(def.id)}
                  title="Regenerate this clause from the latest case data"
                  className="flex items-center gap-1.5 pl-2.5 pr-3 py-1 rounded-full border border-purple-200 bg-purple-50 text-purple-700 text-xs font-semibold hover:bg-purple-100 hover:border-purple-300 active:scale-95 transition-all"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 4v6h-6" /><path d="M1 20v-6h6" />
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                  </svg>
                  Refill from case data
                </button>
              )}
            </div>
            <textarea
              rows={rows}
              value={clauseText(def.id)}
              onChange={e => updateClause(def.id, e.target.value)}
              className={ta}
            />
          </div>
        );
      })}
    </DraftModal>
  );
}


// ── AOA Modal — Table F, fully editable per section ─────────────────────────
function AoaModal({ item, nameApproval, capitalStructure, registeredOfficeData, casePeople, existing, onClose, onSave }) {
  const ic = "w-full border border-gray-200 rounded-lg px-3 py-2.5 text-xs font-mono focus:outline-none focus:border-blue-500 bg-white";
  const approvedName = nameApproval.approved_name || nameApproval.proposed_name_1 || '';
  const isPrivate = item.incorporation_type === 'Private Limited Company' || item.incorporation_type === 'One Person Company';

  const [companyName, setCompanyName] = useState(existing?.companyName || approvedName);

  const directors = (casePeople || []).filter(p => p.din);
  const equityClasses = capitalStructure?.equity_classes || [];
  const totalAuthCapital = equityClasses.reduce((s, c) => s + (Number(c.authorized_shares) || 0) * (Number(c.authorized_nominal_value) || 0), 0);

  const defaultSections = {
    interpretation: `(1) In these regulations — (a) "the Act" means the Companies Act, 2013; (b) "the Company" means ${companyName || '[Company Name]'}; (c) "the Seal" means the Common Seal of the Company, if any.\n(2) Words importing the singular number include the plural, and vice versa.`,
    privateRestrictions: isPrivate ? `The Company is a Private Company within the meaning of Section 2(68) of the Act:\n(i) the right to transfer shares is restricted;\n(ii) the number of members is limited to 200 (excluding present and former employees who are/were members);\n(iii) any invitation to the public to subscribe for securities is prohibited.` : `As a Public Company, the Company is not subject to the restrictions in Section 2(68).`,
    shareCapital: `The Authorised Share Capital of the Company is ₹${totalAuthCapital.toLocaleString('en-IN')} as stated in Clause V of the Memorandum, with power to increase, consolidate, sub-divide, or reduce the capital subject to the Act.\nEvery member shall be entitled to a share certificate under the seal or digital authentication of the Company.`,
    lienAndCalls: `The Company shall have a first and paramount lien on every share for all monies called or payable in respect thereof.\nThe Board may make calls upon members in respect of any monies unpaid on their shares, subject to the terms of allotment.`,
    transferTransmission: `The instrument of transfer of shares shall be in writing and executed by or on behalf of both the transferor and the transferee.\nOn the death of a member, the survivor(s) or legal representative shall be the only persons recognised as having title to the shares.`,
    generalMeetings: `General meetings shall be held in accordance with Sections 96 to 122 of the Act.\nThe quorum, notice period, and voting procedure shall follow the provisions of the Act unless the Articles specify otherwise.`,
    directors: `The number of Directors shall not be less than the minimum, and not more than the maximum, prescribed under the Act.\nThe First Directors of the Company are:\n${directors.length > 0 ? directors.map((d, i) => `${i + 1}. ${d.full_name} (DIN: ${d.din})`).join('\n') : '[Set director DIN in Promoters & Partners tab]'}`,
    boardProceedings: `The Board may meet for the conduct of business, adjourn, and regulate its meetings as it thinks fit.\nThe quorum for a Board meeting shall be one-third of its total strength or two Directors, whichever is higher.`,
    seal: `The Company may have a Common Seal, the use of which shall be regulated by the Board. [Note: use of a common seal is optional since the Companies (Amendment) Act, 2015.]`,
    dividendsAccounts: `The Company in general meeting may declare dividends, but no dividend shall exceed the amount recommended by the Board.\nProper books of account shall be kept as required under Section 128 of the Act.`,
    windingUp: `If the Company is wound up, the liquidator may, with the sanction of a special resolution, divide the assets among the members in kind, subject to the Act.`,
    indemnity: `Every officer of the Company shall be indemnified out of the assets of the Company against liability incurred in defending proceedings, civil or criminal, in which judgment is given in his favour.`,
  };

  const [sections, setSections] = useState(existing?.sections || defaultSections);
  const updateSection = (key, value) => setSections(s => ({ ...s, [key]: value }));

  const sectionOrder = [
    ['interpretation', '1. Interpretation'],
    ['privateRestrictions', '2. Private Company Restrictions (Sec 2(68))'],
    ['shareCapital', '3. Share Capital & Variation of Rights'],
    ['lienAndCalls', '4. Lien & Calls on Shares'],
    ['transferTransmission', '5. Transfer & Transmission of Shares'],
    ['generalMeetings', '6. General Meetings & Proceedings'],
    ['directors', '7. Directors'],
    ['boardProceedings', '8. Proceedings of the Board'],
    ['seal', '9. The Seal'],
    ['dividendsAccounts', '10. Dividends & Accounts'],
    ['windingUp', '11. Winding Up'],
    ['indemnity', '12. Indemnity'],
  ];

  const buildSubscriberTable = (forWord) => {
    const subs = casePeople || [];
    if (subs.length === 0) return forWord ? '<p><em>[No subscribers added yet]</em></p>' : '[No subscribers added yet]';
    if (forWord) {
      const rows = subs.map((p, i) => `<tr><td>${i + 1}</td><td>${p.full_name || ''}</td><td>${p.shares_or_contribution || 0}</td><td style="height:40px"></td></tr>`).join('');
      return `<table><tr><th>No.</th><th>Name</th><th>Shares</th><th>Signature</th></tr>${rows}</table>`;
    }
    return subs.map((p, i) => `${i + 1}. ${p.full_name} — ${p.shares_or_contribution || 0} shares`).join('\n');
  };

  const compileText = () => {
    let out = `THE COMPANIES ACT, 2013\nARTICLES OF ASSOCIATION\nOF\n${(companyName || '[COMPANY NAME]').toUpperCase()}\n\nTABLE F — SCHEDULE I ADAPTATION\n\n`;
    sectionOrder.forEach(([key, label]) => {
      out += `${label}\n${sections[key]}\n\n`;
    });
    out += `SUBSCRIBERS TO THE ARTICLES:\n${buildSubscriberTable(false)}`;
    return out;
  };

  const compileHtml = () => {
    let out = `<h1>THE COMPANIES ACT, 2013</h1><h2>ARTICLES OF ASSOCIATION OF ${(companyName || '[COMPANY NAME]').toUpperCase()}</h2><p style="text-align:center"><b>TABLE F — SCHEDULE I ADAPTATION</b></p>`;
    sectionOrder.forEach(([key, label]) => {
      out += `<p><b>${label}</b></p>${textToHtml(sections[key])}`;
    });
    out += `<h2>SUBSCRIBERS TO THE ARTICLES</h2>${buildSubscriberTable(true)}`;
    return out;
  };

  const handleSave = () => {
    onSave({
      key: 'AOA', name: 'Articles of Association (AOA)', type: 'Regulations', status: 'Generated',
      companyName, sections,
      content: compileText(),
    });
  };

  return (
    <DraftModal
      title="Articles of Association"
      subtitle="Table F — Schedule I, fully editable"
      colorClass="bg-gradient-to-r from-purple-600 to-purple-700"
      onClose={onClose}
      onSave={handleSave}
      filename={`AOA_${item.proposed_name || 'Draft'}`}
      wordHtml={compileHtml()}
      pdfHtml={compileHtml()}
    >
      <div>
        <p className="text-xs font-bold text-purple-700 mb-2">Company Name</p>
        {approvedName ? (
          <input readOnly value={companyName} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-purple-50 text-gray-700" />
        ) : (
          <input value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="Company name" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm" />
        )}
      </div>

      <p className="text-xs text-gray-400 -mt-2">Every section below is fully editable — adjust wording as needed for this case.</p>

      {sectionOrder.map(([key, label]) => (
        <div key={key}>
          <p className="text-xs font-bold text-purple-700 mb-2">{label}</p>
          <textarea rows="3" value={sections[key]} onChange={e => updateSection(key, e.target.value)} className={ic} />
        </div>
      ))}

      <div>
        <p className="text-xs font-bold text-purple-700 mb-2">Subscribers to the Articles</p>
        <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-xs text-gray-600 whitespace-pre-line max-h-32 overflow-y-auto">
          {buildSubscriberTable(false)}
        </div>
      </div>
    </DraftModal>
  );
}

// ── INC-14 Modal — single professional declaration ──────────────────────────
function Inc14Modal({ item, nameApproval, existing, onClose, onSave }) {
  const ic = "w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-blue-500 bg-white";
  const approvedName = nameApproval.approved_name || nameApproval.proposed_name_1 || '';

  const [companyName, setCompanyName] = useState(existing?.companyName || approvedName);
  const [professionalType, setProfessionalType] = useState(existing?.professionalType || 'Company Secretary in practice');
  const [professionalName, setProfessionalName] = useState(existing?.professionalName || '');
  const [membershipNo, setMembershipNo] = useState(existing?.membershipNo || '');
  const [place, setPlace] = useState(existing?.place || '');

  const professionalOptions = [
    'an advocate',
    'a Chartered Accountant in India',
    'a Cost Accountant in India',
    'a Company Secretary in practice in India',
  ];

  const compileText = () => `FORM NO. INC-14\nDECLARATION\n[Pursuant to section 7(1)(b) and rule 19(3)(b) of the Companies (Incorporation) Rules, 2014]\n\nName of the Company: ${companyName || '[Company Name]'}\n\nI, ${professionalName || '[Professional Name]'}, ${professionalType} who is engaged in the formation of the company, do hereby declare that:\n\n(a) the draft memorandum and articles of association have been drawn up in conformity with the provisions of section 8 and rules made thereunder; and\n(b) all the requirements of Companies Act, 2013 and the rules made thereunder relating to registration of the company under section 8 of the Act and matters precedent or incidental thereto have been complied with.\n\nDate: ___________________          Signature: ___________________\nPlace: ${place || '___________________'}          Membership No.: ${membershipNo || '___________________'}`;

  const compileHtml = () => `<h1>FORM NO. INC-14</h1><h2>DECLARATION</h2><p style="text-align:center">[Pursuant to section 7(1)(b) and rule 19(3)(b) of the Companies (Incorporation) Rules, 2014]</p>
<p><b>Name of the Company:</b> ${companyName || '[Company Name]'}</p>
<p>I, ${professionalName || '[Professional Name]'}, ${professionalType} who is engaged in the formation of the company, do hereby declare that:</p>
<p>(a) the draft memorandum and articles of association have been drawn up in conformity with the provisions of section 8 and rules made thereunder; and</p>
<p>(b) all the requirements of Companies Act, 2013 and the rules made thereunder relating to registration of the company under section 8 of the Act and matters precedent or incidental thereto have been complied with.</p>
<p>Date: ___________________ &nbsp;&nbsp;&nbsp; Signature: ___________________</p>
<p>Place: ${place || '___________________'} &nbsp;&nbsp;&nbsp; Membership No.: ${membershipNo || '___________________'}</p>`;

  const handleSave = () => {
    onSave({
      key: 'INC14', name: 'Form INC-14 (Professional Declaration)', type: 'Declaration', status: 'Generated',
      companyName, professionalType, professionalName, membershipNo, place,
      content: compileText(),
    });
  };

  return (
    <DraftModal
      title="Form INC-14"
      subtitle="Declaration by practicing professional"
      colorClass="bg-gradient-to-r from-amber-600 to-amber-700"
      onClose={onClose}
      onSave={handleSave}
      filename={`INC14_${item.proposed_name || 'Draft'}`}
      wordHtml={compileHtml()}
      pdfHtml={compileHtml()}
    >
      <div>
        <p className="text-xs font-bold text-amber-700 mb-2">Company Name</p>
        {approvedName ? (
          <input readOnly value={companyName} className={`${ic} bg-amber-50 text-gray-700`} />
        ) : (
          <input value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="Company name" className={ic} />
        )}
      </div>

      <div>
        <p className="text-xs font-bold text-amber-700 mb-2">Professional Type</p>
        <div className="grid grid-cols-2 gap-2">
          {professionalOptions.map(opt => (
            <button
              key={opt}
              type="button"
              onClick={() => setProfessionalType(opt)}
              className={`px-3 py-2 rounded-lg border text-xs font-medium text-left ${professionalType === opt ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-gray-600 border-gray-200'}`}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-xs font-bold text-amber-700 mb-2">Professional Name</p>
          <input value={professionalName} onChange={e => setProfessionalName(e.target.value)} placeholder="Full name" className={ic} />
        </div>
        <div>
          <p className="text-xs font-bold text-amber-700 mb-2">Membership No.</p>
          <input value={membershipNo} onChange={e => setMembershipNo(e.target.value)} className={ic} />
        </div>
      </div>

      <div>
        <p className="text-xs font-bold text-amber-700 mb-2">Place</p>
        <input value={place} onChange={e => setPlace(e.target.value)} className={ic} />
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-500 whitespace-pre-line font-mono max-h-40 overflow-y-auto">
        {compileText()}
      </div>
    </DraftModal>
  );
}

// ── INC-15 Modal — one declaration per subscriber/applicant ─────────────────
function Inc15Modal({ item, nameApproval, casePeople, existing, onClose, onSave }) {
  const approvedName = nameApproval.approved_name || nameApproval.proposed_name_1 || '';
  const [companyName, setCompanyName] = useState(existing?.[0]?.companyName || approvedName);
  const [selectedId, setSelectedId] = useState((casePeople && casePeople[0]?.id) || null);

  const ic = "w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-blue-500 bg-white";
  const selectedPerson = (casePeople || []).find(p => p.id === selectedId);

  const buildText = (person) => `FORM NO. INC-15\nDECLARATION\n[Pursuant to rule 19(3)(d) of the Companies (Incorporation) Rules, 2014]\n\nIn connection with the application of ${companyName || '[Proposed Company Name]'} for a licence under section 8 of the Companies Act, 2013, I, ${person?.full_name || '[Name]'}, do hereby declare that —\n\n(a) the draft memorandum and articles of association have been drawn up in conformity with the provisions of section 8 and rules made thereunder; and\n(b) all the requirements of the Act and the rules made thereunder relating to registration of the company under section 8 and matters incidental or supplemental thereto have been complied with;\n\nand I make this solemn declaration conscientiously believing the same to be true.\n\nPlace: ___________________          Signature: ___________________\nDate: ___________________          Name: ${person?.full_name || ''}\n                                    Address: ${(person?.present_address?.address) || person?.address || ''}`;

  const buildHtml = (person) => `<h1>FORM NO. INC-15</h1><h2>DECLARATION</h2><p style="text-align:center">[Pursuant to rule 19(3)(d) of the Companies (Incorporation) Rules, 2014]</p>
<p>In connection with the application of ${companyName || '[Proposed Company Name]'} for a licence under section 8 of the Companies Act, 2013, I, ${person?.full_name || '[Name]'}, do hereby declare that —</p>
<p>(a) the draft memorandum and articles of association have been drawn up in conformity with the provisions of section 8 and rules made thereunder; and</p>
<p>(b) all the requirements of the Act and the rules made thereunder relating to registration of the company under section 8 and matters incidental or supplemental thereto have been complied with;</p>
<p>and I make this solemn declaration conscientiously believing the same to be true.</p>
<p>Place: ___________________ &nbsp;&nbsp;&nbsp; Signature: ___________________</p>
<p>Date: ___________________ &nbsp;&nbsp;&nbsp; Name: ${person?.full_name || ''}</p>
<p>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; Address: ${(person?.present_address?.address) || person?.address || ''}</p>`;

  const handleSaveOne = () => {
    if (!selectedPerson) return;
    onSave(selectedPerson.id, {
      key: `INC15_${selectedPerson.id}`, name: `Form INC-15 — ${selectedPerson.full_name}`, type: 'Declaration', status: 'Generated',
      companyName, personId: selectedPerson.id, personName: selectedPerson.full_name,
      content: buildText(selectedPerson),
    });
  };

  const handleSaveAll = () => {
    (casePeople || []).forEach(person => {
      onSave(person.id, {
        key: `INC15_${person.id}`, name: `Form INC-15 — ${person.full_name}`, type: 'Declaration', status: 'Generated',
        companyName, personId: person.id, personName: person.full_name,
        content: buildText(person),
      });
    });
  };

  return (
    <DraftModal
      title="Form INC-15"
      subtitle="Declaration by each subscriber / applicant"
      colorClass="bg-gradient-to-r from-teal-600 to-teal-700"
      onClose={onClose}
      onSave={handleSaveOne}
      filename={`INC15_${selectedPerson?.full_name || 'Draft'}`}
      wordHtml={buildHtml(selectedPerson)}
      pdfHtml={buildHtml(selectedPerson)}
    >
      <div>
        <p className="text-xs font-bold text-teal-700 mb-2">Company Name</p>
        {approvedName ? (
          <input readOnly value={companyName} className={`${ic} bg-teal-50 text-gray-700`} />
        ) : (
          <input value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="Company name" className={ic} />
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-bold text-teal-700">Select Subscriber</p>
          <button onClick={handleSaveAll} className="text-xs text-teal-600 hover:underline font-medium">
            Generate for all {(casePeople || []).length} subscribers
          </button>
        </div>
        {(casePeople || []).length === 0 ? (
          <p className="text-xs text-gray-400 border border-dashed border-gray-200 rounded-lg p-3 text-center">
            No subscribers added yet — add them in Promoters &amp; Partners tab.
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {(casePeople || []).map(p => (
              <button
                key={p.id}
                onClick={() => setSelectedId(p.id)}
                className={`px-3 py-2 rounded-lg border text-xs font-medium text-left ${selectedId === p.id ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-600 border-gray-200'}`}
              >
                {p.full_name}
              </button>
            ))}
          </div>
        )}
      </div>

      {selectedPerson && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-500 whitespace-pre-line font-mono max-h-40 overflow-y-auto">
          {buildText(selectedPerson)}
        </div>
      )}
    </DraftModal>
  );
}

// ── Main Drafting Tab ────────────────────────────────────────────────────────
function DraftingTab({ item, casePeople, nameApproval, capitalStructure, registeredOfficeData, opcNomineeData, drafts, setDrafts, selectedDraft, setSelectedDraft }) {
  const isLLP = item.incorporation_type === 'LLP Incorporation';
  const isSection8 = item.incorporation_type === 'Section 8 Company';
  const [openModal, setOpenModal] = useState(null);

  const getDraft = (key) => drafts.find(d => d.key === key);
  const inc15Count = drafts.filter(d => d.key?.startsWith('INC15_')).length;

  const saveDraft = (data) => {
    setDrafts(prev => {
      const existing = prev.find(d => d.key === data.key);
      const entry = { id: existing?.id || `${Date.now()}-${data.key}`, remarks: existing?.remarks || '', ...data };
      return existing ? prev.map(d => d.key === data.key ? entry : d) : [...prev, entry];
    });
    setOpenModal(null);
  };

  const saveInc15 = (personId, data) => {
    setDrafts(prev => {
      const existing = prev.find(d => d.key === data.key);
      const entry = { id: existing?.id || `${Date.now()}-${data.key}`, remarks: existing?.remarks || '', ...data };
      return existing ? prev.map(d => d.key === data.key ? entry : d) : [...prev, entry];
    });
  };

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
        <h4 className="text-base font-bold text-[#0f1f45] mb-1">Drafting Workspace</h4>
        <p className="text-sm text-gray-400 mb-4">
          Click a document to open its generator — auto-filled from data already captured across other tabs.
        </p>

        <div className="grid grid-cols-4 gap-4">
          {isLLP ? (
            <DraftCard
              icon="📄" title="LLP Agreement" description="Sec 23 & Schedule I"
              colorClass="border-purple-200 bg-purple-50 hover:border-purple-300"
              status={getDraft('LLP_AGREEMENT') ? 'Generated' : null}
              onClick={() => setOpenModal('llp_agreement')}
            />
          ) : (
            <>
              <DraftCard
                icon="📜" title="MOA" description={isSection8 ? 'Section 8 format' : 'Table A'}
                colorClass="border-blue-200 bg-blue-50 hover:border-blue-300"
                status={getDraft('MOA') ? 'Generated' : null}
                onClick={() => setOpenModal('moa')}
              />
              <DraftCard
                icon="📋" title="AOA" description="Table F — editable"
                colorClass="border-purple-200 bg-purple-50 hover:border-purple-300"
                status={getDraft('AOA') ? 'Generated' : null}
                onClick={() => setOpenModal('aoa')}
              />
            </>
          )}

          {isSection8 && (
            <>
              <DraftCard
                icon="✅" title="INC-14" description="Professional declaration"
                colorClass="border-amber-200 bg-amber-50 hover:border-amber-300"
                status={getDraft('INC14') ? 'Generated' : null}
                onClick={() => setOpenModal('inc14')}
              />
              <DraftCard
                icon="✍️" title="INC-15" description="Per-subscriber declaration"
                colorClass="border-teal-200 bg-teal-50 hover:border-teal-300"
                status={inc15Count > 0 ? `${inc15Count} of ${casePeople.length}` : null}
                onClick={() => setOpenModal('inc15')}
              />
            </>
          )}
        </div>
      </div>

      {openModal === 'moa' && (
        <MoaModal
          item={item} nameApproval={nameApproval} capitalStructure={capitalStructure}
          registeredOfficeData={registeredOfficeData} opcNomineeData={opcNomineeData} casePeople={casePeople}
          existing={getDraft('MOA')} onClose={() => setOpenModal(null)} onSave={saveDraft}
        />
      )}
      {openModal === 'llp_agreement' && (
        <LlpAgreementModal
          item={item} nameApproval={nameApproval} capitalStructure={capitalStructure}
          registeredOfficeData={registeredOfficeData} casePeople={casePeople}
          existing={getDraft('LLP_AGREEMENT')} onClose={() => setOpenModal(null)} onSave={saveDraft}
        />
      )}
      {openModal === 'aoa' && (
        <AoaModal
          item={item} nameApproval={nameApproval} capitalStructure={capitalStructure}
          registeredOfficeData={registeredOfficeData} casePeople={casePeople}
          existing={getDraft('AOA')} onClose={() => setOpenModal(null)} onSave={saveDraft}
        />
      )}
      {openModal === 'inc14' && (
        <Inc14Modal
          item={item} nameApproval={nameApproval}
          existing={getDraft('INC14')} onClose={() => setOpenModal(null)} onSave={saveDraft}
        />
      )}
      {openModal === 'inc15' && (
        <Inc15Modal
          item={item} nameApproval={nameApproval} casePeople={casePeople}
          existing={drafts.filter(d => d.key?.startsWith('INC15_'))}
          onClose={() => setOpenModal(null)} onSave={saveInc15}
        />
      )}
    </div>
  );
}


// ── Reusable icon badge for box headers ─────────────────────────────────
function IconBadge({ color, children }) {
  return (
    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${color}`}>
      {children}
    </div>
  );
}

// ── Standalone compliance box — MUST live outside PostIncorporationTab so
//    React doesn't recreate it (and lose input/date-picker focus) on every
//    keystroke. The "add task" draft state lives locally inside this
//    component so typing never touches the parent checklist at all.
function ComplianceBox({ title, subtitle, iconBg, iconColor, icon, rows, showSrn, groupKey, accentBorder, statusColors, updateChecklist, removeChecklistItem, getUrgency, onAddItem, headerNote, onNavigate, companyId }) {
  const [draft, setDraft] = useState({ task: '', date_basis: 'incorporation', days_offset: '' });

  const handleAdd = () => {
    if (!draft.task.trim()) return;
    onAddItem(groupKey, draft);
    setDraft({ task: '', date_basis: 'incorporation', days_offset: '' });
  };

  return (
    <div className={`bg-white rounded-xl border border-gray-100 shadow-sm overflow-visible border-l-4 ${accentBorder}`}>
      <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
        <IconBadge color={iconBg}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2">{icon}</svg>
        </IconBadge>
        <div>
          <h4 className="text-sm font-bold text-[#0f1f45]">{title}</h4>
          <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>
        </div>
      </div>

      {headerNote && (
        <p className="px-5 py-2 text-[11px] text-gray-400 bg-gray-50/50 border-b border-gray-100">{headerNote}</p>
      )}

      {rows.length === 0 ? (
        <div className="py-10 text-center text-gray-400 text-sm">
          No items yet — click "Generate Post Compliance" above, or add one below.
        </div>
      ) : (
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50/60">
              {['Task', 'Basis', 'Due Date', 'Urgency', 'Status', ...(showSrn ? ['SRN'] : []), 'Remarks', ''].map(h => (
                <th key={h} className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(row => {
              const urgency = getUrgency(row);
              return (
                <tr key={row.id} className="border-b border-gray-50 hover:bg-gray-50/60">
                  <td className="px-4 py-3">
                    <p className="text-sm font-semibold text-gray-800">{row.task}</p>
                    <p className="text-[11px] text-gray-400 mt-0.5">{row.due_logic}</p>
                    {row.date_basis === 'first_bm_direct' && !row.due_date && onNavigate && companyId && (
                      <button
                        onClick={() => onNavigate('registers', { registerId: 'board-meetings', companyId })}
                        className="text-[11px] text-purple-600 hover:underline font-medium mt-1"
                      >
                        Record in Board Meeting Register →
                      </button>
                    )}
                    {row.task === 'Appoint First Statutory Auditor' && onNavigate && (
                      <button
                        onClick={() => onNavigate('resolutions')}
                        className="text-[11px] text-purple-600 hover:underline font-medium mt-1 ml-2"
                      >
                        Prepare Resolution →
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {row.date_basis === 'first_bm_direct' ? 'From Register' : row.date_basis === 'first_bm' ? 'First BM' : 'Incorporation'}
                    {row.date_basis !== 'first_bm_direct' && ` +${row.days_offset}d`}
                  </td>
                  <td className="px-4 py-3">
                    {row.date_basis === 'first_bm_direct' ? (
                      <span className="text-xs text-gray-600">
                        {row.due_date ? new Date(row.due_date).toLocaleDateString('en-IN') : '—'}
                      </span>
                    ) : (
                      <input
                        type="date"
                        value={row.due_date || ''}
                        onChange={e => updateChecklist(row.id, 'due_date', e.target.value)}
                        className="w-36 border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-blue-500"
                      />
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[11px] ${urgency.color}`}>{urgency.label}</span>
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={row.status}
                      onChange={e => updateChecklist(row.id, 'status', e.target.value)}
                      className={`px-2 py-1 rounded-full text-[11px] font-medium border-0 focus:outline-none ${statusColors[row.status]}`}
                    >
                      <option>Pending</option>
                      <option>In Progress</option>
                      <option>Completed</option>
                      <option>Not Applicable</option>
                    </select>
                  </td>
                  {showSrn && (
                    <td className="px-4 py-3">
                      <input
                        value={row.srn || ''}
                        onChange={e => updateChecklist(row.id, 'srn', e.target.value.toUpperCase())}
                        placeholder="SRN"
                        className="w-24 border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-blue-500 uppercase"
                      />
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <input
                      value={row.remarks || ''}
                      onChange={e => updateChecklist(row.id, 'remarks', e.target.value)}
                      placeholder="Note"
                      className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-blue-500"
                    />
                  </td>
                  <td className="px-2 py-3">
                    <button onClick={() => removeChecklistItem(row.id)} className="text-red-400 hover:text-red-600 text-xs">✕</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* Add compliance row — draft state is local to this component */}
      <div className="px-4 py-3 bg-gray-50/50 border-t border-gray-100 flex items-center gap-2">
        <input
          value={draft.task}
          onChange={e => setDraft(d => ({ ...d, task: e.target.value }))}
          placeholder="+ Add compliance task"
          className="flex-1 border border-dashed border-gray-300 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:border-blue-500 bg-white"
        />
        <select
          value={draft.date_basis}
          onChange={e => setDraft(d => ({ ...d, date_basis: e.target.value }))}
          className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none"
        >
          <option value="incorporation">From Incorporation</option>
          <option value="first_bm">From First BM</option>
        </select>
        <input
          type="number"
          value={draft.days_offset}
          onChange={e => setDraft(d => ({ ...d, days_offset: e.target.value }))}
          placeholder="Days"
          className="w-16 border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none"
        />
        <button
          onClick={handleAdd}
          className="px-3 py-1.5 rounded-lg text-white text-xs font-medium bg-[#0f1f45]"
        >
          Add
        </button>
      </div>
    </div>
  );
}

// ── Standalone row for updating a director's DIN once allotted post-
//    incorporation — a real update to the Company Master director record,
//    not just local state. Kept as a top-level component (not nested) so
//    the DIN input doesn't lose focus on every keystroke.
function PendingDinRow({ person, isLLP, companyId, directorId, onUpdated }) {
  const [dinValue, setDinValue] = useState(person.post_incorporation_din || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleUpdate = async () => {
    if (!dinValue.trim()) return;
    setSaving(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      if (directorId) {
        const res = await fetch(`${BASE}/api/mca/companies/${companyId}/directors/${directorId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            director_name: person.full_name,
            din: dinValue,
            pan: person.pan_number || '',
            email: person.email || '',
            mobile: person.primary_mobile || '',
            designation: person.designation || 'Director',
            din_kyc_status: 'Filed',
            is_active: true,
          }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.message || 'Could not update director record');
        }
      }
      onUpdated(dinValue);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const isDone = !!person.post_incorporation_din;

  return (
    <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${isDone ? 'bg-green-50 border-green-200' : 'bg-white border-amber-200'}`}>
      <div className="flex-1">
        <span className="text-sm text-gray-700">{person.full_name} <span className="text-xs text-gray-400">({person.designation || 'Director'})</span></span>
        {person.post_incorporation_din_date && (
          <p className="text-[11px] text-green-600">Logged on {new Date(person.post_incorporation_din_date).toLocaleDateString('en-IN')}</p>
        )}
      </div>
      <input
        value={dinValue}
        onChange={e => setDinValue(e.target.value)}
        placeholder={isLLP ? 'DPIN' : 'DIN'}
        className={`w-32 border rounded-lg px-2 py-1.5 text-xs focus:outline-none ${isDone ? 'border-green-300 focus:border-green-500' : 'border-gray-200 focus:border-blue-500'}`}
      />
      <button
        onClick={handleUpdate}
        disabled={saving || !dinValue.trim()}
        className={`px-3 py-1.5 rounded-lg text-white text-xs font-medium disabled:opacity-50 ${isDone ? 'bg-green-600' : 'bg-[#0f1f45]'}`}
      >
        {saving ? 'Updating…' : isDone ? 'Update' : 'Update'}
      </button>
      {error && <span className="text-xs text-red-500">{error}</span>}
      {!directorId && <span className="text-xs text-gray-400 italic">Not yet in Company Master — will apply once moved</span>}
    </div>
  );
}

function PostIncorporationTab({ item, postIncorpData, setPostIncorpData, checklist, setChecklist, registeredOfficeData, filingData, setFilingData, nameApproval, capitalStructure, casePeople, setCasePeople, onNavigate }) {
  const isLLP = item.incorporation_type === 'LLP Incorporation';
  const isSection8 = item.incorporation_type === 'Section 8 Company';
  const isNidhi = item.incorporation_type === 'Nidhi Company';
  const isProducer = item.incorporation_type === 'Producer Company';

  const ic = "w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-blue-500 bg-white";

  const update = (field, value) => setPostIncorpData(prev => ({ ...prev, [field]: value }));
  const updateFiling = (field, value) => setFilingData(prev => ({ ...prev, [field]: value }));

  // ── Create Company Client — creates a REAL ERP client record, not just a
  //    flag. This is the linked ERP Client that Move to Company Master below
  //    now requires. ──────────────────────────────────────────────────────
  const [creatingClient, setCreatingClient] = useState(false);
  const [clientError, setClientError] = useState('');

  // Never trust the locally-stored company_client_id blindly — if the client
  // was deleted from the ERP Clients module outside this flow, our local
  // flag would still say "created" forever. Verify against the real backend
  // by PAN every time this loads, same pattern as the Company Master check
  // below, and heal a stale/broken pointer automatically.
  const [verifiedClientId, setVerifiedClientId] = useState(undefined); // undefined = still checking
  useEffect(() => {
    if (!postIncorpData.pan) { setVerifiedClientId(null); return; }
    const token = localStorage.getItem('token');
    fetch(`${BASE}/api/clients/search?q=${encodeURIComponent(postIncorpData.pan)}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(list => {
        const match = Array.isArray(list) ? list.find(c => c.pan && c.pan === postIncorpData.pan) : null;
        setVerifiedClientId(match ? match.id : null);
        if (match && match.id !== postIncorpData.company_client_id) {
          setPostIncorpData(prev => ({ ...prev, company_client_id: match.id, company_client_created: true }));
        } else if (!match && postIncorpData.company_client_id) {
          setPostIncorpData(prev => ({ ...prev, company_client_id: null, company_client_created: false }));
        }
      })
      .catch(() => setVerifiedClientId(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postIncorpData.pan]);

  const CLIENT_TYPE_MAP = {
    'LLP Incorporation': 'llp',
    'Private Limited Company': 'private_limited',
    'Public Limited Company': 'public_limited',
    'One Person Company': 'private_limited',
    'Section 8 Company': 'private_limited',
    'Nidhi Company': 'private_limited',
    'Producer Company': 'other',
  };

  // Company Master uses its own vocabulary for company_type (see
  // CompanyMaster.js's Company Type dropdown and its isLLPView check
  // `company_type === 'LLP'`) — every value here matches this module's
  // incorporation_type verbatim EXCEPT LLP, which Company Master spells
  // 'LLP' rather than 'LLP Incorporation'. Sending the raw incorporation_type
  // straight through silently mismatched that one case: the Company Type
  // dropdown fell back to its first option (Private Limited Company) and
  // every LLP-specific tab (Partners, Capital Contribution, Contribution
  // Transfers, Partner Meetings) never activated. Always go through this map.
  const COMPANY_MASTER_TYPE_MAP = {
    'LLP Incorporation': 'LLP',
    'Private Limited Company': 'Private Limited Company',
    'Public Limited Company': 'Public Limited Company',
    'One Person Company': 'One Person Company',
    'Section 8 Company': 'Section 8 Company',
    'Nidhi Company': 'Nidhi Company',
    'Producer Company': 'Producer Company',
  };

  const createCompanyClient = async () => {
    setCreatingClient(true);
    setClientError('');
    try {
      const token = localStorage.getItem('token');
      const authHeaders = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
      const addr = registeredOfficeData?.correspondence_address;
      const companyName = nameApproval?.approved_name || nameApproval?.proposed_name_1 || item.proposed_name || item.client_name;
      // Everything the Incorporation case can supply for this client — used
      // both to create a brand-new client and, below, to backfill any of
      // these fields on an already-linked one (e.g. CIN/incorporation date
      // that only became known after the client was first created).
      const sourceFields = {
        client_name: companyName,
        client_type: CLIENT_TYPE_MAP[item.incorporation_type] || 'other',
        pan: postIncorpData.pan || null,
        gstin: postIncorpData.gstin || null,
        cin: postIncorpData.cin_llpin || null,
        date_of_birth: postIncorpData.incorporation_date || null,
        mobile: addr?.mobile || '',
        email: addr?.company_email || '',
        address: addr?.address || '',
        city: addr?.district || '',
        state: addr?.state || '',
        pincode: addr?.pincode || '',
      };

      if (verifiedClientId) {
        // Already linked — sync in whichever of the fields above are still
        // blank on the client record, merged onto the full existing record
        // so nothing staff already filled in manually (contact person,
        // group, billing profile, etc.) gets overwritten or nulled out.
        const getRes = await fetch(`${BASE}/api/clients/${verifiedClientId}`, { headers: { Authorization: `Bearer ${token}` } });
        const existing = await getRes.json();
        if (!getRes.ok) throw new Error(existing.message || 'Could not load the linked client');
        const merged = { ...existing };
        let changed = false;
        Object.entries(sourceFields).forEach(([key, value]) => {
          if (value && !merged[key]) { merged[key] = value; changed = true; }
        });
        if (!changed) return;
        const putRes = await fetch(`${BASE}/api/clients/${verifiedClientId}`, { method: 'PUT', headers: authHeaders, body: JSON.stringify(merged) });
        const putData = await putRes.json();
        if (!putRes.ok) throw new Error(putData.message || 'Could not sync the linked client');
        return;
      }

      const res = await fetch(`${BASE}/api/clients`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          ...sourceFields,
          is_active: true,
          notes: `Auto-created from Incorporation Module on completion of ${item.incorporation_type} (case #${item.id}).`,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Could not create Client record');
      setPostIncorpData(prev => ({ ...prev, company_client_id: data.id, company_client_created: true }));
      setVerifiedClientId(data.id);
    } catch (e) {
      setClientError(e.message);
    } finally {
      setCreatingClient(false);
    }
  };

  // ── Move to Company Master — creates a REAL record, not just a flag ─────
  const [creatingMaster, setCreatingMaster] = useState(false);
  const [masterError, setMasterError] = useState('');

  // Never trust the locally-stored company_master_id blindly — if the
  // company was deleted from Company Master outside this flow, our local
  // flag would still say "moved" forever, permanently locking the button.
  // Instead, verify against the real backend by CIN every time this loads.
  const [verifiedCompanyId, setVerifiedCompanyId] = useState(undefined); // undefined = still checking
  useEffect(() => {
    if (!postIncorpData.cin_llpin) { setVerifiedCompanyId(null); return; }
    const token = localStorage.getItem('token');
    fetch(`${BASE}/api/mca/companies`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(list => {
        const match = Array.isArray(list) ? list.find(c => c.cin_llpin && c.cin_llpin === postIncorpData.cin_llpin) : null;
        setVerifiedCompanyId(match ? match.id : null);
        // Keep local state in sync with what's actually there — heals a
        // stale/broken company_master_id automatically.
        if (match && match.id !== postIncorpData.company_master_id) {
          setPostIncorpData(prev => ({ ...prev, company_master_id: match.id }));
        } else if (!match && postIncorpData.company_master_id) {
          setPostIncorpData(prev => ({ ...prev, company_master_id: null, moved_to_company_master: false }));
        }
      })
      .catch(() => setVerifiedCompanyId(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postIncorpData.cin_llpin]);

  const moveToCompanyMaster = async () => {
    if (!verifiedClientId) { setMasterError('Create the Company Client first — Move to Company Master needs a linked ERP client.'); return; }
    if (verifiedCompanyId) return; // already genuinely exists in Company Master
    setCreatingMaster(true);
    setMasterError('');
    try {
      const token = localStorage.getItem('token');
      const authHeaders = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

      const equityClasses = capitalStructure?.equity_classes || [];
      const authorizedCapital = equityClasses.reduce((s, c) => s + (Number(c.authorized_shares) || 0) * (Number(c.authorized_nominal_value) || 0), 0);
      const faceValue = Number(equityClasses[0]?.authorized_nominal_value) || 10;

      // Paid-up capital = the declared Subscribed Capital from Structure &
      // Capital tab — per your instruction, not a re-derived allotment sum.
      const totalSubscribedShares = equityClasses.reduce((s, c) => s + (Number(c.subscribed_shares) || 0), 0);
      const paidUpCapital = totalSubscribedShares * faceValue;

      // LLP has no authorized/paid-up share capital — Company Master's LLP
      // Snapshot instead reads a dedicated `llp_contribution` column (see
      // its "Total Contribution (obligation)" row), fed from the total
      // declared in the Capital Contribution tab.
      const totalContributionLLP = Number(capitalStructure?.total_authorized_capital)
        || (casePeople || []).reduce((s, p) => s + (Number(p.shares_or_contribution) || 0), 0);

      const addr = registeredOfficeData?.correspondence_address;
      const registeredOfficeText = addr ? [addr.address, addr.district, addr.state, addr.pincode].filter(Boolean).join(', ') : '';

      // ── Step 1: create the company record itself ──────────────────────
      const res = await fetch(`${BASE}/api/mca/companies`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          client_id: postIncorpData.company_client_id || item.client_id || null,
          client_name: item.client_name || '',
          cin_llpin: postIncorpData.cin_llpin,
          company_name: nameApproval?.approved_name || nameApproval?.proposed_name_1 || item.proposed_name,
          company_type: COMPANY_MASTER_TYPE_MAP[item.incorporation_type] || item.incorporation_type,
          pan: postIncorpData.pan,
          tan: postIncorpData.tan,
          gstin: postIncorpData.gstin,
          incorporation_date: postIncorpData.incorporation_date || null,
          authorized_capital: isLLP ? '' : authorizedCapital,
          paid_up_capital: isLLP ? '' : paidUpCapital,
          llp_contribution: isLLP ? totalContributionLLP : '',
          registered_office: registeredOfficeText,
          email: addr?.company_email || '',
          mobile: addr?.mobile || '',
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Could not create Company Master record');
      const companyId = data.id;

      // ── Step 2: create real director/partner records — Company Master
      //    stores these in a separate table, one POST per person, not
      //    inline on the company payload (that's what was silently
      //    dropping them). For LLP, EVERY partner (designated or not)
      //    becomes a row here — Company Master's "Partners" and "Capital
      //    Contribution" tabs both read from mca_company_directors, not
      //    from mca_company_shareholders, so a Partner-Only person must
      //    land here too, not just Designated Partners.
      const directorIdMap = {};
      const directorPeople = isLLP
        ? (casePeople || []).filter(p => p.entity_type !== 'corporate')
        : (casePeople || []).filter(p => p.entity_type !== 'corporate' && (p.person_role === 'director_only' || p.person_role === 'subscriber_director'));
      for (const p of directorPeople) {
        // Filing-time DIN/DPIN if they had one, else any DIN recorded
        // afterward via the Pending DIN Allotment panel — never invented,
        // never blank if we genuinely have it from either source. LLP
        // partners store their number under `dpin`, not `din` — reading
        // `din` unconditionally here was why an existing DPIN never came
        // across into Company Master.
        const resolvedDin = (p.has_din ? (isLLP ? p.dpin : p.din) : '') || p.post_incorporation_din || '';
        const isDesignatedPartner = p.person_role === 'director_only' || p.person_role === 'subscriber_director';
        const contributionAmount = Number(p.shares_or_contribution) || 0;
        const profitSharePercent = isLLP && totalContributionLLP > 0
          ? Math.round((contributionAmount / totalContributionLLP) * 10000) / 100
          : '';
        try {
          const dRes = await fetch(`${BASE}/api/mca/companies/${companyId}/directors`, {
            method: 'POST',
            headers: authHeaders,
            body: JSON.stringify({
              person_id: p.person_id || '',
              director_name: p.full_name,
              din: resolvedDin,
              pan: p.pan_number || '',
              aadhaar: p.aadhaar_number || '',
              address_aadhaar: p.address || '',
              date_of_birth: p.date_of_birth || '',
              email: p.email || '',
              mobile: p.primary_mobile || '',
              designation: isLLP ? (isDesignatedPartner ? 'Designated Partner' : 'Partner') : (p.designation || 'Director'),
              is_designated_partner: isLLP ? isDesignatedPartner : false,
              partner_type: isLLP ? 'Individual' : undefined,
              appointment_date: postIncorpData.incorporation_date || '',
              date_admitted: isLLP ? (postIncorpData.incorporation_date || '') : undefined,
              // Only the obligation is auto-filled — this is the amount
              // declared in the FiLLiP filing. Whether it's actually been
              // deposited into the LLP's bank account is a separate,
              // later fact (tracked by the "Deposit Partner Capital
              // Contribution" checklist item), not something to assume.
              contribution_obligation: isLLP ? contributionAmount : undefined,
              profit_share_percent: isLLP ? profitSharePercent : undefined,
              din_kyc_status: resolvedDin ? 'Filed' : 'Pending',
              is_active: true,
              // First directors/partners are appointed via this very
              // incorporation filing (Sec 152(1) / LLP Sec 7 as applicable)
              // — no separate Board/AGM resolution or DIR-12/Form 4 applies
              // to their appointment.
              appointed_at_incorporation: true,
            }),
          });
          const dData = await dRes.json();
          if (dRes.ok && dData.id) directorIdMap[p.id] = dData.id;
        } catch (_) { /* one bad record shouldn't block the rest */ }
      }

      // ── Step 3: create real shareholder records — company cases only.
      //    LLPs have no share register; their contribution lives on the
      //    partner record created above instead.
      if (!isLLP) {
        const shareholderPeople = (casePeople || []).filter(p =>
          p.entity_type === 'corporate' || p.person_role === 'subscriber_only' || p.person_role === 'subscriber_director'
        );
        for (const p of shareholderPeople) {
          try {
            await fetch(`${BASE}/api/mca/companies/${companyId}/shareholders`, {
              method: 'POST',
              headers: authHeaders,
              body: JSON.stringify({
                member_name: p.full_name,
                address: p.address || '',
                email: p.email || '',
                pan: p.pan_number || '',
                aadhaar: p.aadhaar_number || '',
                share_class: 'Equity',
                no_of_shares: p.shares_or_contribution || 0,
                face_value: faceValue,
                amount_paid: faceValue,
                mode_of_acquisition: 'Subscription',
                date_of_allotment: postIncorpData.incorporation_date || '',
                is_active: true,
              }),
            });
          } catch (_) { /* one bad record shouldn't block the rest */ }
        }
      }

      setPostIncorpData(prev => ({
        ...prev,
        moved_to_company_master: true,
        company_master_id: companyId,
        director_id_map: directorIdMap,
      }));
    } catch (e) {
      setMasterError(e.message);
    } finally {
      setCreatingMaster(false);
    }
  };

  // ── Auto-fetch First Board Meeting date — once the company exists in
  //    Company Master, this becomes the single source of truth instead of
  //    a manually-typed field here.
  const [boardMeetings, setBoardMeetings] = useState([]);
  useEffect(() => {
    if (!postIncorpData.company_master_id) return;
    const token = localStorage.getItem('token');
    fetch(`${BASE}/api/mca/companies/${postIncorpData.company_master_id}/board-meetings`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => setBoardMeetings(Array.isArray(d) ? d : []))
      .catch(() => setBoardMeetings([]));
  }, [postIncorpData.company_master_id]);

  const firstBoardMeeting = boardMeetings.length > 0
    ? [...boardMeetings].sort((a, b) => new Date(a.meeting_date) - new Date(b.meeting_date))[0]
    : null;
  const effectiveFbmDate = firstBoardMeeting?.meeting_date ? String(firstBoardMeeting.meeting_date).slice(0, 10) : '';

  const statusColors = {
    Pending: 'bg-gray-100 text-gray-700',
    'In Progress': 'bg-blue-100 text-blue-700',
    Completed: 'bg-green-100 text-green-700',
    'Not Applicable': 'bg-amber-100 text-amber-700',
  };

  // ── Statutory templates — two anchor dates, split governance vs filing ──
  const governanceTemplate = () => {
    if (isLLP) {
      return [
        { task: 'Execute LLP Agreement on Stamp Paper', date_basis: 'incorporation', days_offset: 15, due_logic: 'Execute on State Stamp Paper after COI' },
        { task: 'Deposit Partner Capital Contribution', date_basis: 'incorporation', days_offset: 30, due_logic: 'Deposit agreed contribution into LLP bank account' },
        { task: 'PAN / TAN & Bank Account Setup', date_basis: 'incorporation', days_offset: 15, due_logic: 'Confirm bank activation and tax IDs' },
        { task: 'Maintain Books & Records of Accounts', date_basis: 'incorporation', days_offset: 30, due_logic: 'Establish accounting records at Registered Office' },
      ];
    }
    const base = [
      { task: 'Hold First Board Meeting', date_basis: 'first_bm_direct', days_offset: 0, due_logic: 'Within 30 days from incorporation (Sec 173(1)) — tracked via Board Meeting Register' },
      { task: 'Appoint First Statutory Auditor', date_basis: 'first_bm', days_offset: 0, due_logic: 'Ratified at First Board Meeting (Sec 139(6))' },
      { task: 'Collect MBP-1 (Interest) & DIR-8 (Eligibility)', date_basis: 'first_bm', days_offset: 0, due_logic: 'Collected at First Board Meeting (Sec 184/164)' },
      { task: 'Deposit Share Capital Subscription', date_basis: 'incorporation', days_offset: 60, due_logic: 'Before INC-20A filing' },
      { task: 'Issue Share Certificates (Form SH-1)', date_basis: 'incorporation', days_offset: 60, due_logic: 'Within 2 months of incorporation (Sec 56(4))' },
    ];
    if (isProducer) {
      base.push(
        { task: 'Appoint Chief Executive Officer (CEO)', date_basis: 'first_bm', days_offset: 30, due_logic: 'Appointed by Board (Sec 378W)' },
        { task: 'Establish Member Patronage Reserve Ledger', date_basis: 'first_bm', days_offset: 60, due_logic: 'Setup mutual assistance reserve mechanism' },
      );
    }
    if (isNidhi) {
      base.push(
        { task: 'Achieve 200 Members & ₹20L Net Owned Funds', date_basis: 'incorporation', days_offset: 365, due_logic: 'Must comply within 1 year from incorporation' },
      );
    }
    return base;
  };

  const filingTemplate = () => {
    if (isLLP) {
      return [
        { task: 'Form 3 & 4 - LLP Agreement Filing', date_basis: 'incorporation', days_offset: 30, due_logic: 'Within 30 days from incorporation (Rule 21(1))', form: 'LLP Form 3 & 4' },
      ];
    }
    const base = [
      { task: 'INC-20A - Declaration of Commencement of Business', date_basis: 'incorporation', days_offset: 180, due_logic: 'Within 180 days from incorporation (Sec 10A)', form: 'INC-20A' },
    ];
    if (registeredOfficeData?.same_as_registered_office === false) {
      base.push(
        { task: 'INC-22 - Notice of Registered Office', date_basis: 'incorporation', days_offset: 30, due_logic: 'Required — no registered office confirmed at incorporation (Sec 12(2))', form: 'INC-22' },
      );
    }
    if (isSection8) {
      base.push(
        { task: 'Apply for Section 12AB & 80G Tax Exemption', date_basis: 'incorporation', days_offset: 90, due_logic: 'File Form 10A on IT Portal before charitable activity', form: 'IT Form 10A' },
        { task: 'File Form CSR-1 Registration', date_basis: 'incorporation', days_offset: 120, due_logic: 'Mandatory before accepting CSR grants', form: 'CSR-1' },
        { task: 'Apply for FCRA Registration', date_basis: 'incorporation', days_offset: 180, due_logic: 'Required before accepting foreign donations', form: 'FCRA (MHA)' },
      );
    }
    if (isNidhi) {
      base.push(
        { task: 'Form NDH-4 - Declaration as Nidhi', date_basis: 'incorporation', days_offset: 120, due_logic: 'Within 120 days of incorporation (Nidhi Rules 2014)', form: 'NDH-4' },
      );
    }
    return base;
  };

  // ── Due-date computation — respects which anchor each item uses ────────
  const computeDueDate = (dateBasis, daysOffset) => {
    if (dateBasis === 'first_bm_direct') return effectiveFbmDate || '';
    const anchor = dateBasis === 'first_bm' ? effectiveFbmDate : postIncorpData.incorporation_date;
    if (!anchor) return '';
    const d = new Date(anchor);
    d.setDate(d.getDate() + daysOffset);
    return d.toISOString().slice(0, 10);
  };

  const generateChecklist = () => {
    const rows = [
      ...governanceTemplate().map(row => ({ ...row, group: 'governance' })),
      ...filingTemplate().map(row => ({ ...row, group: 'filing' })),
    ];
    const generated = rows.map((row, index) => ({
      id: `${Date.now()}-${index}`,
      ...row,
      status: 'Pending',
      due_date: computeDueDate(row.date_basis, row.days_offset),
      srn: '',
      remarks: '',
    }));
    setChecklist(generated);
  };

  // Recompute due dates live whenever either anchor date changes — so filling
  // in First BM date instantly unlocks correct dates for BM-anchored items.
  useEffect(() => {
    if (checklist.length === 0) return;
    setChecklist(prev => prev.map(row => ({
      ...row,
      due_date: row.due_date && row.status !== 'Pending' ? row.due_date : computeDueDate(row.date_basis, row.days_offset) || row.due_date,
    })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postIncorpData.incorporation_date, effectiveFbmDate]);

  const updateChecklist = (id, field, value) => {
    setChecklist(prev => prev.map(row => row.id === id ? { ...row, [field]: value } : row));
  };

  const removeChecklistItem = (id) => {
    setChecklist(prev => prev.filter(row => row.id !== id));
  };

  const addCustomItem = (group, draft) => {
    if (!draft || !draft.task?.trim()) return;
    const dateBasis = draft.date_basis || 'incorporation';
    const daysOffset = Number(draft.days_offset) || 0;
    setChecklist(prev => [...prev, {
      id: `${Date.now()}-custom`,
      task: draft.task, group, date_basis: dateBasis, days_offset: daysOffset,
      due_logic: 'Custom item', form: draft.form || '',
      status: 'Pending', due_date: computeDueDate(dateBasis, daysOffset), srn: '', remarks: '',
    }]);
  };

  // ── Urgency — Overdue / Critical / Done / Awaiting anchor ───────────────
  const getUrgency = (row) => {
    if (row.status === 'Completed' || row.status === 'Not Applicable') return { label: 'Done', color: 'text-green-600' };
    if (row.date_basis === 'first_bm_direct') {
      return effectiveFbmDate
        ? { label: 'Held ✓', color: 'text-green-600 font-semibold' }
        : { label: 'Not yet held', color: 'text-amber-600 font-semibold' };
    }
    const anchorSet = row.date_basis === 'first_bm' ? !!effectiveFbmDate : !!postIncorpData.incorporation_date;
    if (!anchorSet) return { label: row.date_basis === 'first_bm' ? 'Awaiting First BM' : 'Set Incorporation Date', color: 'text-gray-400' };
    if (!row.due_date) return { label: 'No date', color: 'text-gray-400' };
    const due = new Date(row.due_date); due.setHours(0,0,0,0);
    const today = new Date(); today.setHours(0,0,0,0);
    const diff = Math.ceil((due - today) / 86400000);
    if (diff < 0) return { label: `${Math.abs(diff)}d overdue`, color: 'text-red-600 font-semibold' };
    if (diff <= 7) return { label: `${diff}d left`, color: 'text-orange-600 font-semibold' };
    return { label: `${diff}d left`, color: 'text-gray-500' };
  };

  const governanceRows = checklist.filter(c => c.group === 'governance');
  const filingRows = checklist.filter(c => c.group === 'filing');

  const stats = {
    total: checklist.length,
    completed: checklist.filter(c => c.status === 'Completed').length,
  };
  const progressPct = stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0;

  // ── INC-22 auto-reminder ──────────────────────────────────────────────
  const needsInc22 = registeredOfficeData && registeredOfficeData.same_as_registered_office === false;
  const inc22DueDate = needsInc22 && postIncorpData.incorporation_date
    ? new Date(new Date(postIncorpData.incorporation_date).getTime() + 30 * 86400000)
    : null;
  const inc22DaysLeft = inc22DueDate
    ? Math.ceil((inc22DueDate.setHours(0,0,0,0) - new Date().setHours(0,0,0,0)) / 86400000)
    : null;

  return (
    <div className="space-y-5">

      {needsInc22 && (
        <div className={`rounded-xl border p-4 flex items-center justify-between ${
          inc22DaysLeft !== null && inc22DaysLeft <= 7 ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'
        }`}>
          <div className="flex items-start gap-3">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={inc22DaysLeft !== null && inc22DaysLeft <= 7 ? '#dc2626' : '#d97706'} strokeWidth="2" className="mt-0.5 flex-shrink-0">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            <div>
              <p className={`text-sm font-semibold ${inc22DaysLeft !== null && inc22DaysLeft <= 7 ? 'text-red-700' : 'text-amber-700'}`}>
                INC-22 required — no registered office was confirmed at incorporation
              </p>
              <p className={`text-xs mt-0.5 ${inc22DaysLeft !== null && inc22DaysLeft <= 7 ? 'text-red-600' : 'text-amber-600'}`}>
                {postIncorpData.incorporation_date
                  ? `Must be filed within 30 days from Date of Incorporation — due ${inc22DueDate.toLocaleDateString('en-IN')}.`
                  : 'Must be filed within 30 days from Date of Incorporation — enter the incorporation date below to see the exact due date.'}
              </p>
            </div>
          </div>
          {inc22DaysLeft !== null && (
            <div className="text-right flex-shrink-0 ml-3">
              <p className={`text-lg font-bold ${inc22DaysLeft <= 7 ? 'text-red-700' : 'text-amber-700'}`}>
                {inc22DaysLeft > 0 ? inc22DaysLeft : 'Overdue'}
              </p>
              {inc22DaysLeft > 0 && <p className="text-xs text-gray-400">days left</p>}
            </div>
          )}
        </div>
      )}

      {/* ── Header with soft progress ring ── */}
      <div className="bg-gradient-to-r from-[#0f1f45] to-blue-900 rounded-xl shadow-sm p-5 flex items-center justify-between">
        <div>
          <h4 className="text-base font-bold text-white">Filing &amp; Post Incorporation</h4>
          <p className="text-sm text-blue-200 mt-1">
            Mark the incorporation form as filed, capture COI, then work through compliance box by box.
          </p>
        </div>
        <div className="flex items-center gap-4">
          {stats.total > 0 && (
            <div className="text-right">
              <p className="text-2xl font-bold text-white">{progressPct}%</p>
              <p className="text-[11px] text-blue-200">{stats.completed} of {stats.total} done</p>
            </div>
          )}
          <button
            onClick={generateChecklist}
            className="px-4 py-2 rounded-lg text-[#0f1f45] text-sm font-semibold bg-white hover:bg-blue-50"
          >
            Generate Post Compliance
          </button>
        </div>
      </div>

      {/* ── Box 1: Filing & COI ── */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-visible border-l-4 border-l-blue-400">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
          <IconBadge color="bg-blue-50">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
          </IconBadge>
          <div>
            <h4 className="text-sm font-bold text-[#0f1f45]">Filing &amp; Certificate of Incorporation</h4>
            <p className="text-xs text-gray-400 mt-0.5">Record SPICe+ / FiLLiP submission and final COI credentials.</p>
          </div>
        </div>

        <div className="p-5 space-y-5">
          <div>
            <p className="text-xs font-semibold text-gray-600 mb-3">Incorporation Filing</p>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Form Filed?</label>
                <select
                  value={filingData.filing_status === 'Filed' || filingData.filing_status === 'Approved' ? 'Yes' : 'No'}
                  onChange={e => updateFiling('filing_status', e.target.value === 'Yes' ? 'Filed' : 'Not Filed')}
                  className={ic}
                >
                  <option>No</option>
                  <option>Yes</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">SRN</label>
                <input value={filingData.master_srn} onChange={e => updateFiling('master_srn', e.target.value.toUpperCase())} placeholder="SRN" className={ic} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Filing Date</label>
                <input type="date" value={filingData.filing_date} onChange={e => updateFiling('filing_date', e.target.value)} className={ic} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">ROC Fees (₹)</label>
                <input type="number" value={filingData.roc_fees} onChange={e => updateFiling('roc_fees', e.target.value)} placeholder="0" className={ic} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Challan Upload</label>
                <label className="flex items-center justify-center border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-600 cursor-pointer hover:bg-gray-50 bg-white">
                  {filingData.challan_file || 'Upload Challan'}
                  <input type="file" className="hidden" onChange={e => updateFiling('challan_file', e.target.files?.[0]?.name || '')} />
                </label>
              </div>
              <div className="col-span-3">
                <label className="block text-xs font-medium text-gray-600 mb-1">Resubmission / Remarks (if any)</label>
                <input value={filingData.resubmission_remarks} onChange={e => updateFiling('resubmission_remarks', e.target.value)} placeholder="MCA resubmission remarks, if any" className={ic} />
              </div>
            </div>
          </div>

          <div className="p-4 rounded-xl bg-blue-50/50 border border-blue-100">
            <p className="text-xs font-semibold text-blue-800 mb-3">Corporate Identification</p>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">COI Received</label>
                <select value={postIncorpData.coi_received ? 'Yes' : 'No'} onChange={e => update('coi_received', e.target.value === 'Yes')} className={ic}>
                  <option>No</option>
                  <option>Yes</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{isLLP ? 'LLPIN' : 'CIN'}</label>
                <input value={postIncorpData.cin_llpin} onChange={e => update('cin_llpin', e.target.value.toUpperCase())} placeholder={isLLP ? 'LLPIN' : 'CIN'} className={ic} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Incorporation Date *</label>
                <input type="date" value={postIncorpData.incorporation_date} onChange={e => update('incorporation_date', e.target.value)} className={`${ic} border-blue-300`} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">COI Upload</label>
                <label className="flex items-center justify-center border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-600 cursor-pointer hover:bg-gray-50 bg-white">
                  {postIncorpData.coi_file || 'Upload COI'}
                  <input type="file" className="hidden" onChange={e => update('coi_file', e.target.files?.[0]?.name || '')} />
                </label>
              </div>
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-600 mb-3">PAN / TAN / GST / Bank</p>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">PAN</label>
                <input value={postIncorpData.pan} onChange={e => update('pan', e.target.value.toUpperCase())} placeholder="PAN" className={ic} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">TAN</label>
                <input value={postIncorpData.tan} onChange={e => update('tan', e.target.value.toUpperCase())} placeholder="TAN" className={ic} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">GSTIN</label>
                <input value={postIncorpData.gstin} onChange={e => update('gstin', e.target.value.toUpperCase())} placeholder="GSTIN, if applied" className={ic} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Bank Account Opened</label>
                <select value={postIncorpData.bank_account_opened ? 'Yes' : 'No'} onChange={e => update('bank_account_opened', e.target.value === 'Yes')} className={ic}>
                  <option>No</option>
                  <option>Yes</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {(() => {
        const pendingDinDirectors = (casePeople || []).filter(p =>
          p.entity_type !== 'corporate' &&
          (p.person_role === 'director_only' || p.person_role === 'subscriber_director') &&
          !p.has_din
        );
        if (pendingDinDirectors.length === 0) return null;

        const allDinAllotted = pendingDinDirectors.every(p => !!p.post_incorporation_din);

        return (
          <div className={`rounded-xl border p-5 ${allDinAllotted ? 'bg-green-50 border-green-100' : 'bg-amber-50 border-amber-100'}`}>
            <h4 className={`text-sm font-bold ${allDinAllotted ? 'text-green-800' : 'text-amber-800'}`}>
              {allDinAllotted ? 'DIN Allotted' : 'Pending DIN Allotment'}
            </h4>
            <p className={`text-xs mt-0.5 mb-3 ${allDinAllotted ? 'text-green-600' : 'text-amber-600'}`}>
              {allDinAllotted
                ? 'All directors now have a DIN on record — logged separately from the original filing.'
                : 'These directors didn\'t have a DIN at the time of filing — once SPICe+ allots it, record it here. This does not change the filing record (they correctly had "No DIN" at incorporation) — it\'s a separate post-incorporation log, and it also updates the live record in Company Master once moved there.'}
            </p>
            <div className="space-y-2">
              {pendingDinDirectors.map(p => (
                <PendingDinRow
                  key={p.id}
                  person={p}
                  isLLP={item.incorporation_type === 'LLP Incorporation'}
                  companyId={postIncorpData.company_master_id}
                  directorId={postIncorpData.director_id_map?.[p.id]}
                  onUpdated={(newDin) => {
                    setCasePeople(prev => prev.map(row =>
                      row.id === p.id ? { ...row, post_incorporation_din: newDin, post_incorporation_din_date: new Date().toISOString().slice(0, 10) } : row
                    ));
                  }}
                />
              ))}
            </div>
          </div>
        );
      })()}

      {/* ── Box 4 (moved here): Transition to Company Master ── */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 border-l-4 border-l-green-400">
        <div className="flex items-center gap-3 mb-4">
          <IconBadge color="bg-green-50">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
          </IconBadge>
          <div>
            <h4 className="text-sm font-bold text-[#0f1f45]">Transition to Active Client Master</h4>
            <p className="text-xs text-gray-400 mt-0.5">One click — directors, shareholders, and paid-up capital are all pulled automatically. Nothing to fill manually.</p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <button
            onClick={createCompanyClient}
            disabled={creatingClient || verifiedClientId === undefined || !postIncorpData.pan}
            title={
              !postIncorpData.pan ? 'Enter PAN above first — it is used to detect an existing client and prevent duplicates'
              : verifiedClientId ? 'Click again anytime to sync CIN/PAN/GSTIN/incorporation date onto the linked client — never overwrites fields already filled in manually'
              : undefined
            }
            className={`px-4 py-3 rounded-lg border text-sm font-medium disabled:opacity-60 ${
              verifiedClientId ? 'bg-green-50 border-green-200 text-green-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            {creatingClient ? (verifiedClientId ? 'Syncing…' : 'Creating…')
              : !postIncorpData.pan ? 'Create Company Client (enter PAN first)'
              : verifiedClientId === undefined ? 'Checking Client Master…'
              : verifiedClientId ? '✓ Company Client Created (click to sync)' : 'Create Company Client'}
          </button>
          <button
            onClick={moveToCompanyMaster}
            disabled={creatingMaster || !!verifiedCompanyId || verifiedCompanyId === undefined || !verifiedClientId}
            title={!verifiedClientId ? 'Create the Company Client first' : undefined}
            className={`px-4 py-3 rounded-lg border text-sm font-medium disabled:opacity-60 ${
              verifiedCompanyId ? 'bg-green-50 border-green-200 text-green-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            {creatingMaster ? 'Creating…' : verifiedCompanyId === undefined ? 'Checking Company Master…' : verifiedCompanyId ? '✓ Already in Company Master' : 'Move to Company Master'}
          </button>
          <input value={postIncorpData.notes} onChange={e => update('notes', e.target.value)} placeholder="Closure notes" className={ic} />
        </div>
        {clientError && <p className="text-xs text-red-500 mt-2">{clientError}</p>}
        {masterError && <p className="text-xs text-red-500 mt-2">{masterError}</p>}
        {verifiedClientId === null && postIncorpData.pan && (
          <p className="text-xs text-amber-600 mt-2">
            No client with PAN <b>{postIncorpData.pan}</b> found in Clients — safe to create{postIncorpData.company_client_id ? ' (previous link was removed — probably deleted from Clients)' : ''}.
          </p>
        )}
        {verifiedCompanyId === null && postIncorpData.cin_llpin && (
          <p className="text-xs text-amber-600 mt-2">
            No company with CIN/LLPIN <b>{postIncorpData.cin_llpin}</b> found in Company Master — safe to move.
          </p>
        )}
      </div>

      {/* ── Box 2: Board Governance & Paperwork — no SRN ── */}
      <ComplianceBox
        title="Board Governance & Paperwork"
        subtitle="No SRN needed — internal governance and record-keeping"
        iconBg="bg-purple-50"
        iconColor="#7c3aed"
        icon={<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>}
        rows={governanceRows}
        showSrn={false}
        groupKey="governance"
        accentBorder="border-l-purple-400"
        statusColors={statusColors}
        updateChecklist={updateChecklist}
        removeChecklistItem={removeChecklistItem}
        getUrgency={getUrgency}
        onAddItem={addCustomItem}
        onNavigate={onNavigate}
        companyId={postIncorpData.company_master_id}
        headerNote={
          !postIncorpData.company_master_id ? (
            <span>First Board Meeting date will show here once you complete <b>Move to Company Master</b> above and record the meeting in the Board Meeting Register.</span>
          ) : effectiveFbmDate ? (
            <span className="text-green-600">First Board Meeting recorded on <b>{new Date(effectiveFbmDate).toLocaleDateString('en-IN')}</b> — pulled live from the Board Meeting Register.</span>
          ) : (
            <span>Company is in Company Master — no First Board Meeting recorded yet. Add it in Registers → Board Meeting Register for this company.</span>
          )
        }
      />

      {/* ── Box 3: MCA / ROC Filings — SRN shown ── */}
      <ComplianceBox
        title="MCA / ROC Filings"
        subtitle="Genuine statutory filings — SRN tracked once submitted"
        iconBg="bg-amber-50"
        iconColor="#d97706"
        icon={<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></>}
        rows={filingRows}
        showSrn={true}
        groupKey="filing"
        accentBorder="border-l-amber-400"
        statusColors={statusColors}
        updateChecklist={updateChecklist}
        removeChecklistItem={removeChecklistItem}
        getUrgency={getUrgency}
        onAddItem={addCustomItem}
        headerNote="INC-20A and ADT-1 are also tracked in the Event Filing Module — this box mirrors them for a single-case view."
      />

    </div>
  );
}



// ── Standalone share-class table component — MUST live outside its parent
//    so React doesn't recreate it (and lose input focus) on every keystroke.
function ShareClassTable({ title, classKey, data, setData, ic, actuallyAllottedShares }) {
  const addClass = () => {
    setData(d => ({
      ...d,
      [classKey]: [...(d[classKey] || []), { class_name: '', authorized_shares: '', authorized_nominal_value: '10', subscribed_shares: '', subscribed_nominal_value: '10' }],
    }));
  };
  const updateClass = (index, field, value) => {
    setData(d => {
      const list = [...(d[classKey] || [])];
      list[index] = { ...list[index], [field]: value };
      return { ...d, [classKey]: list };
    });
  };
  const removeClass = (index) => {
    setData(d => ({ ...d, [classKey]: (d[classKey] || []).filter((_, i) => i !== index) }));
  };

  const classTotal = (c) => (Number(c.authorized_shares) || 0) * (Number(c.authorized_nominal_value) || 0);
  const subscribedClassTotal = (c) => (Number(c.subscribed_shares) || 0) * (Number(c.subscribed_nominal_value) || 0);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h5 className="text-sm font-semibold text-gray-700">{title}</h5>
        <button type="button" onClick={addClass} className="text-xs text-blue-600 hover:underline">+ Add class</button>
      </div>

      {(data[classKey] || []).length === 0 ? (
        <p className="text-xs text-gray-400 border border-dashed border-gray-200 rounded-lg p-3 text-center">No classes added.</p>
      ) : (
        <div className="space-y-3">
          {(data[classKey] || []).map((c, i) => (
            <div key={i} className="border border-gray-100 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <input
                  value={c.class_name}
                  onChange={e => updateClass(i, 'class_name', e.target.value)}
                  placeholder="Class name"
                  className="text-xs font-semibold border-0 border-b border-dashed border-gray-300 focus:outline-none focus:border-blue-400 bg-transparent"
                />
                <button type="button" onClick={() => removeClass(i)} className="text-xs text-red-500 hover:underline">Remove</button>
              </div>

              <p className="text-[11px] font-semibold text-gray-500 mt-1 mb-1">Authorized</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] text-gray-400 mb-1">No. of shares</label>
                  <input type="number" value={c.authorized_shares} onChange={e => updateClass(i, 'authorized_shares', e.target.value)} placeholder="e.g. 150000" className={ic} />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-400 mb-1">Face value/share (₹)</label>
                  <input type="number" value={c.authorized_nominal_value} onChange={e => updateClass(i, 'authorized_nominal_value', e.target.value)} className={ic} />
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-2 mb-3">
                Authorized total: <span className="font-medium text-gray-600">₹{classTotal(c).toLocaleString('en-IN')}</span>
              </p>

              <p className="text-[11px] font-semibold text-blue-600 mt-1 mb-1">Subscribed — the pool offered to subscribers</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] text-gray-400 mb-1">No. of shares</label>
                  <input
                    type="number"
                    value={c.subscribed_shares}
                    onChange={e => updateClass(i, 'subscribed_shares', e.target.value)}
                    placeholder="e.g. 10000"
                    className={`${ic} ${Number(c.subscribed_shares) > Number(c.authorized_shares) ? 'border-red-400' : ''}`}
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-400 mb-1">Face value/share (₹)</label>
                  <input type="number" value={c.subscribed_nominal_value} onChange={e => updateClass(i, 'subscribed_nominal_value', e.target.value)} className={ic} />
                </div>
              </div>
              {Number(c.subscribed_shares) > Number(c.authorized_shares) && (
                <p className="text-xs text-red-500 mt-1">Cannot subscribe more shares than authorized.</p>
              )}
              <p className="text-xs text-gray-400 mt-2">
                Subscribed total: <span className="font-medium text-gray-600">₹{subscribedClassTotal(c).toLocaleString('en-IN')}</span>
              </p>

              {Number(c.subscribed_shares) > 0 && (
                <div className="mt-3 bg-blue-50 rounded-lg px-3 py-2">
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-xs font-semibold text-blue-700">Allotted to subscribers so far</p>
                    <p className="text-xs font-bold text-blue-700">{actuallyAllottedShares} / {c.subscribed_shares} shares</p>
                  </div>
                  <div className="h-1.5 rounded-full bg-blue-100 overflow-hidden">
                    <div
                      className={`h-full rounded-full ${actuallyAllottedShares > Number(c.subscribed_shares) ? 'bg-red-500' : 'bg-blue-500'}`}
                      style={{ width: `${Math.min(100, (actuallyAllottedShares / Number(c.subscribed_shares)) * 100)}%` }}
                    />
                  </div>
                  <p className="text-[11px] text-blue-500 mt-1">Go to Promoters &amp; Partners to allot shares out of this pool.</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CompanyStructureCapitalTab({ item, data, setData, nicCode, casePeople }) {
  const isLLP = item.incorporation_type === 'LLP Incorporation';
  const ic = "w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-blue-500 bg-white";

  const updateField = (field, value) => setData(d => ({ ...d, [field]: value }));

  // ── Entrenchment article helpers ────────────────────────────────────
  const addArticle = () => {
    setData(d => ({ ...d, entrenchment_articles: [...(d.entrenchment_articles || []), { article_number: '', description: '' }] }));
  };
  const updateArticle = (index, field, value) => {
    setData(d => {
      const list = [...(d.entrenchment_articles || [])];
      list[index] = { ...list[index], [field]: value };
      return { ...d, entrenchment_articles: list };
    });
  };
  const removeArticle = (index) => {
    setData(d => ({ ...d, entrenchment_articles: (d.entrenchment_articles || []).filter((_, i) => i !== index) }));
  };

  const classTotal = (c) => (Number(c.authorized_shares) || 0) * (Number(c.authorized_nominal_value) || 0);
  const subscribedClassTotal = (c) => (Number(c.subscribed_shares) || 0) * (Number(c.subscribed_nominal_value) || 0);

  // ── Total Authorized Capital — AUTO-CALCULATED from Equity + Preference
  //    class totals, exactly like SPICe+ Part B section 3A does. Never
  //    manually typed — that would create a second disagreeing number.
  const totalAuthorizedFromClasses = (data.equity_classes || []).reduce((s, c) => s + classTotal(c), 0)
    + (data.preference_classes || []).reduce((s, c) => s + classTotal(c), 0);

  // ── Subscribed capital — DECLARED here (the pool offered to subscribers),
  //    not derived. Promoters & Partners distributes shares out of this pool.
  const totalSubscribedShares = (data.equity_classes || []).reduce((s, c) => s + (Number(c.subscribed_shares) || 0), 0);
  const totalSubscribedAmount = (data.equity_classes || []).reduce((s, c) => s + subscribedClassTotal(c), 0);
  const totalPreferenceSubscribedShares = (data.preference_classes || []).reduce((s, c) => s + (Number(c.subscribed_shares) || 0), 0);
  const totalPreferenceSubscribedAmount = (data.preference_classes || []).reduce((s, c) => s + subscribedClassTotal(c), 0);

  // How much of the declared equity pool has actually been allotted so far
  const actuallyAllottedShares = (casePeople || []).reduce((s, p) => s + (Number(p.shares_or_contribution) || 0), 0);
  const actuallyAllottedPreferenceShares = (casePeople || []).reduce((s, p) => s + (Number(p.preference_shares) || 0), 0);

  const numberToWords = (num) => {
    num = Math.floor(Number(num) || 0);
    if (num === 0) return '';
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
      'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const twoDigits = (n) => n < 20 ? ones[n] : tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
    const threeDigits = (n) => n < 100 ? twoDigits(n) : ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + twoDigits(n % 100) : '');
    const crore = Math.floor(num / 10000000);
    const lakh = Math.floor((num % 10000000) / 100000);
    const thousand = Math.floor((num % 100000) / 1000);
    const rest = num % 1000;
    let words = '';
    if (crore) words += threeDigits(crore) + ' Crore ';
    if (lakh) words += twoDigits(lakh) + ' Lakh ';
    if (thousand) words += twoDigits(thousand) + ' Thousand ';
    if (rest) words += threeDigits(rest);
    return words.trim() + ' Rupees Only';
  };

  return (
    <div className="grid grid-cols-3 gap-5">
      <div className="col-span-2 space-y-5">

        {/* ── LLP Capital Contribution — mirrors FiLLiP Section 8: "Total monetary
             value of contribution by partners in the LLP" ── */}
        {isLLP && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <h4 className="text-base font-bold text-[#0f1f45] mb-1">Total Contribution by Partners</h4>
            <p className="text-xs text-gray-400 mb-4">
              Declare the total monetary value of contribution by all partners (FiLLiP Part A, Section 8). Partners then contribute out of this pool in the Promoters / Partners tab.
            </p>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Total monetary value of contribution (₹)</label>
              <input
                type="number"
                value={data.total_authorized_capital}
                onChange={e => updateField('total_authorized_capital', e.target.value)}
                placeholder="e.g. 10000"
                className={ic}
              />
              {Number(data.total_authorized_capital) > 0 && (
                <p className="text-xs text-gray-400 mt-1.5 italic">{numberToWords(data.total_authorized_capital)}</p>
              )}
            </div>

            <div className="bg-gray-50 rounded-lg px-4 py-3 mt-4 flex items-center gap-4">
              <div className="min-w-[140px]">
                <p className="text-xs text-gray-500">Contributed so far</p>
                <p className="text-lg font-bold text-[#0f1f45]">₹{actuallyAllottedShares.toLocaleString('en-IN')}</p>
              </div>
              <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                <div
                  className={`h-full rounded-full ${actuallyAllottedShares > (Number(data.total_authorized_capital) || 0) ? 'bg-red-500' : 'bg-green-500'}`}
                  style={{ width: `${Number(data.total_authorized_capital) > 0 ? Math.min(100, (actuallyAllottedShares / Number(data.total_authorized_capital)) * 100) : 0}%` }}
                />
              </div>
              <p className="text-xs font-medium text-gray-500 whitespace-nowrap">
                {Math.max(0, (Number(data.total_authorized_capital) || 0) - actuallyAllottedShares).toLocaleString('en-IN')} remaining
              </p>
            </div>
          </div>
        )}

        {/* ── Share capital gate ── */}
        {!isLLP && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <h4 className="text-base font-bold text-[#0f1f45] mb-3">Share Capital</h4>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => updateField('has_share_capital', true)}
                className={`flex-1 px-4 py-2.5 rounded-lg border text-sm font-medium ${data.has_share_capital ? 'bg-[#0f1f45] text-white border-[#0f1f45]' : 'bg-white text-gray-600 border-gray-200'}`}
              >
                Having Share Capital
              </button>
              <button
                type="button"
                onClick={() => updateField('has_share_capital', false)}
                className={`flex-1 px-4 py-2.5 rounded-lg border text-sm font-medium ${!data.has_share_capital ? 'bg-[#0f1f45] text-white border-[#0f1f45]' : 'bg-white text-gray-600 border-gray-200'}`}
              >
                Not having Share Capital
              </button>
            </div>
            {!data.has_share_capital && (
              <p className="text-xs text-gray-400 mt-3">Section 8 (not-for-profit) path — capital and members sections below are hidden.</p>
            )}
          </div>
        )}

        {/* ── AOA Entrenchment ── */}
        {!isLLP && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-base font-bold text-[#0f1f45]">AOA Entrenchment</h4>
              <button
                type="button"
                onClick={() => updateField('aoa_entrenched', !data.aoa_entrenched)}
                className={`w-9 h-5 rounded-full flex items-center px-0.5 transition-colors ${data.aoa_entrenched ? 'bg-[#0f1f45] justify-end' : 'bg-gray-200 justify-start'}`}
              >
                <span className="w-4 h-4 rounded-full bg-white block" />
              </button>
            </div>
            {data.aoa_entrenched && (
              <div className="space-y-2">
                {(data.entrenchment_articles || []).map((a, i) => (
                  <div key={i} className="grid grid-cols-5 gap-2 items-center">
                    <input value={a.article_number} onChange={e => updateArticle(i, 'article_number', e.target.value)} placeholder="Article No." className={`${ic} col-span-1`} />
                    <input value={a.description} onChange={e => updateArticle(i, 'description', e.target.value)} placeholder="Description of entrenchment clause" className={`${ic} col-span-3`} />
                    <button type="button" onClick={() => removeArticle(i)} className="text-xs text-red-500 hover:underline">Remove</button>
                  </div>
                ))}
                <button type="button" onClick={addArticle} className="text-xs text-blue-600 hover:underline">+ Add article</button>
              </div>
            )}
          </div>
        )}

        {/* ── Capital Structure ── */}
        {!isLLP && data.has_share_capital && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <h4 className="text-base font-bold text-[#0f1f45] mb-1">Capital Structure</h4>
            <p className="text-xs text-gray-400 mb-4">Authorized capital is the ceiling — subscribed shares are tracked from actual allotments in Promoters &amp; Partners.</p>

            {/* Total Authorized Capital — auto-calculated, matches SPICe+ Part B 3A behaviour */}
            <div className="bg-gray-50 rounded-lg px-4 py-3 mb-5">
              <p className="text-xs text-gray-500 mb-0.5">Total Authorized Capital (₹) — auto-calculated</p>
              <p className="text-xl font-bold text-[#0f1f45]">₹{totalAuthorizedFromClasses.toLocaleString('en-IN')}</p>
              {totalAuthorizedFromClasses > 0 && (
                <p className="text-xs text-gray-400 mt-1 italic">{numberToWords(totalAuthorizedFromClasses)}</p>
              )}
              <p className="text-[11px] text-gray-400 mt-1.5">Equity authorized total + Preference authorized total — fills in automatically as you add classes below.</p>
            </div>

            <div className="space-y-6">
              <ShareClassTable
                title="Equity Share Capital"
                classKey="equity_classes"
                data={data}
                setData={setData}
                ic={ic}
                actuallyAllottedShares={actuallyAllottedShares}
              />
              <ShareClassTable
                title="Preference Share Capital"
                classKey="preference_classes"
                data={data}
                setData={setData}
                ic={ic}
                actuallyAllottedShares={actuallyAllottedPreferenceShares}
              />
            </div>
          </div>
        )}

        {/* ── Members — only relevant when company has issued Preference shares ── */}
        {!isLLP && data.has_share_capital && (data.preference_classes || []).length > 0 && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <h4 className="text-base font-bold text-[#0f1f45] mb-4">Members</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Maximum number of members</label>
                <input type="number" value={data.max_members} onChange={e => updateField('max_members', e.target.value)} className={ic} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Max excluding proposed employees</label>
                <input type="number" value={data.max_members_excl_employees} onChange={e => updateField('max_members_excl_employees', e.target.value)} className={ic} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Number of members</label>
                <input type="number" value={data.number_of_members} onChange={e => updateField('number_of_members', e.target.value)} className={ic} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Number excluding proposed employees</label>
                <input type="number" value={data.number_of_members_excl_employees} onChange={e => updateField('number_of_members_excl_employees', e.target.value)} className={ic} />
              </div>
            </div>
          </div>
        )}

      </div>

      {/* ── Quick Summary ── */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 h-fit">
        <h4 className="text-base font-bold text-[#0f1f45] mb-4">Quick Summary</h4>
        <div className="space-y-3 text-sm">
          {isLLP ? (
            <>
              <InfoRow label="Total Contribution Declared" value={Number(data.total_authorized_capital) ? `₹${Number(data.total_authorized_capital).toLocaleString('en-IN')}` : '-'} />
              <InfoRow label="Contributed by Partners" value={`₹${actuallyAllottedShares.toLocaleString('en-IN')}`} />
              <InfoRow label="Remaining" value={`₹${Math.max(0, (Number(data.total_authorized_capital) || 0) - actuallyAllottedShares).toLocaleString('en-IN')}`} />
            </>
          ) : (
            <>
              <InfoRow label="Share Capital" value={data.has_share_capital ? 'Yes' : 'No'} />
              <InfoRow label="AOA Entrenched" value={data.aoa_entrenched ? 'Yes' : 'No'} />
              <InfoRow label="Authorized Capital" value={totalAuthorizedFromClasses ? `₹${totalAuthorizedFromClasses.toLocaleString('en-IN')}` : '-'} />
              <InfoRow label="Equity Subscribed" value={totalSubscribedShares ? `${totalSubscribedShares} shares / ₹${totalSubscribedAmount.toLocaleString('en-IN')}` : '-'} />
              <InfoRow label="Equity Allotted" value={`${actuallyAllottedShares} / ${totalSubscribedShares || 0}`} />
              {(data.preference_classes || []).length > 0 && (
                <>
                  <InfoRow label="Preference Subscribed" value={totalPreferenceSubscribedShares ? `${totalPreferenceSubscribedShares} shares / ₹${totalPreferenceSubscribedAmount.toLocaleString('en-IN')}` : '-'} />
                  <InfoRow label="Preference Allotted" value={`${actuallyAllottedPreferenceShares} / ${totalPreferenceSubscribedShares || 0}`} />
                </>
              )}
              <InfoRow label="Equity Classes" value={(data.equity_classes || []).length} />
              <InfoRow label="Preference Classes" value={(data.preference_classes || []).length} />
              <InfoRow label="Members" value={data.number_of_members || '-'} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa', 'Gujarat',
  'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh',
  'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
  'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh',
  'Uttarakhand', 'West Bengal',
  'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry',
];

function RegisteredOfficeTab({ data, setData }) {
  const ic = "w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-blue-500 bg-white";

  const updateField = (field, value) => setData(d => ({ ...d, [field]: value }));
  const updateAddress = (field, value) => setData(d => ({
    ...d,
    correspondence_address: { ...d.correspondence_address, [field]: value },
  }));

  // ── Utility bill freshness — MCA requires < 2 months old ────────────
  const billDate = data.utility_bill_date ? new Date(data.utility_bill_date) : null;
  const billAgeMonths = billDate
    ? (new Date().getFullYear() - billDate.getFullYear()) * 12 + (new Date().getMonth() - billDate.getMonth())
    : null;
  const billIsStale = billAgeMonths !== null && billAgeMonths >= 2;

  const addr = data.correspondence_address || {};

  return (
    <div className="grid grid-cols-3 gap-5">
      <div className="col-span-2 space-y-5">

        {/* ── Correspondence Address ── */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h4 className="text-base font-bold text-[#0f1f45] mb-1">Correspondence Address</h4>
          <p className="text-xs text-gray-400 mb-4">Where MCA sends all official communication for this company.</p>

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Address *</label>
              <textarea rows="2" value={addr.address} onChange={e => updateAddress('address', e.target.value)} placeholder="Full address" className={ic} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Pin Code *</label>
              <input value={addr.pincode} onChange={e => updateAddress('pincode', e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="6 digit PIN" maxLength={6} className={ic} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">District *</label>
              <input value={addr.district} onChange={e => updateAddress('district', e.target.value)} className={ic} />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">State/UT *</label>
              <select value={addr.state} onChange={e => updateAddress('state', e.target.value)} className={ic}>
                <option value="">Select state</option>
                {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Mobile *</label>
              <input value={addr.mobile} onChange={e => updateAddress('mobile', e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="10 digit number" maxLength={10} className={ic} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Company Email *</label>
              <input type="email" value={addr.company_email} onChange={e => updateAddress('company_email', e.target.value)} placeholder="company@example.com" className={ic} />
            </div>
          </div>
        </div>

        {/* ── Registered Office confirmation ── */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h4 className="text-base font-bold text-[#0f1f45] mb-1">Registered Office</h4>
          <p className="text-xs text-gray-400 mb-3">Is the correspondence address above also the registered office?</p>
          <div className="flex gap-3 mb-4">
            <button
              type="button"
              onClick={() => updateField('same_as_registered_office', true)}
              className={`flex-1 px-4 py-2.5 rounded-lg border text-sm font-medium ${data.same_as_registered_office ? 'bg-[#0f1f45] text-white border-[#0f1f45]' : 'bg-white text-gray-600 border-gray-200'}`}
            >
              Yes, same address
            </button>
            <button
              type="button"
              onClick={() => updateField('same_as_registered_office', false)}
              className={`flex-1 px-4 py-2.5 rounded-lg border text-sm font-medium ${!data.same_as_registered_office ? 'bg-[#0f1f45] text-white border-[#0f1f45]' : 'bg-white text-gray-600 border-gray-200'}`}
            >
              No
            </button>
          </div>

          {data.same_as_registered_office ? (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Longitude *</label>
                <input value={data.longitude} onChange={e => updateField('longitude', e.target.value)} placeholder="e.g. 77.209021" className={ic} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Latitude *</label>
                <input value={data.latitude} onChange={e => updateField('latitude', e.target.value)} placeholder="e.g. 28.613939" className={ic} />
              </div>
              <p className="col-span-2 text-[11px] text-gray-400">Required by MCA to geo-verify the registered premises. Use Google Maps — right click the location, coordinates are shown at the top.</p>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-100 rounded-lg p-4">
              <p className="text-sm font-semibold text-amber-700">No registered office at time of incorporation</p>
              <p className="text-xs text-amber-600 mt-1">
                Per the Companies Act, if the company doesn't have a registered office yet, Form INC-22 must be filed
                within 30 days from the Date of Incorporation. This is tracked automatically for you in the Post
                Incorporation tab once COI is received.
              </p>
            </div>
          )}
        </div>

        {/* ── ROC Jurisdiction ── */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h4 className="text-base font-bold text-[#0f1f45] mb-3">ROC Jurisdiction</h4>
          <label className="block text-xs font-medium text-gray-600 mb-1">Name of the Registrar of Companies office *</label>
          <input
            value={data.roc_office}
            onChange={e => updateField('roc_office', e.target.value)}
            placeholder="e.g. Registrar of Companies, Uttar Pradesh, Noida"
            className={ic}
          />
        </div>

        {/* ── Documents — only relevant when there IS a registered office at incorporation ── */}
        {data.same_as_registered_office && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <h4 className="text-base font-bold text-[#0f1f45] mb-4">Documents</h4>

            <div className="grid grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Proof of Office Address + NOC</label>
                <p className="text-[11px] text-gray-400 mb-2">Conveyance / Lease deed / Rent agreement with rent receipts</p>
                {data.noc_rent_agreement_file ? (
                  <div className="flex items-center gap-3">
                    <a href={data.noc_rent_agreement_file} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-blue-200 text-xs text-blue-600 hover:bg-blue-50">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      View
                    </a>
                    <a href={data.noc_rent_agreement_file} download={data.noc_rent_agreement_name} className="inline-flex items-center gap-1 text-xs text-green-600 hover:underline">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                      Download
                    </a>
                    <button
                      type="button"
                      onClick={() => setData(d => ({ ...d, noc_rent_agreement_file: null, noc_rent_agreement_name: '' }))}
                      className="text-xs text-red-500 hover:underline"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <label className="inline-flex items-center px-3 py-2 rounded-lg border border-gray-200 text-xs text-gray-600 cursor-pointer hover:bg-gray-50">
                    Upload NOC / Rent Agreement
                    <input
                      type="file"
                      className="hidden"
                      onChange={e => {
                        const file = e.target.files?.[0];
                        if (file) setData(d => ({ ...d, noc_rent_agreement_file: URL.createObjectURL(file), noc_rent_agreement_name: file.name }));
                      }}
                    />
                  </label>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Utility Bill</label>
                <p className="text-[11px] text-gray-400 mb-2">Must not be older than 2 months at time of filing</p>

                <div className="mb-2">
                  <label className="block text-[11px] text-gray-400 mb-1">Bill date</label>
                  <input
                    type="date"
                    value={data.utility_bill_date || ''}
                    onChange={e => updateField('utility_bill_date', e.target.value)}
                    className={`${ic} ${billIsStale ? 'border-red-400' : ''}`}
                  />
                  {billIsStale && (
                    <p className="text-xs text-red-500 mt-1">This bill is {billAgeMonths} months old — MCA requires under 2 months. Upload a newer bill.</p>
                  )}
                </div>

                {data.utility_bill_file ? (
                  <div className="flex items-center gap-3">
                    <a href={data.utility_bill_file} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-blue-200 text-xs text-blue-600 hover:bg-blue-50">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      View
                    </a>
                    <a href={data.utility_bill_file} download={data.utility_bill_name} className="inline-flex items-center gap-1 text-xs text-green-600 hover:underline">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                      Download
                    </a>
                    <button
                      type="button"
                      onClick={() => setData(d => ({ ...d, utility_bill_file: null, utility_bill_name: '' }))}
                      className="text-xs text-red-500 hover:underline"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <label className="inline-flex items-center px-3 py-2 rounded-lg border border-gray-200 text-xs text-gray-600 cursor-pointer hover:bg-gray-50">
                    Upload Utility Bill
                    <input
                      type="file"
                      className="hidden"
                      onChange={e => {
                        const file = e.target.files?.[0];
                        if (file) setData(d => ({ ...d, utility_bill_file: URL.createObjectURL(file), utility_bill_name: file.name }));
                      }}
                    />
                  </label>
                )}
              </div>
            </div>
          </div>
        )}

      </div>

      {/* ── Quick Summary ── */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 h-fit">
        <h4 className="text-base font-bold text-[#0f1f45] mb-4">Quick Summary</h4>
        <div className="space-y-3 text-sm">
          <InfoRow label="Pin Code" value={addr.pincode || '-'} />
          <InfoRow label="District" value={addr.district || '-'} />
          <InfoRow label="State" value={addr.state || '-'} />
          <InfoRow label="Registered Office at Incorporation" value={data.same_as_registered_office ? 'Yes' : 'No — INC-22 required post-incorp'} />
          <InfoRow label="ROC Office" value={data.roc_office || '-'} />
          {data.same_as_registered_office && (
            <>
              <InfoRow label="NOC / Rent Agreement" value={data.noc_rent_agreement_file ? 'Uploaded' : 'Pending'} />
              <InfoRow label="Utility Bill" value={data.utility_bill_file ? (billIsStale ? 'Stale' : 'Uploaded') : 'Pending'} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function OpcNomineeTab({ data, setData }) {
  const ic = "w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-blue-500 bg-white";
  const update = (field, value) => setData(d => ({ ...d, [field]: value }));

  const nomineeFullName = [data.nominee_first_name, data.nominee_middle_name, data.nominee_surname].filter(Boolean).join(' ');
  const bothFilesUploaded = !!data.identity_proof_file && !!data.residential_proof_file;

  return (
    <div className="grid grid-cols-3 gap-5">
      <div className="col-span-2 space-y-5">

        {/* ── Nomination declaration ── */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h4 className="text-base font-bold text-[#0f1f45] mb-1">Nomination</h4>
          <p className="text-xs text-gray-400 mb-4">
            As required for a One Person Company — the subscriber to the MOA nominates a person who becomes the
            member of the company in the event of the subscriber's death or incapacity to contract.
          </p>

          <div className="grid grid-cols-2 gap-4 mb-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Subscriber Name *</label>
              <input value={data.subscriber_name} onChange={e => update('subscriber_name', e.target.value)} placeholder="Sole subscriber's name" className={ic} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Nominee Full Name (preview)</label>
              <input readOnly value={nomineeFullName} placeholder="Fill nominee details below" className={`${ic} bg-gray-50 text-gray-500 cursor-not-allowed`} />
            </div>
          </div>

          {data.subscriber_name && nomineeFullName && (
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs text-blue-700 italic">
              "I, {data.subscriber_name}, the subscriber to the memorandum of association, do hereby nominate {nomineeFullName},
              who shall become the member of the company in the event of my death or incapacity to contract."
            </div>
          )}
        </div>

        {/* ── Nominee identity ── */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h4 className="text-base font-bold text-[#0f1f45] mb-4">Nominee Identity</h4>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">First Name *</label>
              <input value={data.nominee_first_name} onChange={e => update('nominee_first_name', e.target.value)} className={ic} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Middle Name</label>
              <input value={data.nominee_middle_name} onChange={e => update('nominee_middle_name', e.target.value)} className={ic} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Surname *</label>
              <input value={data.nominee_surname} onChange={e => update('nominee_surname', e.target.value)} className={ic} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Father's First Name *</label>
              <input value={data.father_first_name} onChange={e => update('father_first_name', e.target.value)} className={ic} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Father's Middle Name</label>
              <input value={data.father_middle_name} onChange={e => update('father_middle_name', e.target.value)} className={ic} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Father's Surname *</label>
              <input value={data.father_surname} onChange={e => update('father_surname', e.target.value)} className={ic} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Gender *</label>
              <select value={data.gender} onChange={e => update('gender', e.target.value)} className={ic}>
                <option value="">Select</option>
                <option>Male</option>
                <option>Female</option>
                <option>Transgender</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Date of Birth *</label>
              <input type="date" value={data.date_of_birth} onChange={e => update('date_of_birth', e.target.value)} className={ic} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Nationality *</label>
              <input value={data.nationality} onChange={e => update('nationality', e.target.value)} className={ic} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Income Tax PAN *</label>
              <input value={data.pan} onChange={e => update('pan', e.target.value.toUpperCase().slice(0, 10))} maxLength={10} placeholder="ABCDE1234F" className={ic} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Place of Birth — District *</label>
              <input value={data.place_of_birth_district} onChange={e => update('place_of_birth_district', e.target.value)} className={ic} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Place of Birth — State *</label>
              <select value={data.place_of_birth_state} onChange={e => update('place_of_birth_state', e.target.value)} className={ic}>
                <option value="">Select state</option>
                {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* ── Occupation & Education ── */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h4 className="text-base font-bold text-[#0f1f45] mb-4">Occupation &amp; Education</h4>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Occupation Type *</label>
              <select value={data.occupation_type} onChange={e => update('occupation_type', e.target.value)} className={ic}>
                <option value="">Select</option>
                <option>Business</option>
                <option>Professional</option>
                <option>Government</option>
                <option>Employment</option>
                <option>Private Employment</option>
                <option>Housewife</option>
                <option>Student</option>
                <option>Others</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Area of Occupation *</label>
              <input value={data.area_of_occupation} onChange={e => update('area_of_occupation', e.target.value)} className={ic} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Educational Qualification *</label>
              <select value={data.education_qualification} onChange={e => update('education_qualification', e.target.value)} className={ic}>
                <option value="">Select</option>
                <option>Primary education</option>
                <option>Secondary education</option>
                <option>Vocational qualification</option>
                <option>Bachelor's degree</option>
                <option>Master's degree</option>
                <option>Doctorate or higher</option>
                <option>Professional Diploma</option>
                <option>Others</option>
              </select>
            </div>
          </div>
        </div>

        {/* ── Contact & Address ── */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h4 className="text-base font-bold text-[#0f1f45] mb-4">Contact &amp; Address</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Mobile *</label>
              <input value={data.mobile} onChange={e => update('mobile', e.target.value.replace(/\D/g, '').slice(0, 10))} maxLength={10} className={ic} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Email *</label>
              <input type="email" value={data.email} onChange={e => update('email', e.target.value)} className={ic} />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Address *</label>
              <textarea rows="2" value={data.address} onChange={e => update('address', e.target.value)} placeholder="Full address" className={ic} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Pin Code *</label>
              <input value={data.pincode} onChange={e => update('pincode', e.target.value.replace(/\D/g, '').slice(0, 6))} maxLength={6} className={ic} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">District *</label>
              <input value={data.district} onChange={e => update('district', e.target.value)} className={ic} />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">State/UT *</label>
              <select value={data.state} onChange={e => update('state', e.target.value)} className={ic}>
                <option value="">Select state</option>
                {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* ── Proofs ── */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h4 className="text-base font-bold text-[#0f1f45] mb-4">Identity &amp; Residential Proof</h4>
          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Identity Proof Type *</label>
              <select value={data.identity_proof_type} onChange={e => update('identity_proof_type', e.target.value)} className={`${ic} mb-2`}>
                <option value="">Select</option>
                <option>Voter Identity Card</option>
                <option>Passport</option>
                <option>Driving License</option>
                <option>Aadhaar</option>
              </select>
              <input value={data.identity_proof_number} onChange={e => update('identity_proof_number', e.target.value)} placeholder="Proof number" className={`${ic} mb-2`} />
              {data.identity_proof_file ? (
                <div className="flex items-center gap-3">
                  <a href={data.identity_proof_file} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline">View</a>
                  <a href={data.identity_proof_file} download={data.identity_proof_file_name} className="text-xs text-green-600 hover:underline">Download</a>
                  <button type="button" onClick={() => setData(d => ({ ...d, identity_proof_file: null, identity_proof_file_name: '' }))} className="text-xs text-red-500 hover:underline">Remove</button>
                </div>
              ) : (
                <label className="inline-flex items-center px-3 py-2 rounded-lg border border-gray-200 text-xs text-gray-600 cursor-pointer hover:bg-gray-50">
                  Upload Identity Proof
                  <input type="file" className="hidden" onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) setData(d => ({ ...d, identity_proof_file: URL.createObjectURL(file), identity_proof_file_name: file.name }));
                  }} />
                </label>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Residential Proof Type *</label>
              <select value={data.residential_proof_type} onChange={e => update('residential_proof_type', e.target.value)} className={`${ic} mb-2`}>
                <option value="">Select</option>
                <option>Voter Identity Card</option>
                <option>Passport</option>
                <option>Driving License</option>
                <option>Aadhaar</option>
              </select>
              <input value={data.residential_proof_number} onChange={e => update('residential_proof_number', e.target.value)} placeholder="Proof number" className={`${ic} mb-2`} />
              {data.residential_proof_file ? (
                <div className="flex items-center gap-3">
                  <a href={data.residential_proof_file} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline">View</a>
                  <a href={data.residential_proof_file} download={data.residential_proof_file_name} className="text-xs text-green-600 hover:underline">Download</a>
                  <button type="button" onClick={() => setData(d => ({ ...d, residential_proof_file: null, residential_proof_file_name: '' }))} className="text-xs text-red-500 hover:underline">Remove</button>
                </div>
              ) : (
                <label className="inline-flex items-center px-3 py-2 rounded-lg border border-gray-200 text-xs text-gray-600 cursor-pointer hover:bg-gray-50">
                  Upload Residential Proof
                  <input type="file" className="hidden" onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) setData(d => ({ ...d, residential_proof_file: URL.createObjectURL(file), residential_proof_file_name: file.name }));
                  }} />
                </label>
              )}
            </div>
          </div>
        </div>

        {/* ── Declaration ── */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h4 className="text-base font-bold text-[#0f1f45] mb-3">Declaration by Nominee</h4>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={data.declaration_accepted}
              onChange={e => update('declaration_accepted', e.target.checked)}
              className="mt-0.5"
            />
            <span className="text-xs text-gray-600 leading-relaxed">
              I do solemnly declare that I am an Indian citizen and resident in India, have not been convicted of any
              offence in connection with the promotion, formation or management of any company or LLP, am not a
              nominee in any other One Person Company, and shall comply with the eligibility criteria specified in
              Rule 3(3) within the prescribed period. I understand that the person nominating me may withdraw my
              nomination without my consent. To be digitally signed by the nominee at filing.
            </span>
          </label>
        </div>

      </div>

      {/* ── Quick Summary ── */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 h-fit">
        <h4 className="text-base font-bold text-[#0f1f45] mb-4">Quick Summary</h4>
        <div className="space-y-3 text-sm">
          <InfoRow label="Subscriber" value={data.subscriber_name || '-'} />
          <InfoRow label="Nominee" value={nomineeFullName || '-'} />
          <InfoRow label="PAN" value={data.pan || '-'} />
          <InfoRow label="Mobile" value={data.mobile || '-'} />
          <InfoRow label="Proofs Uploaded" value={bothFilesUploaded ? 'Both uploaded' : 'Pending'} />
          <InfoRow label="Declaration" value={data.declaration_accepted ? 'Accepted' : 'Not accepted'} />
        </div>
      </div>
    </div>
  );
}

function StampDutyPanTanTab({ item, data, setData }) {
  const ic = "w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-blue-500 bg-white";
  const update = (field, value) => setData(d => ({ ...d, [field]: value }));

  const toggleIncomeSource = (source) => {
    setData(d => {
      const list = d.source_of_income || [];
      return {
        ...d,
        source_of_income: list.includes(source) ? list.filter(s => s !== source) : [...list, source],
      };
    });
  };

  const incomeOptions = ['Business/Profession', 'Capital Gains', 'House Property', 'Other Source', 'No Income'];

  return (
    <div className="grid grid-cols-3 gap-5">
      <div className="col-span-2 space-y-5">

        {/* ── AO Code Reference Sheet — MCA-provided lookup, not manually maintained data ── */}
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-5 flex items-center justify-between">
          <div>
            <h4 className="text-sm font-bold text-blue-800">PAN / TAN AO Code Reference Sheet</h4>
            <p className="text-xs text-blue-600 mt-1">
              Official MCA reference — look up the correct Area Code / AO Type / Range Code / AO No. for your jurisdiction before filling below.
            </p>
          </div>
          <a
            href="/uploads/reference/pan_tan_ao_codes.xlsx"
            download
            className="flex-shrink-0 px-4 py-2 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700"
          >
            Download Excel
          </a>
        </div>

        {/* ── PAN Information ── */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h4 className="text-base font-bold text-[#0f1f45] mb-1">Information Specific to PAN</h4>
          <p className="text-xs text-gray-400 mb-4">Jurisdiction codes for allotment of the company's Permanent Account Number.</p>
          <div className="grid grid-cols-4 gap-3">
            <div>
              <label className="block text-[11px] text-gray-400 mb-1">Area Code</label>
              <input value={data.pan_area_code} onChange={e => update('pan_area_code', e.target.value.toUpperCase())} className={ic} />
            </div>
            <div>
              <label className="block text-[11px] text-gray-400 mb-1">AO Type</label>
              <input value={data.pan_ao_type} onChange={e => update('pan_ao_type', e.target.value.toUpperCase())} className={ic} />
            </div>
            <div>
              <label className="block text-[11px] text-gray-400 mb-1">Range Code</label>
              <input value={data.pan_range_code} onChange={e => update('pan_range_code', e.target.value)} className={ic} />
            </div>
            <div>
              <label className="block text-[11px] text-gray-400 mb-1">AO No.</label>
              <input value={data.pan_ao_number} onChange={e => update('pan_ao_number', e.target.value)} className={ic} />
            </div>
          </div>
        </div>

        {/* ── TAN Information ── */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h4 className="text-base font-bold text-[#0f1f45] mb-1">Information Specific to TAN</h4>
          <p className="text-xs text-gray-400 mb-4">Jurisdiction codes for allotment of the company's Tax Deduction Account Number.</p>
          <div className="grid grid-cols-4 gap-3">
            <div>
              <label className="block text-[11px] text-gray-400 mb-1">Area Code</label>
              <input value={data.tan_area_code} onChange={e => update('tan_area_code', e.target.value.toUpperCase())} className={ic} />
            </div>
            <div>
              <label className="block text-[11px] text-gray-400 mb-1">AO Type</label>
              <input value={data.tan_ao_type} onChange={e => update('tan_ao_type', e.target.value.toUpperCase())} className={ic} />
            </div>
            <div>
              <label className="block text-[11px] text-gray-400 mb-1">Range Code</label>
              <input value={data.tan_range_code} onChange={e => update('tan_range_code', e.target.value)} className={ic} />
            </div>
            <div>
              <label className="block text-[11px] text-gray-400 mb-1">AO No.</label>
              <input value={data.tan_ao_number} onChange={e => update('tan_ao_number', e.target.value)} className={ic} />
            </div>
          </div>
        </div>

        {/* ── Source of Income ── */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h4 className="text-base font-bold text-[#0f1f45] mb-3">Source of Income</h4>
          <div className="flex flex-wrap gap-2">
            {incomeOptions.map(opt => (
              <button
                key={opt}
                type="button"
                onClick={() => toggleIncomeSource(opt)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                  (data.source_of_income || []).includes(opt)
                    ? 'bg-[#0f1f45] text-white border-[#0f1f45]'
                    : 'bg-white text-gray-600 border-gray-200'
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>

      </div>

      {/* ── Quick Summary ── */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 h-fit">
        <h4 className="text-base font-bold text-[#0f1f45] mb-4">Quick Summary</h4>
        <div className="space-y-3 text-sm">
          <InfoRow label="PAN Jurisdiction" value={data.pan_area_code ? `${data.pan_area_code} ${data.pan_ao_type} ${data.pan_range_code} ${data.pan_ao_number}` : '-'} />
          <InfoRow label="TAN Jurisdiction" value={data.tan_area_code ? `${data.tan_area_code} ${data.tan_ao_type} ${data.tan_range_code} ${data.tan_ao_number}` : '-'} />
          <InfoRow label="Source of Income" value={(data.source_of_income || []).length ? data.source_of_income.join(', ') : '-'} />
        </div>
      </div>
    </div>
  );
}

function PromotersPartnersTab({ item, peopleList, casePeople, setCasePeople, peopleForm, setPeopleForm, capitalStructure, setCapitalStructure }) {
  const isLLP = item.incorporation_type === 'LLP Incorporation';

  const roleOptions = isLLP
    ? ['Promoter', 'Designated Partner', 'Partner', 'Authorized Signatory']
    : ['Promoter', 'Director', 'Subscriber', 'Shareholder', 'Authorized Signatory'];

  const [entryMode, setEntryMode] = useState('existing');
  const [editingId, setEditingId] = useState(null);
  const [viewingPerson, setViewingPerson] = useState(null);
  const [openKebabId, setOpenKebabId] = useState(null);
  const [alertMsg, setAlertMsg] = useState(null);

  useEffect(() => {
    const closeKebab = (e) => {
      if (!e.target.closest('[data-kebab]')) setOpenKebabId(null);
    };
    document.addEventListener('mousedown', closeKebab);
    return () => document.removeEventListener('mousedown', closeKebab);
  }, []);

  // ── Indian number-to-words (Lakh/Crore system) for capital figures ──
  const numberToWords = (num) => {
    num = Math.floor(Number(num) || 0);
    if (num === 0) return '';
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
      'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

    const twoDigits = (n) => {
      if (n < 20) return ones[n];
      return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
    };
    const threeDigits = (n) => {
      if (n < 100) return twoDigits(n);
      return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + twoDigits(n % 100) : '');
    };

    const crore = Math.floor(num / 10000000);
    const lakh = Math.floor((num % 10000000) / 100000);
    const thousand = Math.floor((num % 100000) / 1000);
    const rest = num % 1000;

    let words = '';
    if (crore) words += threeDigits(crore) + ' Crore ';
    if (lakh) words += twoDigits(lakh) + ' Lakh ';
    if (thousand) words += twoDigits(thousand) + ' Thousand ';
    if (rest) words += threeDigits(rest);

    return words.trim() + ' Rupees Only';
  };

  // ── Capital / shareholding tracking ─────────────────────────────────
  // Ceiling = sum of SUBSCRIBED shares across all equity classes — this is
  // the actual pool declared in Structure & Capital and being distributed
  // among subscribers here. (Authorized is only the outer legal max that
  // Subscribed itself is validated against, one level up.)
  // For LLPs: units are plain contribution amount (₹), no share classes.
  const equityClasses = capitalStructure.equity_classes || [];
  const totalSharesAvailable = isLLP
    ? Number(capitalStructure.total_authorized_capital) || 0
    : equityClasses.reduce((sum, c) => sum + (Number(c.subscribed_shares) || 0), 0);

  const totalAllotted = casePeople
    .filter(p => p.id !== editingId)
    .reduce((sum, p) => sum + (Number(p.shares_or_contribution) || 0), 0);
  const draftAllotted = Number(peopleForm.shares_or_contribution) || 0;
  const remainingUnits = totalSharesAvailable - totalAllotted;
  const wouldExceed = draftAllotted > 0 && draftAllotted > remainingUnits;

  // ── Preference share tracking — only relevant if the company has issued
  //    Preference shares at all (Structure & Capital tab has ≥1 class)
  const preferenceClasses = capitalStructure.preference_classes || [];
  const hasPreferenceShares = preferenceClasses.length > 0;
  const totalPreferenceAvailable = preferenceClasses.reduce((sum, c) => sum + (Number(c.subscribed_shares) || 0), 0);
  const totalPreferenceAllotted = casePeople
    .filter(p => p.id !== editingId)
    .reduce((sum, p) => sum + (Number(p.preference_shares) || 0), 0);
  const draftPreferenceAllotted = Number(peopleForm.preference_shares) || 0;
  const remainingPreferenceUnits = totalPreferenceAvailable - totalPreferenceAllotted;
  const preferenceWouldExceed = draftPreferenceAllotted > 0 && draftPreferenceAllotted > remainingPreferenceUnits;

  // Auto-calculated shareholding % — replaces manual entry to avoid mismatch
  const autoPercent = !isLLP && totalSharesAvailable > 0 && draftAllotted > 0
    ? ((draftAllotted / totalSharesAvailable) * 100).toFixed(2)
    : '';

  const ic = "w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-blue-500 bg-white";

  // ── Format validators — catch data-entry mistakes at source ──────────
  const isValidPAN = (v) => !v || /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(v);
  const isValidAadhaar = (v) => !v || /^\d{4}\s\d{4}\s\d{4}$/.test(v);
  const isValidMobile = (v) => !v || /^\d{10}$/.test(v);
  const isValidEmail = (v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const formatAadhaar = (raw) => {
    const digits = raw.replace(/\D/g, '').slice(0, 12);
    return digits.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
  };

  const errIc = (valid) => `${ic} ${valid ? '' : 'border-red-400 focus:border-red-500'}`;

  const [newPerson, setNewPerson] = useState({
    entity_type: 'individual',
    full_name: '',
    date_of_birth: '',
    pan_number: '',
    pan_image: null,
    pan_image_name: '',
    aadhaar_number: '',
    aadhaar_image: null,
    aadhaar_image_name: '',
    photo: null,
    photo_name: '',
    signature: null,
    signature_name: '',
    primary_mobile: '',
    email: '',
    has_din: false,
    din: '',
    dpin: '',
    din_status: 'Active',
    address: '',
    bank_account_number: '',
    bank_statement_file: null,
    bank_statement_file_name: '',
    person_role: 'subscriber_director',
    designation: '',
    category: '',
    is_chairman: false,
    is_executive_director: false,
    is_non_executive_director: false,
    entity_cin: '',
    entity_name_corporate: '',
    authorized_rep_name: '',
  });

  // ── Interested Companies / LLPs — per-case declaration (applies to
  //     both existing and new person entry, since it's specific to this
  //     incorporation case, not a permanent attribute of the person) ──
  const [hasInterestedEntities, setHasInterestedEntities] = useState(false);
  const [interestedEntities, setInterestedEntities] = useState([]);
  const [entityDraft, setEntityDraft] = useState({ cin: '', designation: '', percentage: '', amount: '' });

  const addEntity = () => {
    if (!entityDraft.cin.trim()) return;
    setInterestedEntities(prev => [...prev, { ...entityDraft, id: Date.now() }]);
    setEntityDraft({ cin: '', designation: '', percentage: '', amount: '' });
  };

  const removeEntity = (id) => {
    setInterestedEntities(prev => prev.filter(e => e.id !== id));
  };

  const selectedPerson = peopleList.find(p => String(p.id) === String(peopleForm.person_id));

  const resetForms = () => {
    setPeopleForm({
      person_id: '',
      role: '',
      shareholding_percent: '',
      shares_or_contribution: '',
      preference_shares: '',
      contribution_type: 'Cash',
      contribution_type_other: '',
      remarks: '',
    });

    setNewPerson({
      entity_type: 'individual',
      full_name: '',
      date_of_birth: '',
      pan_number: '',
      pan_image: null,
      pan_image_name: '',
      aadhaar_number: '',
      aadhaar_image: null,
      aadhaar_image_name: '',
      photo: null,
      photo_name: '',
      signature: null,
      signature_name: '',
      primary_mobile: '',
      email: '',
      has_din: false,
      din: '',
      dpin: '',
      din_status: 'Active',
      address: '',
      bank_account_number: '',
      bank_statement_file: null,
      bank_statement_file_name: '',
      person_role: 'subscriber_director',
      designation: '',
      category: '',
      is_chairman: false,
      is_executive_director: false,
      is_non_executive_director: false,
      entity_cin: '',
      entity_name_corporate: '',
      authorized_rep_name: '',
    });

    setHasInterestedEntities(false);
    setInterestedEntities([]);
    setEntityDraft({ cin: '', designation: '', percentage: '', amount: '' });
  };

  const startEdit = (person) => {
    setEditingId(person.id);

    setPeopleForm({
      person_id: person.person_id || '',
      role: person.role || '',
      shareholding_percent: person.shareholding_percent || '',
      shares_or_contribution: person.shares_or_contribution || '',
      preference_shares: person.preference_shares || '',
      contribution_type: person.contribution_type || 'Cash',
      contribution_type_other: person.contribution_type_other || '',
      remarks: person.remarks || '',
    });

    if (person.source === 'New Person') {
      setEntryMode('new');
      setNewPerson({
        entity_type: person.entity_type || 'individual',
        full_name: person.full_name || '',
        date_of_birth: person.date_of_birth || '',
        pan_number: person.pan_number || '',
        pan_image: person.pan_image || null,
        pan_image_name: person.pan_image_name || '',
        aadhaar_number: person.aadhaar_number || '',
        aadhaar_image: person.aadhaar_image || null,
        aadhaar_image_name: person.aadhaar_image_name || '',
        photo: person.photo || null,
        photo_name: person.photo_name || '',
        signature: person.signature || null,
        signature_name: person.signature_name || '',
        primary_mobile: person.primary_mobile || '',
        email: person.email || '',
        has_din: person.has_din || false,
        din: person.din || '',
        dpin: person.dpin || '',
        din_status: person.din_status || 'Active',
        address: person.address || '',
        bank_account_number: person.bank_account_number || '',
        bank_statement_file: person.bank_statement_file || null,
        bank_statement_file_name: person.bank_statement_file_name || '',
        person_role: person.person_role || 'subscriber_director',
        designation: person.designation || '',
        category: person.category || '',
        is_chairman: person.is_chairman || false,
        is_executive_director: person.is_executive_director || false,
        is_non_executive_director: person.is_non_executive_director || false,
        entity_cin: person.entity_cin || '',
        entity_name_corporate: person.entity_name_corporate || '',
        authorized_rep_name: person.authorized_rep_name || '',
      });
    } else {
      setEntryMode('existing');
    }

    setHasInterestedEntities((person.interested_entities || []).length > 0);
    setInterestedEntities(person.interested_entities || []);
    setViewingPerson(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    resetForms();
  };

  // ── Derived from person_role — controls which sections show/validate ──
  const isCorporateEntry = entryMode === 'new' && newPerson.entity_type === 'corporate';
  const isSubscriberRole = isCorporateEntry || newPerson.person_role === 'subscriber_only' || newPerson.person_role === 'subscriber_director';
  const isDirectorRole = !isCorporateEntry && (newPerson.person_role === 'director_only' || newPerson.person_role === 'subscriber_director');

  const handleAddPerson = (e) => {
    e.preventDefault();

    if (entryMode === 'new') {
      if (isCorporateEntry) {
        if (!newPerson.entity_cin.trim()) { setAlertMsg('CIN / LLPIN / FCRN is required for a corporate subscriber.'); return; }
        if (!newPerson.entity_name_corporate.trim()) { setAlertMsg('Entity name is required for a corporate subscriber.'); return; }
      } else {
        if (!isValidPAN(newPerson.pan_number)) { setAlertMsg('PAN format is invalid — expected ABCDE1234F.'); return; }
        if (!isValidAadhaar(newPerson.aadhaar_number)) { setAlertMsg('Aadhaar must be 12 digits in 4-4-4 format.'); return; }
        if (!isValidMobile(newPerson.primary_mobile)) { setAlertMsg('Mobile number must be exactly 10 digits.'); return; }
        if (!isValidEmail(newPerson.email)) { setAlertMsg('Email must be a valid address containing @.'); return; }
        if (newPerson.has_din && !newPerson.din.trim() && !isLLP) { setAlertMsg('Enter the DIN, or toggle "Not having DIN" if not yet allotted.'); return; }
      }
    } else if (!selectedPerson) {
      return;
    }

    if (isSubscriberRole && wouldExceed) {
      setAlertMsg(
        `Cannot allot ${draftAllotted} ${isLLP ? 'contribution' : 'shares'} — only ${remainingUnits > 0 ? remainingUnits : 0} remaining out of the subscribed pool. Reduce the amount or increase Subscribed Shares in the Structure & Capital tab.`
      );
      return;
    }

    if (isSubscriberRole && preferenceWouldExceed) {
      setAlertMsg(
        `Cannot allot ${draftPreferenceAllotted} preference shares — only ${remainingPreferenceUnits > 0 ? remainingPreferenceUnits : 0} remaining out of the subscribed preference pool.`
      );
      return;
    }

    let personPayload = null;

    if (entryMode === 'existing') {
      personPayload = {
        person_id: selectedPerson.id,
        entity_type: 'individual',
        full_name: selectedPerson.full_name,
        date_of_birth: selectedPerson.date_of_birth || '',
        pan_number: selectedPerson.pan_number,
        aadhaar_number: selectedPerson.aadhaar_number,
        primary_mobile: selectedPerson.primary_mobile,
        email: selectedPerson.email,
        has_din: !!(selectedPerson.din || selectedPerson.dpin),
        din: selectedPerson.din,
        dpin: selectedPerson.dpin,
        din_status: selectedPerson.din_status || 'Active',
        address: selectedPerson.address_correspondence || selectedPerson.address_aadhaar || '',
        source: 'People Master',
      };
    } else if (isCorporateEntry) {
      personPayload = {
        person_id: null,
        entity_type: 'corporate',
        full_name: newPerson.entity_name_corporate,
        entity_cin: newPerson.entity_cin,
        entity_name_corporate: newPerson.entity_name_corporate,
        authorized_rep_name: newPerson.authorized_rep_name,
        has_din: false,
        source: 'New Person',
      };
    } else {
      if (!newPerson.full_name.trim()) return;

      personPayload = {
        person_id: null,
        entity_type: 'individual',
        full_name: newPerson.full_name,
        date_of_birth: newPerson.date_of_birth,
        pan_number: newPerson.pan_number,
        pan_image: newPerson.pan_image,
        pan_image_name: newPerson.pan_image_name,
        aadhaar_number: newPerson.aadhaar_number,
        aadhaar_image: newPerson.aadhaar_image,
        aadhaar_image_name: newPerson.aadhaar_image_name,
        photo: newPerson.photo,
        photo_name: newPerson.photo_name,
        signature: newPerson.signature,
        signature_name: newPerson.signature_name,
        primary_mobile: newPerson.primary_mobile,
        email: newPerson.email,
        has_din: newPerson.has_din,
        din: newPerson.has_din ? newPerson.din : '',
        dpin: newPerson.has_din ? newPerson.dpin : '',
        din_status: newPerson.din_status,
        address: newPerson.address,
        bank_account_number: newPerson.bank_account_number,
        bank_statement_file: newPerson.bank_statement_file,
        bank_statement_file_name: newPerson.bank_statement_file_name,
        source: 'New Person',
      };
    }

    const entry = {
      id: editingId || Date.now(),
      ...personPayload,
      person_role: isCorporateEntry ? 'subscriber_only' : newPerson.person_role,
      designation: isDirectorRole ? newPerson.designation : '',
      category: isDirectorRole ? newPerson.category : '',
      is_chairman: isDirectorRole ? newPerson.is_chairman : false,
      is_executive_director: isDirectorRole ? newPerson.is_executive_director : false,
      is_non_executive_director: isDirectorRole ? newPerson.is_non_executive_director : false,
      role: isLLP
        ? (isDirectorRole ? 'Designated Partner' : 'Partner')
        : (isDirectorRole && isSubscriberRole ? 'Subscriber & Director' : isDirectorRole ? 'Director' : 'Subscriber'),
      shareholding_percent: isLLP || !isSubscriberRole ? '' : (autoPercent || peopleForm.shareholding_percent),
      shares_or_contribution: isSubscriberRole ? peopleForm.shares_or_contribution : '',
      preference_shares: isSubscriberRole ? peopleForm.preference_shares : '',
      contribution_type: isLLP && isSubscriberRole ? peopleForm.contribution_type : '',
      contribution_type_other: isLLP && isSubscriberRole && peopleForm.contribution_type === 'Other than cash' ? peopleForm.contribution_type_other : '',
      remarks: peopleForm.remarks,
      interested_entities: (personPayload.has_din && hasInterestedEntities) ? interestedEntities : [],
    };

    if (editingId) {
      setCasePeople(prev => prev.map(p => p.id === editingId ? entry : p));
      setEditingId(null);
    } else {
      setCasePeople(prev => [entry, ...prev]);
    }
    resetForms();
  };

  const removePerson = (id) => {
    setCasePeople(prev => prev.filter(p => p.id !== id));
  };

  return (
    <div className="space-y-5">
      {/* ── Capital summary — read only. Editing happens in Structure & Capital tab ── */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
        <div className="flex items-center justify-between mb-1">
          <h4 className="text-base font-bold text-[#0f1f45]">Share allotment tracking</h4>
          <span className="text-xs text-blue-600">Set {isLLP ? 'total contribution' : 'subscribed capital'} in the {isLLP ? 'Capital Contribution' : 'Structure & Capital'} tab</span>
        </div>
        <p className="text-xs text-gray-400 mb-4">
          {isLLP ? 'Contribution' : 'Share'} allotment below is distributed out of the subscribed pool — you can't over-allot.
        </p>

        {totalSharesAvailable > 0 ? (
          <div className="flex items-center gap-4">
            <div className="bg-gray-50 rounded-lg px-3 py-2 flex flex-col justify-center min-w-[140px]">
              <p className="text-xs text-gray-400">{isLLP ? 'Total Contribution' : 'Total Subscribed Shares'}</p>
              <p className="text-lg font-bold text-[#0f1f45]">{totalSharesAvailable}</p>
            </div>
            <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
              <div
                className={`h-full rounded-full ${totalAllotted > totalSharesAvailable ? 'bg-red-500' : 'bg-green-500'}`}
                style={{ width: `${Math.min(100, (totalAllotted / totalSharesAvailable) * 100)}%` }}
              />
            </div>
            <p className="text-xs font-medium text-gray-500 whitespace-nowrap">
              {totalAllotted} / {totalSharesAvailable} allotted
              {' '}({remainingUnits >= 0 ? remainingUnits : 0} remaining)
            </p>
          </div>
        ) : (
          <div className="border border-dashed border-gray-200 rounded-lg p-4 text-center text-xs text-gray-400">
            {isLLP
              ? 'No total contribution set yet — visit the Capital Contribution tab and declare the total monetary value of contribution, then come back here to allot each partner’s share.'
              : 'No subscribed capital set yet — visit the Structure & Capital tab, enter Authorized shares and then Subscribed shares (the pool to distribute), then come back here to allot shares.'}
          </div>
        )}
      </div>

      {/* ── SPICe+ Part-B Matrix — Row 5 (Number of first subscribers & directors) ── */}
      {!isLLP && (() => {
        const isSub = p => p.person_role === 'subscriber_only' || p.person_role === 'subscriber_director' || p.entity_type === 'corporate';
        const isSubDir = p => p.person_role === 'subscriber_director' && p.entity_type !== 'corporate';
        const isDirOnly = p => p.person_role === 'director_only';
        const hasDIN = p => !!(p.has_din);

        const subscribers = casePeople.filter(isSub);
        const nonIndividualSubs = subscribers.filter(p => p.entity_type === 'corporate');
        const individualSubs = subscribers.filter(p => p.entity_type !== 'corporate');

        const rowA = { yes: individualSubs.filter(hasDIN).length, no: individualSubs.filter(p => !hasDIN(p)).length + nonIndividualSubs.length };
        const rowB = { yes: 0, no: nonIndividualSubs.length };
        const subDirs = casePeople.filter(isSubDir);
        const rowC = { yes: subDirs.filter(hasDIN).length, no: subDirs.filter(p => !hasDIN(p)).length };
        const dirOnly = casePeople.filter(isDirOnly);
        const rowD = { yes: dirOnly.filter(hasDIN).length + rowC.yes, no: dirOnly.filter(p => !hasDIN(p)).length + rowC.no };

        const rows = [
          ['(a) Total first subscribers (individual + non-individual)', rowA],
          ['(b) Non-individual first subscribers', rowB],
          ['(c) Individual first subscriber(s) cum director(s)', rowC],
          ['(d) Total number of directors', rowD],
        ];

        return (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <h4 className="text-base font-bold text-[#0f1f45]">Number of First Subscriber(s) & Directors</h4>
              <p className="text-xs text-gray-400 mt-1">Matches SPICe+ Part-B Section 5 — updates live as you add people below.</p>
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="px-5 py-2.5 text-left text-xs font-semibold text-gray-500"></th>
                  <th className="px-5 py-2.5 text-center text-xs font-semibold text-gray-500 uppercase">Having valid DIN</th>
                  <th className="px-5 py-2.5 text-center text-xs font-semibold text-gray-500 uppercase">Not having valid DIN</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(([label, val]) => (
                  <tr key={label} className="border-b border-gray-50">
                    <td className="px-5 py-2.5 text-sm text-gray-700">{label}</td>
                    <td className="px-5 py-2.5 text-center text-sm font-semibold text-[#0f1f45]">{val.yes}</td>
                    <td className="px-5 py-2.5 text-center text-sm font-semibold text-[#0f1f45]">{val.no}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })()}

    <div className="grid grid-cols-3 gap-5">
      <form onSubmit={handleAddPerson} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-visible col-span-1">
        <div className={`px-5 py-4 border-b border-gray-100 ${editingId ? 'bg-blue-50' : ''}`}>
          <h4 className="text-base font-bold text-[#0f1f45]">
            {editingId ? `Editing ${isLLP ? 'Partner' : 'Promoter'}` : `Add ${isLLP ? 'Partner' : 'Promoter'}`}
          </h4>
          <p className="text-xs text-gray-400 mt-1">
            {editingId ? 'Update the details below and save.' : 'Select existing person or add a new person for this incorporation.'}
          </p>
        </div>

        <div className="p-5 space-y-4">
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            <button
              type="button"
              onClick={() => setEntryMode('existing')}
              className={`flex-1 py-2 text-sm font-medium ${
                entryMode === 'existing' ? 'bg-[#0f1f45] text-white' : 'bg-white text-gray-600'
              }`}
            >
              Existing
            </button>

            <button
              type="button"
              onClick={() => setEntryMode('new')}
              className={`flex-1 py-2 text-sm font-medium border-l border-gray-200 ${
                entryMode === 'new' ? 'bg-[#0f1f45] text-white' : 'bg-white text-gray-600'
              }`}
            >
              New Person
            </button>
          </div>

          {entryMode === 'new' && (
            <div className="flex rounded-lg border border-gray-200 overflow-hidden">
              <button
                type="button"
                onClick={() => setNewPerson(p => ({ ...p, entity_type: 'individual' }))}
                className={`flex-1 py-2 text-xs font-medium ${
                  newPerson.entity_type === 'individual' ? 'bg-purple-600 text-white' : 'bg-white text-gray-600'
                }`}
              >
                Individual
              </button>
              <button
                type="button"
                onClick={() => setNewPerson(p => ({ ...p, entity_type: 'corporate' }))}
                className={`flex-1 py-2 text-xs font-medium border-l border-gray-200 ${
                  newPerson.entity_type === 'corporate' ? 'bg-purple-600 text-white' : 'bg-white text-gray-600'
                }`}
              >
                {isLLP ? 'Body Corporate' : 'Corporate (Body Corporate)'}
              </button>
            </div>
          )}

          {entryMode === 'existing' ? (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Person *</label>
              <select
                required={entryMode === 'existing'}
                value={peopleForm.person_id}
                onChange={e => setPeopleForm(f => ({ ...f, person_id: e.target.value }))}
                className={ic}
              >
                <option value="">Select Person</option>
                {peopleList.map(person => (
                  <option key={person.id} value={person.id}>
                    {person.full_name} {person.pan_number ? `- ${person.pan_number}` : ''}
                  </option>
                ))}
              </select>

              {peopleList.length === 0 && (
                <p className="text-xs text-orange-500 mt-1">
                  No people found. Use New Person option.
                </p>
              )}
            </div>
          ) : isCorporateEntry ? (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">CIN / LLPIN / FCRN *</label>
                <input
                  value={newPerson.entity_cin}
                  onChange={e => setNewPerson(p => ({ ...p, entity_cin: e.target.value.toUpperCase() }))}
                  placeholder="e.g. U72900UP2020PTC123456"
                  className={ic}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Entity Name *</label>
                <input
                  value={newPerson.entity_name_corporate}
                  onChange={e => setNewPerson(p => ({ ...p, entity_name_corporate: e.target.value }))}
                  placeholder="Body corporate's registered name"
                  className={ic}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Authorized Representative Name</label>
                <input
                  value={newPerson.authorized_rep_name}
                  onChange={e => setNewPerson(p => ({ ...p, authorized_rep_name: e.target.value }))}
                  placeholder="Person authorized to sign on the entity's behalf"
                  className={ic}
                />
              </div>
              <p className="text-xs text-gray-400">
                A corporate subscriber always participates as a subscriber — director designation and DIN don't apply to the entity itself.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Full Name *</label>
                <input
                  required={entryMode === 'new'}
                  value={newPerson.full_name}
                  onChange={e => setNewPerson(p => ({ ...p, full_name: e.target.value }))}
                  placeholder="Person name"
                  className={ic}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">PAN</label>
                  <input
                    value={newPerson.pan_number}
                    onChange={e => setNewPerson(p => ({ ...p, pan_number: e.target.value.toUpperCase().slice(0, 10) }))}
                    placeholder="ABCDE1234F"
                    maxLength={10}
                    className={errIc(isValidPAN(newPerson.pan_number))}
                  />
                  {!isValidPAN(newPerson.pan_number) && (
                    <p className="text-xs text-red-500 mt-1">Format must be ABCDE1234F</p>
                  )}
                  <label className="inline-flex items-center gap-1.5 mt-1.5 text-xs text-blue-600 cursor-pointer hover:underline">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                    {newPerson.pan_image_name || 'Upload PAN image'}
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      className="hidden"
                      onChange={e => {
                        const file = e.target.files?.[0];
                        if (file) setNewPerson(p => ({ ...p, pan_image: URL.createObjectURL(file), pan_image_name: file.name }));
                      }}
                    />
                  </label>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Aadhaar</label>
                  <input
                    value={newPerson.aadhaar_number}
                    onChange={e => setNewPerson(p => ({ ...p, aadhaar_number: formatAadhaar(e.target.value) }))}
                    placeholder="1234 5678 9012"
                    maxLength={14}
                    className={errIc(isValidAadhaar(newPerson.aadhaar_number))}
                  />
                  {!isValidAadhaar(newPerson.aadhaar_number) && (
                    <p className="text-xs text-red-500 mt-1">Must be 12 digits — 4 4 4</p>
                  )}
                  <label className="inline-flex items-center gap-1.5 mt-1.5 text-xs text-blue-600 cursor-pointer hover:underline">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                    {newPerson.aadhaar_image_name || 'Upload Aadhaar image'}
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      className="hidden"
                      onChange={e => {
                        const file = e.target.files?.[0];
                        if (file) setNewPerson(p => ({ ...p, aadhaar_image: URL.createObjectURL(file), aadhaar_image_name: file.name }));
                      }}
                    />
                  </label>
                </div>
              </div>
              <p className="text-xs text-gray-400 -mt-1">
                Auto-fill from these images (OCR) is planned for a future update — for now they're stored against this person's record for quick reference.
              </p>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Mobile</label>
                  <div className={`flex items-stretch rounded-lg border overflow-hidden ${!isValidMobile(newPerson.primary_mobile) ? 'border-red-400' : 'border-gray-200'}`}>
                    <span className="px-3 flex items-center bg-gray-50 text-sm text-gray-500 border-r border-gray-200">+91</span>
                    <input
                      value={newPerson.primary_mobile}
                      onChange={e => setNewPerson(p => ({ ...p, primary_mobile: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                      placeholder="10 digit number"
                      maxLength={10}
                      className="flex-1 px-3 py-2.5 text-sm focus:outline-none bg-white"
                    />
                  </div>
                  {!isValidMobile(newPerson.primary_mobile) && (
                    <p className="text-xs text-red-500 mt-1">Must be exactly 10 digits</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                  <input
                    type="email"
                    value={newPerson.email}
                    onChange={e => setNewPerson(p => ({ ...p, email: e.target.value }))}
                    placeholder="name@example.com"
                    className={errIc(isValidEmail(newPerson.email))}
                  />
                  {!isValidEmail(newPerson.email) && (
                    <p className="text-xs text-red-500 mt-1">Must contain @ and a valid domain</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Photograph</label>
                  <label className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border border-gray-200 text-xs text-gray-600 cursor-pointer hover:bg-gray-50">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
                    {newPerson.photo_name || 'Upload photograph'}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={e => {
                        const file = e.target.files?.[0];
                        if (file) setNewPerson(p => ({ ...p, photo: URL.createObjectURL(file), photo_name: file.name }));
                      }}
                    />
                  </label>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Signature</label>
                  <label className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border border-gray-200 text-xs text-gray-600 cursor-pointer hover:bg-gray-50">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 19l7-7 3 3-7 7-3-3z"/><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/><path d="M2 2l7.586 7.586"/><circle cx="11" cy="11" r="2"/></svg>
                    {newPerson.signature_name || 'Upload signature'}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={e => {
                        const file = e.target.files?.[0];
                        if (file) setNewPerson(p => ({ ...p, signature: URL.createObjectURL(file), signature_name: file.name }));
                      }}
                    />
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Date of Birth</label>
                <input
                  type="date"
                  value={newPerson.date_of_birth}
                  onChange={e => setNewPerson(p => ({ ...p, date_of_birth: e.target.value }))}
                  className={ic}
                />
              </div>

              <div className="border border-gray-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-gray-700 mb-2">{isLLP ? 'DPIN' : 'DIN'} Status</p>
                <div className="flex rounded-lg border border-gray-200 overflow-hidden mb-2">
                  <button
                    type="button"
                    onClick={() => setNewPerson(p => ({ ...p, has_din: true }))}
                    className={`flex-1 py-1.5 text-xs font-medium ${newPerson.has_din ? 'bg-[#0f1f45] text-white' : 'bg-white text-gray-600'}`}
                  >
                    Having {isLLP ? 'DPIN' : 'DIN'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewPerson(p => ({ ...p, has_din: false, din: '', dpin: '' }))}
                    className={`flex-1 py-1.5 text-xs font-medium border-l border-gray-200 ${!newPerson.has_din ? 'bg-[#0f1f45] text-white' : 'bg-white text-gray-600'}`}
                  >
                    Not Having {isLLP ? 'DPIN' : 'DIN'}
                  </button>
                </div>

                {newPerson.has_din ? (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[11px] text-gray-400 mb-1">{isLLP ? 'DPIN' : 'DIN'} Number</label>
                      <input
                        value={isLLP ? newPerson.dpin : newPerson.din}
                        onChange={e => setNewPerson(p => isLLP ? { ...p, dpin: e.target.value } : { ...p, din: e.target.value })}
                        placeholder={isLLP ? 'DPIN' : 'DIN'}
                        className={ic}
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] text-gray-400 mb-1">Status</label>
                      <select
                        value={newPerson.din_status}
                        onChange={e => setNewPerson(p => ({ ...p, din_status: e.target.value }))}
                        className={ic}
                      >
                        <option>Active</option>
                        <option>Deactivated</option>
                      </select>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 italic">
                    This person will be allotted a {isLLP ? 'DPIN' : 'DIN'} only after incorporation of the company, via SPICe+.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Address</label>
                <input
                  value={newPerson.address}
                  onChange={e => setNewPerson(p => ({ ...p, address: e.target.value }))}
                  placeholder="Address"
                  className={ic}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Bank Account No.</label>
                  <input
                    value={newPerson.bank_account_number}
                    onChange={e => setNewPerson(p => ({ ...p, bank_account_number: e.target.value.replace(/\D/g, '') }))}
                    placeholder="Account number"
                    className={ic}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Bank Statement <span className="text-gray-400 font-normal">(as Residential Proof)</span></label>
                  <label className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border border-gray-200 text-xs text-gray-600 cursor-pointer hover:bg-gray-50">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                    {newPerson.bank_statement_file_name || 'Upload bank statement'}
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      className="hidden"
                      onChange={e => {
                        const file = e.target.files?.[0];
                        if (file) setNewPerson(p => ({ ...p, bank_statement_file: URL.createObjectURL(file), bank_statement_file_name: file.name }));
                      }}
                    />
                  </label>
                </div>
              </div>
            </div>
          )}

          {!isCorporateEntry && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-2">Person's Role in this Case *</label>
              <div className="grid grid-cols-3 gap-2">
                {(isLLP ? [
                  { key: 'subscriber_only', label: 'Partner Only' },
                  { key: 'director_only', label: 'Designated Partner Only' },
                  { key: 'subscriber_director', label: 'Designated Partner + Partner' },
                ] : [
                  { key: 'subscriber_only', label: 'Subscriber Only' },
                  { key: 'director_only', label: 'Director Only' },
                  { key: 'subscriber_director', label: 'Subscriber + Director' },
                ]).map(opt => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setNewPerson(p => ({ ...p, person_role: opt.key }))}
                    className={`px-2 py-2 rounded-lg border text-xs font-medium ${
                      newPerson.person_role === opt.key ? 'bg-[#0f1f45] text-white border-[#0f1f45]' : 'bg-white text-gray-600 border-gray-200'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {isDirectorRole && !isLLP && (
            <div className="border border-gray-200 rounded-lg p-3 space-y-3">
              <p className="text-xs font-semibold text-gray-700">Director Details</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] text-gray-400 mb-1">Designation</label>
                  <select
                    value={newPerson.designation}
                    onChange={e => setNewPerson(p => ({ ...p, designation: e.target.value }))}
                    className={ic}
                  >
                    <option value="">Select</option>
                    <option>Director</option>
                    <option>Managing Director</option>
                    <option>Whole Time Director</option>
                    <option>Nominee Director</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] text-gray-400 mb-1">Category</label>
                  <select
                    value={newPerson.category}
                    onChange={e => setNewPerson(p => ({ ...p, category: e.target.value }))}
                    className={ic}
                  >
                    <option value="">Select</option>
                    <option>Promoter</option>
                    <option>Professional</option>
                    <option>Independent</option>
                    <option>Nominee</option>
                  </select>
                </div>
              </div>
              <div>
                <p className="text-[11px] text-gray-400 mb-1">Whether</p>
                <div className="flex gap-4">
                  {[
                    { key: 'is_chairman', label: 'Chairman' },
                    { key: 'is_executive_director', label: 'Executive Director' },
                    { key: 'is_non_executive_director', label: 'Non-executive Director' },
                  ].map(opt => (
                    <label key={opt.key} className="flex items-center gap-1.5 text-xs text-gray-600">
                      <input
                        type="checkbox"
                        checked={newPerson[opt.key]}
                        onChange={e => setNewPerson(p => ({ ...p, [opt.key]: e.target.checked }))}
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {isSubscriberRole && (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {isLLP ? 'Contribution Amount' : 'No. of Shares'}
                </label>
                <input
                  type="number"
                  value={peopleForm.shares_or_contribution}
                  onChange={e => setPeopleForm(f => ({ ...f, shares_or_contribution: e.target.value }))}
                  placeholder={isLLP ? 'Contribution amount' : 'No. of shares'}
                  className={`${ic} ${wouldExceed ? 'border-red-400' : ''}`}
                />
                {totalSharesAvailable > 0 && (
                  <p className={`text-xs mt-1 ${wouldExceed ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
                    {remainingUnits >= 0 ? remainingUnits : 0} {isLLP ? '₹' : 'shares'} remaining out of {totalSharesAvailable} {isLLP ? 'total contribution' : 'total shares'}
                    {wouldExceed && ' — exceeds availability!'}
                  </p>
                )}
              </div>

              {isLLP && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Form of Contribution</label>
                  <select
                    value={peopleForm.contribution_type}
                    onChange={e => setPeopleForm(f => ({ ...f, contribution_type: e.target.value }))}
                    className={ic}
                  >
                    <option value="Cash">Cash</option>
                    <option value="Other than cash">Other than cash</option>
                    <option value="Conversion">Conversion</option>
                  </select>
                  {peopleForm.contribution_type === 'Other than cash' && (
                    <input
                      value={peopleForm.contribution_type_other}
                      onChange={e => setPeopleForm(f => ({ ...f, contribution_type_other: e.target.value }))}
                      placeholder="Specify nature of contribution (e.g. equipment, property)"
                      className={`${ic} mt-2`}
                    />
                  )}
                </div>
              )}

              {!isLLP && hasPreferenceShares && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">No. of Preference Shares</label>
                  <input
                    type="number"
                    value={peopleForm.preference_shares}
                    onChange={e => setPeopleForm(f => ({ ...f, preference_shares: e.target.value }))}
                    placeholder="No. of preference shares (if any)"
                    className={`${ic} ${preferenceWouldExceed ? 'border-red-400' : ''}`}
                  />
                  {totalPreferenceAvailable > 0 && (
                    <p className={`text-xs mt-1 ${preferenceWouldExceed ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
                      {remainingPreferenceUnits >= 0 ? remainingPreferenceUnits : 0} shares remaining out of {totalPreferenceAvailable} total preference shares
                      {preferenceWouldExceed && ' — exceeds availability!'}
                    </p>
                  )}
                </div>
              )}

              {!isLLP && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Shareholding % <span className="text-gray-400 font-normal">(auto-calculated)</span>
                  </label>
                  <input
                    readOnly
                    value={autoPercent ? `${autoPercent}%` : ''}
                    placeholder="Enter shares above to calculate"
                    className={`${ic} bg-gray-50 text-gray-500 cursor-not-allowed`}
                  />
                </div>
              )}
            </>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Remarks</label>
            <input
              value={peopleForm.remarks}
              onChange={e => setPeopleForm(f => ({ ...f, remarks: e.target.value }))}
              placeholder="Optional note"
              className={ic}
            />
          </div>

          {/* ── Interested Companies / LLP — declaration u/s 184 — only if DIN held ── */}
          {(entryMode === 'existing' ? !!(selectedPerson?.din || selectedPerson?.dpin) : newPerson.has_din) && (
          <div className="border border-gray-200 rounded-lg p-3">
            <button
              type="button"
              onClick={() => setHasInterestedEntities(v => !v)}
              className={`w-full flex items-center justify-between text-left`}
            >
              <div>
                <p className="text-xs font-semibold text-gray-700">Interested in other Companies / LLPs?</p>
                <p className="text-xs text-gray-400 mt-0.5">Declaration u/s 184 — entities where this person is a director/partner or holds interest</p>
              </div>
              <span className={`w-9 h-5 rounded-full flex items-center px-0.5 transition-colors flex-shrink-0 ml-3 ${hasInterestedEntities ? 'bg-[#0f1f45] justify-end' : 'bg-gray-200 justify-start'}`}>
                <span className="w-4 h-4 rounded-full bg-white block" />
              </span>
            </button>

            {hasInterestedEntities && (
              <div className="mt-3 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <input
                    value={entityDraft.cin}
                    onChange={e => setEntityDraft(d => ({ ...d, cin: e.target.value.toUpperCase() }))}
                    placeholder="CIN / LLPIN"
                    className="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-xs focus:outline-none focus:border-blue-500 bg-white"
                  />
                  <input
                    value={entityDraft.designation}
                    onChange={e => setEntityDraft(d => ({ ...d, designation: e.target.value }))}
                    placeholder="Designation"
                    className="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-xs focus:outline-none focus:border-blue-500 bg-white"
                  />
                  <input
                    type="number"
                    value={entityDraft.percentage}
                    onChange={e => setEntityDraft(d => ({ ...d, percentage: e.target.value }))}
                    placeholder="% Holding"
                    className="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-xs focus:outline-none focus:border-blue-500 bg-white"
                  />
                  <input
                    type="number"
                    value={entityDraft.amount}
                    onChange={e => setEntityDraft(d => ({ ...d, amount: e.target.value }))}
                    placeholder="Amount (₹)"
                    className="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-xs focus:outline-none focus:border-blue-500 bg-white"
                  />
                </div>
                <p className="text-xs text-gray-400">
                  Name & registered address will be auto-fetched via CIN — no need to type them here.
                </p>
                <button
                  type="button"
                  onClick={addEntity}
                  className="w-full px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-medium text-gray-600 hover:bg-gray-50"
                >
                  + Add Entity
                </button>

                {interestedEntities.length > 0 && (
                  <div className="space-y-1.5 pt-1">
                    {interestedEntities.map(ent => (
                      <div key={ent.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-2.5 py-1.5 text-xs">
                        <div>
                          <span className="font-medium text-gray-700">{ent.cin}</span>
                          <span className="text-gray-400"> · {ent.designation || '-'} · {ent.percentage || '0'}% · ₹{ent.amount || '0'}</span>
                        </div>
                        <button type="button" onClick={() => removeEntity(ent.id)} className="text-red-500 hover:underline">
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          )}

          {entryMode === 'existing' && selectedPerson && (
            <div className="bg-gray-50 rounded-xl p-3 text-xs text-gray-500 space-y-1">
              <p><span className="font-medium text-gray-700">PAN:</span> {selectedPerson.pan_number || '-'}</p>
              <p><span className="font-medium text-gray-700">{isLLP ? 'DPIN' : 'DIN'}:</span> {isLLP ? selectedPerson.dpin || '-' : selectedPerson.din || '-'}</p>
              <p><span className="font-medium text-gray-700">DOB:</span> {selectedPerson.date_of_birth ? new Date(selectedPerson.date_of_birth).toLocaleDateString('en-IN') : '-'}</p>
              <p><span className="font-medium text-gray-700">DIN Status:</span> {selectedPerson.din_status || 'Active'}</p>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-gray-100 bg-gray-50 flex gap-2">
          {editingId && (
            <button
              type="button"
              onClick={cancelEdit}
              className="px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-gray-600 text-sm font-medium"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            className="flex-1 px-5 py-2.5 rounded-lg text-white text-sm font-medium bg-[#0f1f45]"
          >
            {editingId ? 'Save Changes' : 'Add to Case'}
          </button>
        </div>
      </form>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-visible col-span-2">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h4 className="text-base font-bold text-[#0f1f45]">
              {isLLP ? 'Designated Partners / Partners' : 'Directors / Subscribers / Shareholders'}
            </h4>
            <p className="text-xs text-gray-400 mt-1">
              {casePeople.length} person{casePeople.length !== 1 ? 's' : ''} added to this incorporation case.
            </p>
          </div>
        </div>

        {casePeople.length === 0 ? (
          <div className="py-14 text-center text-gray-400 text-sm">
            No person added yet.
          </div>
        ) : (
          <div className="p-5 grid grid-cols-2 gap-4">
            {casePeople.map(person => (
              <div key={person.id} className="relative rounded-xl border border-gray-100 hover:border-blue-100 hover:shadow-sm transition-all p-4">
                {/* Kebab menu */}
                <div className="absolute top-3 right-3" data-kebab>
                  <button
                    onClick={() => setOpenKebabId(openKebabId === person.id ? null : person.id)}
                    className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="1.8"/><circle cx="12" cy="12" r="1.8"/><circle cx="12" cy="19" r="1.8"/></svg>
                  </button>

                  {openKebabId === person.id && (
                    <div className="absolute right-0 top-8 bg-white border border-gray-200 rounded-xl shadow-lg z-20 w-36 p-1.5">
                      <button
                        onClick={() => { setViewingPerson(person); setOpenKebabId(null); }}
                        className="w-full text-left px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                        View
                      </button>
                      <button
                        onClick={() => { startEdit(person); setOpenKebabId(null); }}
                        className="w-full text-left px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        Edit
                      </button>
                      <button
                        onClick={() => { removePerson(person.id); setOpenKebabId(null); }}
                        className="w-full text-left px-3 py-2 rounded-lg text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
                        Delete
                      </button>
                    </div>
                  )}
                </div>

                {/* Header — photo + name + role */}
                <div className="flex items-center gap-3 mb-3">
                  {person.photo ? (
                    <img src={person.photo} alt={person.full_name} className="w-11 h-11 rounded-full object-cover border border-gray-100" />
                  ) : (
                    <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#0f1f45] to-blue-900 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                      {(person.full_name || '?').charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 pr-8">
                    <p className="text-sm font-bold text-gray-800 truncate">{person.full_name}</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">{person.role}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        (person.din_status || 'Active') === 'Active' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'
                      }`}>
                        {person.din_status || 'Active'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Details grid */}
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">PAN</span>
                    <span className="font-medium text-gray-700">{person.pan_number || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">{isLLP ? 'DPIN' : 'DIN'}</span>
                    <span className="font-medium text-gray-700">{isLLP ? person.dpin || '-' : person.din || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Mobile</span>
                    <span className="font-medium text-gray-700">{person.primary_mobile ? `+91 ${person.primary_mobile}` : '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Email</span>
                    <span className="font-medium text-gray-700 truncate max-w-[160px]">{person.email || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">{isLLP ? 'Contribution' : 'Holding'}</span>
                    <span className="font-medium text-gray-700">
                      {isLLP ? person.shares_or_contribution || '-' : person.shareholding_percent ? `${person.shareholding_percent}%` : '-'}
                    </span>
                  </div>
                </div>

                {person.interested_entities && person.interested_entities.length > 0 && (
                  <span className="inline-block mt-2 px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-50 text-purple-600">
                    Interested in {person.interested_entities.length} {person.interested_entities.length === 1 ? 'entity' : 'entities'}
                  </span>
                )}

                <p className="text-[10px] text-gray-300 mt-2 uppercase tracking-wide">{person.source}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>

    {/* ── View Details modal — shows every field captured for this person ── */}
    {viewingPerson && (
      <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setViewingPerson(null)}>
        <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white">
            <div>
              <h4 className="text-base font-bold text-[#0f1f45]">{viewingPerson.full_name}</h4>
              <p className="text-xs text-gray-400 mt-0.5">{viewingPerson.role} · {viewingPerson.source}</p>
            </div>
            <button onClick={() => setViewingPerson(null)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
          </div>

          <div className="p-6 space-y-5">
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Personal Details</p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <p><span className="text-gray-400">Date of Birth:</span> <span className="text-gray-700 font-medium">{viewingPerson.date_of_birth ? new Date(viewingPerson.date_of_birth).toLocaleDateString('en-IN') : '-'}</span></p>
                <p><span className="text-gray-400">Mobile:</span> <span className="text-gray-700 font-medium">{viewingPerson.primary_mobile || '-'}</span></p>
                <p><span className="text-gray-400">Email:</span> <span className="text-gray-700 font-medium">{viewingPerson.email || '-'}</span></p>
                <p className="col-span-2"><span className="text-gray-400">Address:</span> <span className="text-gray-700 font-medium">{viewingPerson.address || '-'}</span></p>
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Uploaded Documents</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Photograph', number: null, file: viewingPerson.photo, name: viewingPerson.photo_name },
                  { label: 'Signature', number: null, file: viewingPerson.signature, name: viewingPerson.signature_name },
                  { label: 'PAN', number: viewingPerson.pan_number, file: viewingPerson.pan_image, name: viewingPerson.pan_image_name },
                  { label: 'Aadhaar', number: viewingPerson.aadhaar_number, file: viewingPerson.aadhaar_image, name: viewingPerson.aadhaar_image_name },
                ].map(doc => (
                  <div key={doc.label} className="border border-gray-100 rounded-lg p-3">
                    <p className="text-xs text-gray-400 mb-0.5">{doc.label}</p>
                    {doc.number && <p className="text-sm font-medium text-gray-700 mb-1.5">{doc.number}</p>}
                    {doc.file ? (
                      <div className="flex items-center gap-3">
                        <a href={doc.file} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                          View
                        </a>
                        <a href={doc.file} download={doc.name || doc.label} className="inline-flex items-center gap-1 text-xs text-green-600 hover:underline">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                          Download
                        </a>
                      </div>
                    ) : (
                      <p className="text-xs text-gray-300 italic">Not uploaded</p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Director / Partner Details</p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <p><span className="text-gray-400">{isLLP ? 'DPIN' : 'DIN'}:</span> <span className="text-gray-700 font-medium">{isLLP ? viewingPerson.dpin || '-' : viewingPerson.din || '-'}</span></p>
                <p>
                  <span className="text-gray-400">DIN Status:</span>{' '}
                  <span className={`font-medium ${(viewingPerson.din_status || 'Active') === 'Active' ? 'text-green-600' : 'text-red-600'}`}>
                    {viewingPerson.din_status || 'Active'}
                  </span>
                </p>
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Shareholding</p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <p><span className="text-gray-400">{isLLP ? 'Contribution' : 'Shares'}:</span> <span className="text-gray-700 font-medium">{viewingPerson.shares_or_contribution || '-'}</span></p>
                {!isLLP && <p><span className="text-gray-400">Holding %:</span> <span className="text-gray-700 font-medium">{viewingPerson.shareholding_percent || '-'}%</span></p>}
              </div>
            </div>

            {viewingPerson.interested_entities && viewingPerson.interested_entities.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Interested Companies / LLPs</p>
                <div className="space-y-1.5">
                  {viewingPerson.interested_entities.map((ent, i) => (
                    <div key={i} className="bg-gray-50 rounded-lg px-3 py-2 text-xs">
                      <span className="font-medium text-gray-700">{ent.cin}</span>
                      <span className="text-gray-400"> · {ent.designation || '-'} · {ent.percentage || '0'}% · ₹{ent.amount || '0'}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {viewingPerson.remarks && (
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Remarks</p>
                <p className="text-sm text-gray-700">{viewingPerson.remarks}</p>
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-2">
            <button
              onClick={() => setViewingPerson(null)}
              className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-gray-600 text-sm font-medium"
            >
              Close
            </button>
            <button
              onClick={() => startEdit(viewingPerson)}
              className="px-4 py-2 rounded-lg text-white text-sm font-medium bg-[#0f1f45]"
            >
              Edit This Person
            </button>
          </div>
        </div>
      </div>
    )}

    {alertMsg && (
      <AlertModal message={alertMsg} onClose={() => setAlertMsg(null)} />
    )}
    </div>
  );
}

function NameApprovalTab({ data, setData, isLLP }) {
  const [saved, setSaved] = useState(false);

  // Auto-fill Approved Name when there's only one proposed name and the
  // approval date has just been entered — removes a manual typing step
  // and the risk of a mismatched/mistyped approved name.
  useEffect(() => {
    if (data.approval_date && !data.proposed_name_2 && !data.approved_name && data.proposed_name_1) {
      setData(d => ({ ...d, approved_name: d.proposed_name_1 }));
    }
  }, [data.approval_date, data.proposed_name_1, data.proposed_name_2]);

  const ic = "w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-blue-500 bg-white";

  const statusColors = {
    'Pending': 'bg-gray-100 text-gray-700',
    'Applied': 'bg-yellow-100 text-yellow-700',
    'Approved': 'bg-green-100 text-green-700',
  };

  // Auto-derived from SRN filing progress — no manual status selection needed
  const srnStatus = !data.srn_filed
    ? 'Pending'
    : !data.approval_date
      ? 'Applied'
      : 'Approved';

  // ── Auto-calculate name validity per MCA rule:
  //     Company (SPICe+ Part A / RUN) — 20 days base, +20 days per
  //     extension, max 2 extensions (60 days total).
  //     LLP (RUN-LLP) — flat 3 months from date of issue, no extensions;
  //     see the RUN-LLP name approval letter's clause 2. ────────────────
  const addDays = (dateStr, days) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    d.setDate(d.getDate() + days);
    return d;
  };
  const addMonths = (dateStr, months) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    d.setMonth(d.getMonth() + months);
    return d;
  };

  const extensions = Number(data.extensions_taken || 0);
  const validTillDate = data.srn_filed
    ? (isLLP ? addMonths(data.approval_date, 3) : addDays(data.approval_date, 20 * (1 + extensions)))
    : null;

  const fmtDate = (d) => d
    ? `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`
    : '-';

  const daysRemaining = validTillDate
    ? Math.ceil((validTillDate.setHours(0,0,0,0) - new Date().setHours(0,0,0,0)) / 86400000)
    : null;

  const urgency = daysRemaining === null ? null
    : daysRemaining <= 0 ? 'expired'
    : daysRemaining <= 7 ? 'critical'
    : daysRemaining <= 15 ? 'warning'
    : 'safe';

  const urgencyStyle = {
    expired:  { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', dot: 'bg-red-500' },
    critical: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', dot: 'bg-red-500' },
    warning:  { bg: 'bg-orange-50', border: 'border-orange-100', text: 'text-orange-700', dot: 'bg-orange-500' },
    safe:     { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', dot: 'bg-green-500' },
  };

  const handleSave = (e) => {
    e.preventDefault();
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="grid grid-cols-3 gap-5">
      <form onSubmit={handleSave} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-visible col-span-2">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h4 className="text-base font-bold text-[#0f1f45]">Name Approval</h4>
            <p className="text-xs text-gray-400 mt-1">
              Capture proposed names, object clause, NIC code and SRN details.
            </p>
          </div>

          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[srnStatus]}`}>
            {srnStatus}
          </span>
        </div>

        <div className="p-5 space-y-5">
          <section>
            <h5 className="text-sm font-semibold text-gray-700 mb-3">Proposed Names</h5>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Proposed Name 1 *</label>
                <input
                  required
                  value={data.proposed_name_1}
                  onChange={e => setData(d => ({ ...d, proposed_name_1: e.target.value }))}
                  placeholder="First proposed name"
                  className={ic}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Proposed Name 2</label>
                <input
                  value={data.proposed_name_2}
                  onChange={e => setData(d => ({ ...d, proposed_name_2: e.target.value }))}
                  placeholder="Second proposed name"
                  className={ic}
                />
              </div>

              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Approved Name {data.approval_date && <span className="text-red-500">*</span>}
                </label>
                <select
                  required={!!data.approval_date}
                  value={data.approved_name}
                  onChange={e => setData(d => ({ ...d, approved_name: e.target.value }))}
                  className={`${ic} ${data.approval_date && !data.approved_name ? 'border-red-300' : ''}`}
                >
                  <option value="">Select the approved name</option>
                  {data.proposed_name_1 && (
                    <option value={data.proposed_name_1}>{data.proposed_name_1}</option>
                  )}
                  {data.proposed_name_2 && (
                    <option value={data.proposed_name_2}>{data.proposed_name_2}</option>
                  )}
                </select>
                {data.approval_date && !data.approved_name && (
                  <p className="text-xs text-red-500 mt-1">
                    Required once an approval date is entered — select the name MCA has approved.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">NIC Code</label>
                <input
                  value={data.nic_code}
                  onChange={e => setData(d => ({ ...d, nic_code: e.target.value }))}
                  placeholder="e.g. 46900"
                  className={ic}
                />
              </div>
            </div>
          </section>

          <section>
            <div className="flex items-center justify-between mb-3">
              <h5 className="text-sm font-semibold text-gray-700">Application Details</h5>

              {/* SRN Filed toggle — gates the rest of this section */}
              <button
                type="button"
                onClick={() => setData(d => ({ ...d, srn_filed: !d.srn_filed }))}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                  data.srn_filed
                    ? 'bg-green-50 border-green-200 text-green-700'
                    : 'bg-gray-50 border-gray-200 text-gray-500'
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${data.srn_filed ? 'bg-green-500' : 'bg-gray-300'}`} />
                {data.srn_filed ? 'SRN Filed' : 'Mark SRN as Filed'}
              </button>
            </div>

            {!data.srn_filed ? (
              <div className="border border-dashed border-gray-200 rounded-lg p-5 text-center text-xs text-gray-400">
                {isLLP ? 'RUN-LLP' : 'SPICe+ Part A / RUN'} not filed yet. Toggle "Mark SRN as Filed" once the application is submitted to MCA to start tracking validity automatically.
              </div>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{isLLP ? 'RUN-LLP SRN *' : 'RUN / SPICe+ Part A SRN *'}</label>
                    <input
                      required
                      value={data.srn}
                      onChange={e => setData(d => ({ ...d, srn: e.target.value.toUpperCase() }))}
                      placeholder="e.g. AC4673909"
                      className={ic}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Application Date</label>
                    <input
                      type="date"
                      value={data.application_date}
                      onChange={e => setData(d => ({ ...d, application_date: e.target.value }))}
                      className={ic}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Approval Date <span className="text-gray-400 font-normal">(fill once MCA approves)</span>
                    </label>
                    <input
                      type="date"
                      value={data.approval_date}
                      onChange={e => setData(d => ({ ...d, approval_date: e.target.value }))}
                      className={ic}
                    />
                  </div>

                  {!isLLP && (
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Extensions Taken</label>
                      <select
                        value={extensions}
                        onChange={e => setData(d => ({ ...d, extensions_taken: Number(e.target.value) }))}
                        className={ic}
                      >
                        <option value={0}>None — base 20 days</option>
                        <option value={1}>1 extension — 40 days total</option>
                        <option value={2}>2 extensions (max) — 60 days total</option>
                      </select>
                    </div>
                  )}

                  <div className={isLLP ? 'col-span-3' : 'col-span-1'}>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Remarks / Resubmission Notes</label>
                    <input
                      value={data.remarks}
                      onChange={e => setData(d => ({ ...d, remarks: e.target.value }))}
                      placeholder="Any MCA remarks, resubmission reason, internal note"
                      className={ic}
                    />
                  </div>
                </div>

                {/* Auto-calculated validity banner */}
                {validTillDate && (
                  <div className={`mt-4 rounded-lg border p-4 flex items-center justify-between ${urgencyStyle[urgency].bg} ${urgencyStyle[urgency].border}`}>
                    <div className="flex items-center gap-3">
                      <span className={`w-2.5 h-2.5 rounded-full ${urgencyStyle[urgency].dot}`} />
                      <div>
                        <p className={`text-sm font-bold ${urgencyStyle[urgency].text}`}>
                          Name valid till {fmtDate(validTillDate)}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {isLLP
                            ? 'Auto-calculated: 3 months from approval date (RUN-LLP validity, no extension mechanism)'
                            : `Auto-calculated: ${20 * (1 + extensions)} days from approval date${extensions > 0 ? ` (base 20 + ${extensions * 20} extension${extensions > 1 ? 's' : ''})` : ''}`}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-lg font-bold ${urgencyStyle[urgency].text}`}>
                        {urgency === 'expired' ? 'Expired' : `${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}`}
                      </p>
                      {urgency !== 'expired' && <p className="text-xs text-gray-400">remaining</p>}
                    </div>
                  </div>
                )}
                {validTillDate && urgency !== 'safe' && (isLLP || extensions < 2) && (
                  <p className="text-xs text-orange-600 mt-2">
                    {isLLP
                      ? (urgency === 'expired'
                          ? 'Name has expired — RUN-LLP reservations cannot be extended; file a fresh RUN-LLP application.'
                          : 'Validity is running low — RUN-LLP reservations cannot be extended, so incorporation (FiLLiP) must be filed before it expires.')
                      : (urgency === 'expired'
                          ? 'Name has expired — file a fresh RUN/SPICe+ Part A application, or file an extension if within the permitted window.'
                          : `Validity is running low. You can file for an extension (up to ${2 - extensions} more available) before it expires.`)}
                  </p>
                )}
              </>
            )}
          </section>
        </div>

        <div className="px-5 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-end gap-3">
          {saved && <span className="text-green-600 text-sm">Saved</span>}

          <button
            type="submit"
            className="px-6 py-2.5 rounded-lg text-white text-sm font-medium bg-[#0f1f45]"
          >
            Save Name Approval
          </button>
        </div>
      </form>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
        <h4 className="text-base font-bold text-[#0f1f45] mb-4">Quick Summary</h4>

        <div className="space-y-3 text-sm">
          <InfoRow label="Name 1" value={data.proposed_name_1 || '-'} />
          <InfoRow label="Name 2" value={data.proposed_name_2 || '-'} />
          <InfoRow label="Approved" value={data.approved_name || '-'} />
          <InfoRow label="Status" value={srnStatus} />
          <InfoRow label="SRN" value={data.srn_filed ? (data.srn || '-') : 'Not filed'} />
          {validTillDate && (
            <>
              <InfoRow label="Valid Till" value={fmtDate(validTillDate)} />
              <InfoRow label="Days Left" value={urgency === 'expired' ? 'Expired' : daysRemaining} />
            </>
          )}
        </div>

        {validTillDate && urgency !== 'safe' && (
          <div className={`mt-4 rounded-lg p-3 text-xs font-medium ${urgencyStyle[urgency].bg} ${urgencyStyle[urgency].text}`}>
            {urgency === 'expired' ? '⚠ Name reservation expired' : `⚠ Expiring in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}`}
          </div>
        )}
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex justify-between gap-4 border-b border-gray-100 pb-2">
      <span className="text-gray-400">{label}</span>
      <span className="font-medium text-gray-700 text-right">{value}</span>
    </div>
  );
}


export default Incorporation;